import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';
import ChatbotView from './ChatbotView'; 
import ChatWidget from './ChatWidget/ChatWidget';
import PropertyView from './PropertyView';  // ← ADD THIS LINE
import CashflowCalculator from './CashflowCalculator';
import TodosView from './TodosView';
import DubaiSellListingsView from './DubaiSellListingsView';
import MainDashboard from './MainDashboard';
import ClientServicesSection from './ClientServicesSection';
import './MainDashboard.css';
import './authLux.css';
import RoleSwitch from '../portals/shared/RoleSwitch';
import { signupRequested, clearSignupRequest } from '../portals/shared/entryIntent';
import ROICalculatorView from './ROICalculatorView';
import { LanguageProvider, useLanguage, LanguageSelector } from './LanguageContext';
import AppWalkthrough, { useWalkthrough } from './AppWalkthrough';
import AdminWrapper from './AdminWrapper';
import InstallmentPlanView from './InstallmentPlanView';
// Sample identities/units for the code-gated showcase (demoMode). Imported
// unconditionally — they are plain data, a couple of KB, and tree-shaking a
// conditional import here would not be reliable.
import { DEMO_INVESTOR, isFilmMode } from '../portals/demo/demoMode';
import { DEMO_PORTAL_UNITS, DEMO_DLD_DATA, DEMO_BENCHMARKS, DEMO_TIMELINES, DEMO_SELL_LISTINGS, DEMO_PLANS, DEMO_DEAL_HISTORY } from '../portals/demo/demoAssetDeskData';
import { DEMO_LEASES, DEMO_PM_UNITS } from '../portals/demo/demoPmData';
// TODO: Enable App Check when reCAPTCHA site key is ready
// import { getAppCheckToken } from '../services/firebaseAppCheck';
import AboutUsView from './AboutUsView';
import PortfolioView, { cachedMarketIntelligence, storedMarketIntelligence, storedFullMarketIntelligence, fetchMarketIntelligence } from './PortfolioView';
import { portfolioDldValue, portfolioRent, useLeases } from './portfolioMetrics';
import CorporateServicesView from './CorporateServicesView';
import PushNotificationHandler from './PushNotificationHandler';
import NewsfeedView from './NewsfeedView';
import SurveyView from './SurveyView';
import PropertyManagementView, { UnitDetail } from '../portals/owner/PropertyManagementView';
import Segmented from '../portals/shared/Segmented';
import InvestMenterBrain from './InvestMenterBrain';
import TrustCenter from './TrustCenter';
import WealthOverview from './WealthOverview';
import { M, T } from '../portals/shared/pmTokens';
import EAAdminConsole from '../portals/ea/EAAdminConsole';
import CustomerMessages from '../portals/shared/CustomerMessages';
import { fetchMessageSummary, pollWhileActive } from '../portals/shared/messaging';
import { isAdminAccount } from '../portals/shared/theme';
import { clearPmCache } from '../portals/shared/pmCache';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { trackLogin, trackPageView, trackEvent } from '../services/firebaseAnalytics';
import totp from 'totp-generator';
import { appLanguage, appLocale, trData, uiError } from '../i18n/locale';
import { de as dateFnsDe } from 'date-fns/locale';
import { 
  checkBiometricAvailability, 
  enrollBiometric, 
  authenticateWithBiometric,
  disableBiometric,
  hasBiometricCredentials,
  isNativeApp,
  refreshBiometricPassword,
  markSignedOut,
  shouldAutoPromptFaceId
} from './biometricAuth';
import {
  Upload, FileText, Home, CreditCard, TrendingUp, Check, Clock, Info, ListChecks,
  AlertCircle, User, X, Star, Menu, DollarSign, Percent, MapPin,
  Square, Eye, EyeOff, Building2, MessageCircle, ChevronRight,
  Phone, Mail, HelpCircle, Settings, LogOut, Bell, Shield,
  Newspaper, Calendar, Video, Briefcase, Search, Mic, Send, Bot,
  PieChart, Calculator, Download, ExternalLink, ArrowLeft,
  BarChart3, PenLine, Lightbulb, RefreshCw, CheckCircle, Lock,
  Smartphone, Flame, Ticket, Activity, Target, ArrowUpRight, ArrowDownRight, Layers, Zap,
  ClipboardList, FlaskConical, MessagesSquare, ShieldCheck, Wallet, Wrench, LayoutDashboard, Brain
} from 'lucide-react';
import { TERMS_URL as LEGAL_TERMS_URL, PRIVACY_URL as LEGAL_PRIVACY_URL, openPolicy } from '../portals/shared/legal';
const dateFnsLocale = () => (appLanguage() === 'de' ? dateFnsDe : undefined);
// DocumentData type removed - using local Document interface

// ============================================
// SMART AREA CONVERSION UTILITIES
// ============================================


const SQM_TO_SQFT_RATE = 10.764;
const SQFT_TO_SQM_RATE = 0.0929;

const isLikelySqm = (area: number): boolean => {
  return area > 0 && area < 300;
};

const formatAreaDisplay = (area: string | number | undefined): { 
  primary: string; 
  secondary: string;
} => {
  const areaNum = typeof area === 'string' ? parseFloat(area) || 0 : (area || 0);
  
  if (areaNum === 0) return { primary: '-', secondary: '' };
  
  if (isLikelySqm(areaNum)) {
    const sqft = Math.round(areaNum * SQM_TO_SQFT_RATE);
    return {
      primary: `${areaNum.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} m²`,
      secondary: `${sqft.toLocaleString('en-AE')} sqft`
    };
  } else {
    const sqm = areaNum * SQFT_TO_SQM_RATE;
    return {
      primary: `${Math.round(areaNum).toLocaleString('en-AE')} sqft`,
      secondary: `${sqm.toFixed(1)} m²`
    };
  }
};

const formatAED = (amount: number): string => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Invite links removed from frontend bundle — fetched from authenticated API at runtime
const COMMUNITY_CHANNELS = [
  { id: 'rym',         name: 'RYM Construction Updates',         developer: 'RYM',       location: 'Seagate'            },
  { id: 'damac',       name: 'DAMAC Construction Updates',       developer: 'DAMAC',     location: undefined            },
  { id: 'samana',      name: 'Samana Construction Updates',      developer: 'Samana',    location: 'Dubai Land'         },
  { id: 'sobha',       name: 'Sobha Hartland Updates',           developer: 'Sobha',     location: 'Sobha Hartland'     },
  { id: 'imtiaz',      name: 'Imtiaz Dubai Islands Updates',     developer: 'Imtiaz',    location: 'Dubai Islands'      },
  { id: 'grandpolo',   name: 'Grand Polo Updates',               developer: 'Grand Polo',location: undefined            },
  { id: 'emaar-creek', name: 'Dubai Creek Harbour Updates',      developer: 'Emaar',     location: 'Dubai Creek Harbour'},
  { id: 'avenew',      name: 'Avenew Dubai Islands Updates',     developer: 'Avenew',    location: 'Dubai Islands'      },
];

const getInvestorCommunities = (units: any[]) => {
  const matchedCommunities: any[] = [];
  const addedIds = new Set<string>();

  units.forEach(unit => {
    COMMUNITY_CHANNELS.forEach(channel => {
      if (addedIds.has(channel.id)) return;

      // Strict match: project name must match channel location exactly
      // OR developer must match exactly
      const projectMatch = unit.project && channel.location && 
        unit.project.toLowerCase() === channel.location.toLowerCase();
      
      const developerMatch = unit.developer && channel.developer && 
        unit.developer.toLowerCase() === channel.developer.toLowerCase();

      if (projectMatch || developerMatch) {
        matchedCommunities.push(channel);
        addedIds.add(channel.id);
      }
    });
  });

  return matchedCommunities;
};

// Add this BEFORE the InvestorPortal component definition

const AppLayout = ({ children, activeTab, setActiveTab, currentInvestor, setIsAuthenticated, setCurrentInvestor, setUnits, setPayments, setDocuments, totalPortfolioValue, vipStatus }: any) => {
  // NEW: State for the mobile sidebar (slides in from the side)
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // NEW: State for the desktop sidebar (collapses in place)
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Mirrors the four-tab shell, then the tools. Portfolio and Property
  // Management are gone as separate entries: both are the Properties screen.
  const navigationItems = [
    { id: 'dashboard', label: t('ipNavHome'), icon: Home },
    { id: 'properties', label: t('ipNavProperties'), icon: Building2 },
    { id: 'money', label: t('ipNavMoney'), icon: CreditCard },
    { id: 'analysis', label: t('analysis'), icon: TrendingUp },
    { id: 'marketplace', label: t('services'), icon: () => (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ) },
    { id: 'roi-calculator', label: t('roiCalculator'), icon: Calculator },
    { id: 'chatbot', label: t('aiAssistant'), icon: () => (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ) },
    // { id: 'events', label: 'Events', icon: AlertCircle },  // HIDDEN
    // { id: 'webinar', label: 'Webinar', icon: () => (      // HIDDEN
    //   <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    //   </svg>
    // ) },
   // { id: 'news', label: 'News', icon: FileText },  // HIDDEN
    { id: 'IM Properties', label: t('imProperties'), icon: Home },
     { id: 'cashflow', label: t('cashflow'), icon: Calculator },
    { id: 'corporate-services', label: t('corporateServices'), icon: Building2 },
    { id: 'partner', label: t('ipPartner'), icon: User }
  ];

  const handleLogout = () => {
    // The owner's Property Management portfolio is cached on the device so the
    // screen paints before the backend has woken up. It carries tenant names,
    // rents and Emirates IDs, so it leaves with the session.
    clearPmCache();
    markSignedOut();
    localStorage.removeItem('authToken');
    localStorage.removeItem('investorId');
    localStorage.removeItem('investorEmail');
    localStorage.removeItem('investor');
    localStorage.removeItem('lastActivity');
    setIsAuthenticated(false);
    setCurrentInvestor(null);
    setUnits([]);
    setPayments([]);
    setDocuments([]);
    setSidebarOpen(false);
  };

  const handleEditProfile = () => {
    setActiveTab('profile');
    setSidebarOpen(false);
  };

  // UPDATED: Added a class to the root layout div
  return (
    <div className={`app-layout ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile Menu Toggle */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Sidebar Overlay (Mobile) */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      {/* UPDATED: Added `collapsed` class when needed */}
      <aside className={`sidebar ${sidebarOpen ? 'mobile-open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* UPDATED: Sidebar Header with Logo and new Toggle Button */}
        <div className="sidebar-header">
          <a href="#" className={`sidebar-logo ${isCollapsed ? 'hidden' : ''}`} onClick={(e) => { e.preventDefault(); setActiveTab('dashboard'); setSidebarOpen(false); }}>
            <img
              src="/asset-desk-wordmark.png"
              alt="Asset Desk"
              onError={(e) => {
                // ... (your existing error handler)
              }}
            />
          </a>
          {/* NEW: Desktop sidebar toggle button */}
          <button className="desktop-sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Sidebar Navigation */}
        <nav className="sidebar-nav">
          {navigationItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
              >
                <Icon />
                {/* UPDATED: Hide the label span when collapsed */}
                <span className={`${isCollapsed ? 'hidden' : ''}`}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-user-section">
            <div className="sidebar-user">
              <div className="sidebar-user-avatar">
                {currentInvestor?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              {/* UPDATED: Hide user info when collapsed */}
              <div className={`sidebar-user-info ${isCollapsed ? 'hidden' : ''}`}>
                <p className="sidebar-user-name">{currentInvestor?.name || t('ipUser')}</p>
               {/* VIP status hidden from UI - used internally only */}
              </div>
            </div>

            <button onClick={handleEditProfile} className="sidebar-action-btn edit-profile">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {/* UPDATED: Hide label span when collapsed */}
              <span className={`${isCollapsed ? 'hidden' : ''}`}>{t('editProfile')}</span>
            </button>

            <button onClick={handleLogout} className="sidebar-action-btn logout">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {/* UPDATED: Hide label span when collapsed */}
              <span className={`${isCollapsed ? 'hidden' : ''}`}>{t('logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      {/* UPDATED: Added `collapsed` class when needed */}
      <main className={`main-content ${isCollapsed ? 'collapsed' : ''}`}>
        <header className="main-header">
          <div className="main-header-left">
            <h2>{t('welcomeBack')}, {currentInvestor?.name?.split(' ')[0] || t('investor')}!</h2>
            <p>{t('heresYourPortfolio')}</p>
          </div>
          <div className="main-header-right">
            <div className="portfolio-badge">
              <p className="portfolio-badge-label">{t('portfolioValue')}</p>
              <p className="portfolio-badge-value">AED {(totalPortfolioValue || 0).toLocaleString('en-AE')}</p>
            </div>
          </div>
        </header>
        <div className="main-body">
          {children}
        </div>
      </main>
    </div>
  );
};

// ============================================================
/** One figure in the Account Statistics grid. */
const StatCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ background: '#fff', padding: '14px 16px' }}>
    <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: T.statValue, fontWeight: 800, color: M.black, marginTop: 4, letterSpacing: '-0.3px' }}>{value}</div>
  </div>
);

/** A named person on the account — advisor or support. */
const ContactCard: React.FC<{ icon: React.ComponentType<any>; label: string; name: string }> = ({ icon: Icon, label, name }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 16, border: `1px solid ${M.hair}`, borderRadius: 12, background: '#fff' }}>
    <span style={{ width: 40, height: 40, borderRadius: 99, flexShrink: 0, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={19} color={M.goldInk} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: T.body, fontWeight: 800, color: M.black, marginTop: 2 }}>{name}</div>
    </div>
  </div>
);

// ============================================================
// PROPERTIES HUB — the one place "my properties" lives
// ============================================================
/**
 * Replaces three bottom-bar tabs with one.
 *
 * Property Mgmt, Properties and Portfolio were separate destinations over the
 * same `units` collection: the PM endpoint (/pm/owner/:id/units) returns a
 * richer projection of the very same documents the investor payload carries,
 * enriched with tenancy, documents, media and complaints. An owner had no way
 * to know which of the three to tap, so all three had to be tried.
 *
 * Now there is one list, and the two portfolio-WIDE reports are opened from
 * above it instead of competing with it. The valuation reports are per unit
 * either way — PortfolioView's Valuation tab lists units and drills into
 * UnitValuationReport — so nothing moved further away than one tap.
 *
 * `screen` is the shell's own activeTab, not private state: 'portfolio' and
 * 'valuation' stay addressable so the dashboard CTAs, the walkthrough and
 * ?tab= links keep working unchanged.
 */
const PropertiesHub: React.FC<{
  screen: string;
  setScreen: (tab: string) => void;
  currentInvestor: any;
  demoMode?: boolean;
  onAsk?: (message: string) => void;
  /**
   * The investor payload's units, from API_URL. PropertyManagementView fetches
   * its own richer copy from PORTAL_API and prefers that, but falls back to
   * these — which matters in development, where PORTAL_API is the local dev
   * API on :4002 and API_URL is the live backend.
   */
  units: any[];
  /** InvestorPortal.handleSmartUpload, for a property's Documents tab. */
  onSmartUpload?: (e: React.ChangeEvent<HTMLInputElement>, unitId?: string) => void;
  smartUploading?: boolean;
  /** <PortfolioView .../> built by the shell, which owns the demo overrides. */
  renderPortfolio: (initialTab: 'report' | 'valuation') => React.ReactNode;
  /** Where the back link on a report goes. */
  backTo?: string;
}> = ({ screen, setScreen, currentInvestor, demoMode, onAsk, units, onSmartUpload, smartUploading, renderPortfolio, backTo }) => {
  const { t } = useLanguage();

  if (screen === 'portfolio' || screen === 'valuation') {
    return (
      <div className="pt-fade">
        <button
          onClick={() => setScreen(backTo || 'properties')}
          className="pt-tap"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', padding: '14px 16px 4px',
            color: M.goldInk, fontSize: T.secondary, fontWeight: 700, cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} /> {t('ipHubBackToList')}
        </button>
        {renderPortfolio(screen === 'valuation' ? 'valuation' : 'report')}
      </div>
    );
  }

  return (
    <PropertyManagementView
      section="properties"
      currentInvestor={currentInvestor}
      units={units}
      demoMode={demoMode}
      onAsk={onAsk}
      onSmartUpload={onSmartUpload}
      smartUploading={smartUploading}
      headerExtra={
        <div style={{ display: 'flex', padding: '0 16px 4px' }}>
          <div style={{ display: 'flex', flex: 1, border: `1px solid ${M.hair}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
            <HubLink icon={FileText} label={t('ipHubReport')} onClick={() => setScreen('portfolio')} />
            <span style={{ width: 1, background: M.hair, flexShrink: 0 }} />
            <HubLink icon={BarChart3} label={t('ipHubValuations')} onClick={() => setScreen('valuation')} />
          </div>
        </div>
      }
    />
  );
};

/**
 * The Properties screen opens on the portfolio, not on its utilities.
 *
 * It used to open on chrome: a report strip in its own card, a search field,
 * and two large filled blocks — Submit Documents in navy, AI Smart Upload in
 * gold — before a single property appeared. On a phone the first card began
 * below the fold, and the two loudest elements on the screen were the rarest
 * actions on it. An owner opening this screen wants the same thing a private
 * bank leads its statement with: the number.
 *
 * The treatment is the one already used for Rental Income on a unit — a gold
 * wash over pure black (M.blackGrad, which is alpha gold, so no second gold
 * enters the palette). Reused rather than invented, so the two screens read as
 * one product.
 */
// One bottom sheet for the hero's two breakdowns: each row is a unit, its
// figure and where that figure comes from, so every number can be traced.
const HeroBreakdownSheet: React.FC<{
  title: string; total: number; suffix?: string; note: string;
  rows: { id: string; name: string; amount: number; source: string; estimated?: boolean }[];
  summary?: { label: string; amount: number }[];
  onClose: () => void;
}> = ({ title, total, suffix, note, rows, summary, onClose }) => {
  const aed = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', color: '#000', width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '22px 20px 28px', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: M.gold }}>{title}</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 2 }}>{aed(total)}{suffix && <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.55)' }}> {suffix}</span>}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', fontSize: 22, color: 'rgba(0,0,0,0.5)', padding: 4 }}>×</button>
        </div>
        <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(214,177,96,0.10)', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5, color: 'rgba(0,0,0,0.75)' }}>{note}</div>
        {summary && summary.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {summary.map((x) => (
              <div key={x.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13.5, fontWeight: 700 }}>
                <span>{x.label}</span><span>{aed(x.amount)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          {rows.map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 13 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{r.name}</div>
                <div style={{ fontSize: 12, color: r.estimated ? '#9a6700' : 'rgba(0,0,0,0.55)', fontWeight: r.estimated ? 600 : 500 }}>{r.source}</div>
              </div>
              <div style={{ fontWeight: 800, flexShrink: 0 }}>{aed(r.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PortfolioHero: React.FC<{
  units: any[];
  totalValue: number;
  annualRental: number;
  /** The units the rental figure is summed over (they carry monthlyRental). */
  rentUnits?: any[];
  /** The DLD valuation payload — when present the hero uses the same value
   *  and rent rules as Home (portfolioDldValue / portfolioRent). */
  dld?: any;
  investorId?: string;
}> = ({ units: allUnits, totalValue: recordValue, annualRental: _legacyRent, rentUnits = [], dld, investorId }) => {
  // Signed leases from Property Management — where the rent actually lives.
  const leases = useLeases(investorId);
  const { t, language } = useLanguage();
  const de = language === 'de';
  // Held units only, matching the value (which already leaves sold units out)
  // and the Property Management dashboard ("19 +1 sold", not 20).
  const units = allUnits.filter((u: any) => (u.status || '').toLowerCase() !== 'sold');
  const [sheet, setSheet] = useState<null | 'value' | 'rent'>(null);
  const unitName = (u: any) => `${u.name || u.project || ''}${u.unitNumber && !String(u.name || '').includes(u.unitNumber) ? ` · ${u.unitNumber}` : ''}`;
  const aedNum = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  // The portfolio value: the same rule and the same DLD data as Home, so the
  // two screens can never disagree. Rent: only real rent (signed lease or rent
  // on record); never 5% of the value. Both include the units the rent is kept
  // on, merged by id with the unit list.
  const merged = React.useMemo(() => {
    const extra = new Map<string, any>(rentUnits.map((u: any) => [String(u.id), u]));
    return allUnits.map((u: any) => ({ ...u, ...(extra.get(String(u.id)) || {}) }));
  }, [allUnits, rentUnits]);
  const valueCalc = React.useMemo(() => portfolioDldValue(dld, merged), [dld, merged]);
  const rentCalc = React.useMemo(() => portfolioRent(dld, merged, leases), [dld, merged, leases]);
  const nDld = valueCalc.rows.filter((r) => r.source === 'dld').length;
  const nPurchase = valueCalc.rows.length - nDld;
  const hasDld = !!(dld && Array.isArray(dld.units) && dld.units.length);
  const totalValue = hasDld ? valueCalc.value : recordValue;
  const annualRental = rentCalc.actual;
  const confLabel = (c: string | null) => {
    const v = String(c || '').toUpperCase();
    if (v === 'HIGH') return de ? 'hohe Sicherheit' : 'high confidence';
    if (v === 'MEDIUM') return de ? 'mittlere Sicherheit' : 'medium confidence';
    if (v === 'LOW') return de ? 'geringe Sicherheit' : 'low confidence';
    return '';
  };
  const valueRows = hasDld
    ? valueCalc.rows.map((r) => ({
        id: String(r.unit.id), name: unitName(r.unit), amount: r.value, estimated: r.source === 'purchase',
        source: r.source === 'dld'
          ? `${de ? 'DLD-Bewertung' : 'DLD valuation'}${confLabel(r.confidence) ? ` · ${confLabel(r.confidence)}` : ''}`
          : (de ? 'Kaufpreis — keine DLD-Bewertung verfügbar' : 'Purchase price — no DLD valuation available'),
      })).sort((a, b) => b.amount - a.amount)
    : units.map((u: any) => ({ id: String(u.id), name: unitName(u), amount: parseFloat(u.currentValue) || 0, source: de ? 'Aktueller Wert der Einheit' : 'Current value on the unit' }))
        .sort((a, b) => b.amount - a.amount);
  const rentRows = rentCalc.lines
    .map((l) => ({
      id: String(l.unit.id), name: unitName(l.unit),
      amount: l.rent,
      estimated: l.rent === 0,
      source: l.rentSource === 'lease' ? (de ? 'Unterzeichneter Mietvertrag' : 'Signed lease')
        : l.rentSource === 'record' ? (de ? 'Miete laut Einheit' : 'Rent on the unit record')
        : l.marketRent > 0
          ? (de ? `Leer · Marktmiete laut DLD ca. ${aedNum(l.marketRent)}/Jahr (nicht mitgezählt)` : `Vacant · DLD market rent about ${aedNum(l.marketRent)}/yr (not counted)`)
          : (de ? 'Leer · keine Miete' : 'Vacant · no rent'),
    }))
    .sort((a, b) => b.amount - a.amount);
  const tap: React.CSSProperties = { all: 'unset', cursor: 'pointer', display: 'block' };
  const more: React.CSSProperties = { fontSize: 11, color: M.gold, fontWeight: 700, marginTop: 4 };
  const rented = units.filter((u: any) => (u.status || '').toLowerCase() === 'rented').length;
  // The same fallback the analysis screen uses for gross yield, so the two
  // screens never disagree about the same portfolio.
  // Yield of the units actually let, on their own value — not the whole
  // portfolio (empty units would dilute it) and never on assumed rent.
  const grossYield = rentCalc.letYield;

  return (
    <div
      style={{
        borderRadius: 18, padding: '24px 22px 20px', marginBottom: 4,
        backgroundColor: '#000', backgroundImage: M.blackGrad, color: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        {/* What the portfolio is worth. */}
        <div style={{ minWidth: 200 }}>
          <button type="button" style={tap} onClick={() => setSheet('value')}>
          <div style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.62)', fontWeight: 600 }}>
            {t('pmPortfolioValue')}
          </div>
          <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.05, marginTop: 4 }}>
            <span style={{ fontSize: 22, color: M.gold, fontWeight: 800, marginRight: 7 }}>AED</span>
            {Math.round(totalValue || 0).toLocaleString('en-AE')}
          </div>
          {/* What the figure is made of, in one line — owners asked "why 54M". */}
          {hasDld && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.66)', marginTop: 4, lineHeight: 1.4 }}>
              {de
                ? `${nDld} Einheiten zur DLD-Bewertung${nPurchase ? ` + ${nPurchase} zum Kaufpreis (keine DLD-Bewertung)` : ''}`
                : `${nDld} unit${nDld === 1 ? '' : 's'} at DLD valuation${nPurchase ? ` + ${nPurchase} at purchase price (no DLD valuation)` : ''}`}
            </div>
          )}
          <div style={more}>{de ? 'Aufschlüsselung ›' : 'Breakdown ›'}</div>
          </button>

          <div style={{ display: 'flex', gap: 22, marginTop: 16, flexWrap: 'wrap' }}>
            <HeroFact value={String(units.length)} label={t('properties')} />
            <HeroFact value={`${rented}/${units.length}`} label={t('pmOccupancy')} />
          </div>
        </div>

        {/* What it pays. The second number an owner asks for, so it sits a size
            below the value rather than beside it as an equal. */}
        <button type="button" style={{ ...tap, textAlign: 'right', marginLeft: 'auto' }} onClick={() => setSheet('rent')}>
          <div style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.62)', fontWeight: 600 }}>
            {t('ipTotalRentalIncome')}
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.1, marginTop: 4 }}>
            <span style={{ fontSize: 15, color: M.gold, fontWeight: 800, marginRight: 6 }}>AED</span>
            {Math.round(annualRental || 0).toLocaleString('en-AE')}
            <span style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.62)', fontWeight: 600, marginLeft: 3 }}>
              {t('ipPerYear')}
            </span>
          </div>
          {grossYield > 0 && (
            <div style={{ fontSize: T.micro, color: M.gold, fontWeight: 700, marginTop: 6 }}>
              {grossYield.toFixed(1)}% {t('ipGrossYield')} {de ? `auf ${rentCalc.letCount} vermietete` : `on ${rentCalc.letCount} let`}
            </div>
          )}
          {rentCalc.potential > 0 && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', marginTop: 4 }}>
              {de ? `+ ${aedNum(rentCalc.potential)}/Jahr möglich, wenn leere vermietet` : `+ ${aedNum(rentCalc.potential)}/yr possible if empty units are let`}
            </div>
          )}
          <div style={more}>{de ? 'So berechnet ›' : 'How it is calculated ›'}</div>
        </button>
      </div>

      {sheet === 'value' && (
        <HeroBreakdownSheet
          title={de ? 'Portfoliowert · Aufschlüsselung' : 'Portfolio value · breakdown'}
          total={totalValue}
          note={hasDld
            ? (de
              ? 'Jede gehaltene Einheit zu ihrer DLD-Bewertung (registrierte Verkäufe im selben Gebäude); ohne DLD-Bewertung zum Kaufpreis. Dieselbe Zahl wie auf der Startseite. Verkaufte Einheiten zählen nicht.'
              : 'Each unit you hold at its DLD valuation (registered sales in its own building); at its purchase price where DLD has no valuation. The same figure as on Home. Sold units are not counted.')
            : (de
              ? `Summe des aktuellen Werts jeder gehaltenen Einheit (${units.length}). Die DLD-Bewertung wird geladen.`
              : `The sum of the current value of each unit you hold (${units.length}). The DLD valuation is loading.`)}
          rows={valueRows}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'rent' && (
        <HeroBreakdownSheet
          title={de ? 'Mieteinnahmen · Berechnung' : 'Rental income · calculation'}
          total={annualRental}
          suffix={de ? 'pro Jahr' : 'per year'}
          note={de
            ? `Nur tatsächliche Miete: unterzeichnete Mietverträge oder die bei der Einheit erfasste Miete (${rentCalc.letCount} Einheiten). Leere Einheiten bringen nichts ein; was sie zur DLD-Marktmiete (registrierte Mietverträge) einbringen könnten, steht separat und wird nicht mitgezählt. Bruttorendite = Miete ÷ DLD-Wert der vermieteten Einheiten.`
            : `Actual rent only: signed leases or the rent recorded on the unit (${rentCalc.letCount} units). Empty units earn nothing; what they could earn at DLD market rent (registered rent contracts) is shown separately and not counted. Gross yield = rent ÷ DLD value of the let units.`}
          summary={[
            { label: de ? 'Tatsächliche Miete pro Jahr' : 'Actual rent per year', amount: annualRental },
            ...(rentCalc.potential > 0 ? [{ label: de ? 'Möglich, wenn leere vermietet (DLD-Marktmiete)' : 'Possible if empty units are let (DLD market rent)', amount: rentCalc.potential }] : []),
          ]}
          rows={rentRows}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
};

const HeroFact: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <span style={{ fontSize: 19, fontWeight: 800 }}>{value}</span>
    <span style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.62)', marginLeft: 6, fontWeight: 600 }}>{label}</span>
  </div>
);

const HubLink: React.FC<{ icon: React.ComponentType<any>; label: string; onClick: () => void }> = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="pt-tap"
    style={{
      flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      background: 'transparent', border: 'none', padding: '13px 10px', color: M.black,
      fontSize: T.secondary, fontWeight: 700, cursor: 'pointer',
    }}
  >
    <Icon size={16} color={M.goldInk} style={{ flexShrink: 0 }} />
    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </button>
);

// ============================================================
// PORTFOLIO — the portfolio seen four ways
// ============================================================
/**
 * Report, Management, Valuation and Plans behind one tab.
 *
 * This replaces the Money tab (Rent / Costs / Plans). Costs left because they
 * are recorded per unit and summed on the management dashboard, so a
 * portfolio-wide Costs tab was a third place to read the same figure. Rent
 * left for the same reason — the dashboard states rent collected against rent
 * due across every unit, which is the question that tab existed to answer.
 *
 * `screen` is the shell's activeTab, so 'portfolio', 'valuation' and
 * 'payments' stay addressable: the dashboard CTAs, the walkthrough and ?tab=
 * links keep landing where they always did, and the Properties hero opens this
 * tab straight onto the report or the valuations.
 */
const PortfolioTabs: React.FC<{
  screen: string;
  setScreen: (tab: string) => void;
  currentInvestor: any;
  demoMode?: boolean;
  onAsk?: (message: string) => void;
  units: any[];
  /** <PortfolioView .../> built by the shell, which owns the demo overrides. */
  renderPortfolio: (initialTab: 'report' | 'valuation') => React.ReactNode;
  /** PaymentsView, which is defined inside the shell and closes over its state. */
  plansNode: React.ReactNode;
  /** The InvestMenter Brain, built by the shell (it owns API_URL + demo data). */
  brainNode: React.ReactNode;
  /** Licences, visas and transactions from the CRM (ClientServicesSection). */
  corporateNode: React.ReactNode;
}> = ({ screen, setScreen, currentInvestor, demoMode, onAsk, units, renderPortfolio, plansNode, brainNode, corporateNode }) => (
  <div className="pt-fade pm-premium">
    <Segmented
      value={screen}
      onChange={setScreen}
      tabs={[
        { key: 'portfolio', label: 'ipTabReport', icon: FileText },
        { key: 'pm-dashboard', label: 'ipTabManagement', icon: ClipboardList },
        // Corporate took Maintenance's seat: the licences, visas and
        // transactions a client holds with Corporate Services and Next Level
        // Consulting (from the CRM). Maintenance is still in every property
        // (its own sub-tab, and an open job flags the property card), and
        // 'pm-maintenance' still renders below, so links to the board keep working.
        { key: 'corporate', label: 'ipTabCorporate', icon: Briefcase },
        { key: 'valuation', label: 'ipTabValuation', icon: BarChart3 },
        { key: 'payments', label: 'ipMoneyPlans', icon: CreditCard },
        // Sixth seat: fills the two-row grid, and is the one tab that is an
        // argument rather than a ledger — DLD vs our own deal history vs the AI.
        { key: 'brain', label: 'ipTabBrain', icon: Brain },
      ]}
    />
    {screen === 'portfolio' && renderPortfolio('report')}
    {screen === 'pm-dashboard' && (
      <PropertyManagementView
        section="dashboard"
        currentInvestor={currentInvestor}
        units={units}
        demoMode={demoMode}
        onAsk={onAsk}
      />
    )}
    {screen === 'pm-maintenance' && (
      <PropertyManagementView
        section="maintenance"
        currentInvestor={currentInvestor}
        units={units}
        demoMode={demoMode}
        onAsk={onAsk}
      />
    )}
    {screen === 'corporate' && corporateNode}
    {screen === 'valuation' && renderPortfolio('valuation')}
    {screen === 'payments' && plansNode}
    {screen === 'brain' && brainNode}
  </div>
);


// BOTTOM NAVIGATION COMPONENT - DEWA Style
// ============================================================
// The admin rail. `also` is empty on each: these ARE the destinations, not
// landing screens with sub-routes behind them.
const ADMIN_NAV = [
  { id: 'admin-assetdesk', label: 'eaTabAssetDesk', icon: LayoutDashboard, also: ['dashboard'] as string[] },
  { id: 'admin-pm', label: 'eaTabPm', icon: Building2, also: [] as string[] },
  { id: 'admin-chat', label: 'eaTabChat', icon: MessagesSquare, also: [] as string[] },
  { id: 'admin-issues', label: 'eaTabIssues', icon: Wrench, also: [] as string[] },
  { id: 'admin-access', label: 'eaTabAccess', icon: ShieldCheck, also: [] as string[] },
  { id: 'menu', label: 'ipNavMore', icon: Menu, also: ['profile', 'about', 'company-docs'] as string[] },
];

// Which console screen a rail entry opens.
export const ADMIN_TAB_VIEW: Record<string, 'assetdesk' | 'pm' | 'chat' | 'issues' | 'access'> = {
  'admin-assetdesk': 'assetdesk',
  'admin-pm': 'pm',
  'admin-chat': 'chat',
  'admin-issues': 'issues',
  'admin-access': 'access',
  // An admin lands on 'dashboard' before touching anything.
  dashboard: 'assetdesk',
};

const BottomNavigation = ({ activeTab, setActiveTab, unreadMessages = 0, adminMode = false }: { activeTab: string; setActiveTab: (tab: string) => void; unreadMessages?: number; adminMode?: boolean }) => {
  const { t } = useLanguage();
  // FOUR destinations, each a different KIND of thing: the overview, the
  // properties, the money, everything else.
  //
  // The bar used to carry five, and three of them — Property Mgmt, Properties
  // and Portfolio — all answered the same question, "show me my properties".
  // They could only be told apart by opening them, and each then opened onto
  // four or five sub-tabs of its own. That stacking is what made the app read
  // as a wall of options rather than a portfolio.
  //
  // `also` lists the routes that belong to a tab without being its landing
  // screen, so the bar still shows where you are. They stay real route ids
  // rather than being rewritten on navigation, because the walkthrough
  // (AppWalkthrough targetTab), the dashboard CTAs and ?tab= deep links all
  // address them by name.
  const navItems = [
    { id: 'dashboard', label: t('ipNavHome'), icon: Home, also: [] as string[] },
    // Each route belongs to ONE item. 'portfolio', 'valuation' and
    // 'property-management' are tabs of the Portfolio screen (PortfolioTabs);
    // listed here too, they lit up Properties AND Portfolio at once.
    { id: 'properties', label: t('ipNavProperties'), icon: Building2, also: [] as string[] },
    // Feed is back in the bar. Five fit — the original bar carried five — and
    // it is the one destination here that is neither a property nor a number,
    // so it does not blur into its neighbours the way the old trio did. It
    // takes the middle seat: the thumb's easiest reach, and the one tab worth
    // opening when nothing in particular needs doing.
    { id: 'newsfeed', label: t('ipFeed'), icon: Newspaper, also: ['news'] },
    { id: 'portfolio', label: t('ipNavPortfolio'), icon: PieChart, also: ['pm-dashboard', 'pm-maintenance', 'corporate', 'valuation', 'payments', 'brain', 'property-management'] },
    // Menu now has a seat. It used to be reachable only from the name in the
    // header — an unmarked target for the ten things that live behind it.
    { id: 'menu', label: t('ipNavMore'), icon: Menu, also: ['profile', 'marketplace', 'IM Properties', 'analysis', 'todos', 'roi-calculator', 'cashflow', 'corporate-services', 'support', 'chatbot', 'partner', 'about', 'company-docs', 'messages', 'trust-center', 'wealth'] },
  ];

  // A management account has no portfolio of its own to browse, so three of
  // the five owner tabs open onto empty screens: Properties lists nothing,
  // the Feed is written for investors, Portfolio totals a portfolio that is
  // not theirs.
  //
  // So in admin mode the rail carries the CONSOLE'S OWN destinations instead.
  // They used to be a second row of pills inside the console, under a rail
  // that held one item called "Console" — two navigations stacked for one set
  // of places. This is one: the rail is the console's navigation, and the
  // console renders whichever of them is selected (see ConsoleView).
  //
  // Not a separate nav component: the same bar, different items. "My view"
  // puts the owner set back (adminViewingOwn), because a manager can also be
  // an investor here and then those screens are genuinely theirs.
  const items = adminMode ? ADMIN_NAV.map((n) => ({ ...n, label: t(n.label) })) : navItems;

  return (
    <nav className="bottom-nav" id="bottom-navigation"> 
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id || item.also.includes(activeTab);
        
        return (
          <button
            key={item.id}
            id={`nav-${item.id}`}
            data-walkthrough={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <div className="bottom-nav-icon">
              <Icon size={22} />
              {/* A reply from the management team is behind More; without a
                  mark on the bar it is two taps away and invisible. */}
              {(item.id === 'menu' || item.id === 'admin-chat') && unreadMessages > 0 && (
                <span className="pm-msg-dot" style={{ position: 'absolute', top: -6, right: -10 }}>
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </div>
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};
// ============================================================
// MOBILE HEADER COMPONENT - DEWA Style
// ============================================================
const MobileHeader = ({ title, onProfileClick }: { title: string; onProfileClick?: () => void }) => {
  return (
    <header className="mobile-header">
      <h1 className="mobile-header-title">{title}</h1>
      <div className="mobile-header-actions">
        <button className="mobile-header-btn" onClick={() => {}}>
          <Search size={22} />
        </button>
        <button className="mobile-header-btn profile-btn" onClick={onProfileClick}>
          <User size={22} />
        </button>
      </div>
    </header>
  );
};

// TypeScript interfaces
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'outline';
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  children: React.ReactNode;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  children: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
}

interface SelectItemProps {
  children: React.ReactNode;
  value: string;
}

interface Service {
  id: string;
  name: string;
  description: string;
  price?: number; // This '?' makes the property optional
  category: 'visa' | 'legal' | 'sales' | 'other';
  deliveryTime?: string; // This '?' makes the property optional
  features?: string[]; // This '?' makes the property optional
  popular?: boolean;
}
// Simple components
const Card: React.FC<CardProps> = ({ children, className = '', ...props }) => (
  <div className={`border p-4 rounded ${className}`} {...props}>{children}</div>
);

const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = '', ...props }) => (
  <div className={`mb-4 ${className}`} {...props}>{children}</div>
);

const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, className = '', ...props }) => (
  <h2 className={`text-xl font-bold ${className}`} {...props}>{children}</h2>
);

const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, className = '', ...props }) => (
  <p className={`text-gray-600 ${className}`} {...props}>{children}</p>
);

const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = '', ...props }) => (
  <div className={className} {...props}>{children}</div>
);

const Button: React.FC<ButtonProps> = ({ 
  children, 
  onClick, 
  className = '', 
  disabled = false, 
  variant = 'default',
  ...props 
}) => {
  const baseClasses = "px-4 py-2 rounded transition-colors";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
  };
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// FIXED: Removed auto-select behavior that caused cursor issues
const Input: React.FC<InputProps> = ({ className = '', ...props }) => (
  <input {...props} className={`border p-2 rounded w-full ${className}`} />
);

const Label: React.FC<LabelProps> = ({ children, className = '', ...props }) => (
  <label {...props} className={`block text-sm font-medium mb-1 ${className}`}>{children}</label>
);

const Select: React.FC<SelectProps> = ({ children, value, onValueChange, className = '', ...props }) => (
  <select 
    value={value} 
    onChange={(e) => onValueChange && onValueChange(e.target.value)}
    className={`border p-2 rounded w-full ${className}`}
    {...props}
  >
    {children}
  </select>
);

const SelectItem: React.FC<SelectItemProps> = ({ children, value }) => (
  <option value={value}>{children}</option>
);

// API URLs
const API_URL = (process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app') + '/api';

// Legal policy — bump POLICY_VERSION whenever the published doc changes so old
// consent records can be distinguished from new ones (GDPR Art. 7(1) audit trail).
const POLICY_VERSION = '1.0';
// Terms / Privacy open in the in-app panel (portals/shared/LegalModal.tsx).
const { TERMS_URL, PRIVACY_URL } = { TERMS_URL: LEGAL_TERMS_URL, PRIVACY_URL: LEGAL_PRIVACY_URL };

const makeAPIRequest = async (endpoint: string, data: Record<string, any> = {}): Promise<any> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    // Include auth token on every request
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_URL}/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    // If investor session is invalid/expired, clear stored auth so login screen shows
    // Only logout for investor-specific session errors, NOT admin auth or IP blocks
    if (response.status === 401 || response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      const isInvestorSessionExpired = errorData.error === 'Authentication required'
        || errorData.error === 'Invalid or expired session';
      if (isInvestorSessionExpired) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('investor');
        localStorage.removeItem('investorId');
        localStorage.removeItem('investorEmail');
        localStorage.removeItem('lastActivity');
        window.location.reload();
        return {};
      }
      // For admin auth errors, IP blocks, or other 403s — don't logout, just throw
      throw new Error(errorData.error || 'Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};
  
const handleLocalFallback = (endpoint: string, data: Record<string, any>): any => {
  
  switch (endpoint) {
    case 'createInvestor':
      return { id: data.data.email, ...data.data }; 
    case 'findInvestorByEmail':
      if (data.email === 'investor1@test.com') {
        return {
          id: 'test-investor-1',
          name: 'Test Investor',
          email: 'investor1@test.com',
          phone: '+1234567890',
          nationality: 'UAE',
          birthDate: '1990-01-01',
          password: 'test123'
        };
      }
      return null;
    case 'createDocument':
    case 'createDocumentWithCategory':
    case 'createUnit':
    case 'createUnitWithForceFolder':
    case 'getInvestorData':
      return { id: Date.now().toString(), units: [], documents: [], payments: [] };
    // ADD THIS NEW CASE
    case 'createAppointment':
      return { success: true, message: 'Appointment booked (offline mode)' };
    case 'requestMeetingLink':
      return { success: true, message: 'Request sent (offline mode)' };
    default:
      throw new Error(`Unknown endpoint: ${endpoint}`);
  }
};

// Data type interfaces
interface Investor {
  id: string;
  name: string;
  email: string;
  phone: string;
  nationality?: string;
  birthDate?: string;
  password?: string;
  createdAt?: string;
  leadOwner?: string;
  executiveAssistant?: string;
  closeLeadName?: string;
  crmSyncedAt?: string;
  twoFactorEnabled?: boolean;
  closeLeadId?: string;
  vipStatus?: string;
  /**
   * Sample account handed to prospects so they can see the app with demo
   * units before they own anything. Set by /api/login from the investor doc.
   * Server-side the account is read-only (uploads, chat handoff and push
   * registration are refused); here it drives the sample-data banner so a
   * visitor never mistakes the demo figures for a real portfolio.
   */
  isDemo?: boolean;
}

interface Unit {
  id: string;
  investorId: string;
  name: string;
  unitNumber: string;
  project: string;
  type: string;
  area: string;
  currentValue: number;
  purchaseValue: number;
  monthlyRental: number;
  occupancyStatus: 'Occupied' | 'Vacant' | 'Offplan';
  location: string;
  
  // Property details
  bedrooms?: number;
  floor?: number;
  
  // Optional OTP-specific fields
  purchaser?: string;
  estimatedCompletionDate?: string;  // Make sure this exists
  propertyStatus?: string;
  isOffplan?: boolean;
  appreciationAmount?: number;
  appreciationPercentage?: number;
  googleDriveFolderId?: string;
  linkedDocuments?: string[];  // ADD THIS
  dataSource?: string;  // ADD THIS
  completionDateSource?: string;  // ADD THIS

 rentalLastUpdated?: string;
  rentalDataSource?: string;
  rentalSampleSize?: number;

  // Construction progress from Google Sheet
  constructionCompletionPercent?: number | null;
  constructionStatus?: string | null;
  constructionDeveloper?: string | null;
  constructionStartDate?: string | null;
  constructionCompletionDate?: string | null;
  constructionRegistrationDate?: string | null;
  constructionLastSync?: string | null;
}

interface Payment {
  id: string;
  unitId: string;
  investorId: string;
  amount: number;
  dueDate: string;
  status: 'Paid' | 'Pending' | 'Overdue';
  installmentNumber: number;
  totalInstallments: number;
  proofOfPayment?: string;
  paymentDate?: string;
}

interface Document {
  id: string;
  investorId: string;
  name: string;
  type: string;
  documentType?: string;
  category?: string;
  unitId?: string | null;
  uploadDate: string;
  status: 'Processing' | 'Processed' | 'Error' | 'Pending Verification' | 'Verified' | 'Rejected';
  verificationStatus?: 'pending_verification' | 'verified' | 'rejected';
  verificationNote?: string;
  extractedData?: any;
  fileUrl?: string;
  url?: string;
  driveUrl?: string;
  downloadUrl?: string;
  fileData?: string;
  fileSize?: number;
  fileType?: string;
  fileName?: string;
  googleDrive?: {
    fileId?: string;
    webViewLink?: string;
    webContentLink?: string;
  };
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  nationality: string;
  birthDate: string;
  password: string;
  confirmPassword: string;
}

interface InvestorPortalProps {
  onChangeRole?: () => void;
  onPreviewOwnerPM?: () => void;
  /**
   * Owner | Tenant switch, rendered at the top of the sign-in panel.
   *
   * The entry screen no longer asks which role you are before showing a login
   * — it opens on WelcomeScreen and continues straight here as an owner. This
   * is how a tenant corrects that without backing out. Undefined outside the
   * normal router (the showcase, the PM preview), where the switch would have
   * nowhere to go.
   */
  onSwitchRole?: (role: 'owner' | 'tenant') => void;
  /**
   * Code-gated showcase (portals/demo/DemoShell): open the app already signed
   * in as a SAMPLE investor holding sample units, so management can walk an
   * audience through the real Owner experience without an account.
   *
   * Three things this must guarantee, all handled below:
   *  1. no real session is picked up — the auto-restore effect is skipped, or
   *     a staff member's cached `investor` in localStorage would be shown;
   *  2. the loaders do not run — a blocked API call resolves to an empty
   *     payload, and `setUnits(data.units || [])` would then wipe the sample
   *     units and leave an empty app mid-demo;
   *  3. nothing is persisted — no writes to localStorage under demo.
   *
   * All API traffic is additionally blocked at window.fetch by the shell, so
   * the guards here are about keeping the demo coherent, not about being the
   * only line of defence.
   */
  demoMode?: boolean;
}

const InvestorPortal: React.FC<InvestorPortalProps> = ({ onChangeRole, onSwitchRole, onPreviewOwnerPM, demoMode }) => {
  const { t, language } = useLanguage();
  // Authentication state
  // demoMode alone walks straight in — that is the code-gated showcase, shown
  // to someone in the room, where a password prompt is friction. FILM mode is
  // the opposite: a product video that cuts from the app icon to a populated
  // dashboard skips the one moment every viewer recognises, so it starts at
  // the sign-in screen and accepts the sample account locally.
  //
  // It does NOT start the walkthrough. The film carries its own narration, and
  // a tour popping up over the shot is something to dismiss, not to record.
  const filmLogin = !!demoMode && isFilmMode();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!demoMode && !filmLogin);
  const [currentInvestor, setCurrentInvestor] = useState<Investor | null>(
    demoMode ? (DEMO_INVESTOR as Investor) : null,
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  // Unread messages from the management team — drives the badge on the More
  // tab and on the Messages row inside it.
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  // A staff account looking at its own portfolio instead of the console.
  const [adminViewingOwn, setAdminViewingOwn] = useState<boolean>(false);
  const lastLoadedInvestorRef = useRef<string | null>(null);
  const [vipStatus, setVipStatus] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');

const [isFaceAuthEnabled, setIsFaceAuthEnabled] = useState<boolean>(false);
const [showFaceAuth, setShowFaceAuth] = useState<boolean>(false);
const videoRef = useRef<HTMLVideoElement>(null);
const canvasRef = useRef<HTMLCanvasElement>(null);
const streamRef = useRef<MediaStream | null>(null);
  const { showWalkthrough, closeWalkthrough, completeWalkthrough } = useWalkthrough();


  // App state
  const [activeTab, setActiveTabRaw] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });
  const setActiveTab = useCallback((tab: string) => {
    setActiveTabRaw(tab);
    trackPageView(tab);
  }, []);
  const [units, setUnits] = useState<Unit[]>(demoMode ? (DEMO_PORTAL_UNITS as Unit[]) : []);
  const [communityChannels, setCommunityChannels] = useState<any[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>('');
  const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<string>('OTP Document');
  const [documentCategory, setDocumentCategory] = useState<string>('Personal Documents');
  const [selectedUnitForDoc, setSelectedUnitForDoc] = useState<string>('');
  const [showAddUnitForm, setShowAddUnitForm] = useState<boolean>(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['Personal Documents']));
  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false);
  // Add these after your existing useState declarations
const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
const [paginationInfo, setPaginationInfo] = useState<any>(null);  // ✅ Now TypeScript knows it can hold data
const [currentPage, setCurrentPage] = useState<number>(1);
const [documentSummary, setDocumentSummary] = useState<any>(null);
const [searchTerm, setSearchTerm] = useState<string>('');
const [selectedDocType, setSelectedDocType] = useState<string>('All');
const [isSearching, setIsSearching] = useState<boolean>(false);
const [searchResults, setSearchResults] = useState<any[]>([]);
const [showAdminPanel, setShowAdminPanel] = useState<boolean>(false);
 
  // Chatbot state
 const [chatMessages, setChatMessages] = useState<Array<{id: string, text: string, isUser: boolean, timestamp: Date}>>([]);
  const [chatInput, setChatInput] = useState<string>('');
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
const [showChatbot, setShowChatbot] = useState(false);
const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
const [chatInitialMessage, setChatInitialMessage] = useState<string | undefined>(undefined);
// 'brain' when the InvestMenter Brain tab opened the chat (advisor conversation).
const [chatInitialMode, setChatInitialMode] = useState<'brain' | undefined>(undefined);
  const chatInputRef = useRef<HTMLInputElement>(null);
  
  const [newUnitForm, setNewUnitForm] = useState<{
    name: string;
    unitNumber: string;
    project: string;
    type: string;
    area: string;
  }>({
    name: '',
    unitNumber: '',
    project: '',
    type: '',
    area: ''
  });

  // nameDe / descriptionDe are what German users see; `name` stays the
  // English key the booking request and the team use.
  const AVAILABLE_SERVICES: (Service & { nameDe?: string; descriptionDe?: string })[] = [
      { id: 'c1', name: 'Holdings & Foundations Abroad', description: 'Strategic international holding and foundation structures for asset protection.', nameDe: 'Holdings & Stiftungen im Ausland', descriptionDe: 'Strategische internationale Holding- und Stiftungsstrukturen zum Vermögensschutz.', category: 'other'},
      { id: 'c2', name: 'Tax Expert Opinions & Advice', description: 'Professional tax consultation and expert opinions for international investors.', nameDe: 'Steuerliche Gutachten & Beratung', descriptionDe: 'Professionelle Steuerberatung und Gutachten für internationale Investoren.', category: 'other'},
      { id: 'c3', name: 'Emigration & Exit Taxation', description: 'Comprehensive exit tax planning and emigration support services.', nameDe: 'Auswanderung & Wegzugsbesteuerung', descriptionDe: 'Umfassende Wegzugssteuerplanung und Unterstützung bei der Auswanderung.', category: 'other'},
      { id: 'c4', name: 'Sustainable Asset Protection', description: 'Long-term asset protection strategies for high-net-worth individuals.', nameDe: 'Nachhaltiger Vermögensschutz', descriptionDe: 'Langfristige Vermögensschutzstrategien für vermögende Privatpersonen.', category: 'other'},
      { id: 'c5', name: 'International Tax Optimization', description: 'Strategic tax optimization for international business and investment activities.', nameDe: 'Internationale Steueroptimierung', descriptionDe: 'Strategische Steueroptimierung für internationale Geschäfts- und Investitionstätigkeiten.', category: 'other'},
      { id: 'c6', name: 'Emigration & Visa Service', description: 'Complete emigration and visa services for relocating to Dubai.', nameDe: 'Auswanderung & Visa-Service', descriptionDe: 'Kompletter Auswanderungs- und Visaservice für den Umzug nach Dubai.', category: 'other'},
      { id: 'c7', name: 'Company Formation in Dubai', description: 'End-to-end company formation and business setup in Dubai.', nameDe: 'Firmengründung in Dubai', descriptionDe: 'Komplette Firmengründung und Geschäftsaufbau in Dubai.', category: 'other'},
      { id: 'c8', name: 'Ongoing Tax Support in the UAE', description: 'Continuous tax compliance and advisory services for UAE-based businesses.', nameDe: 'Laufende Steuerbetreuung in den VAE', descriptionDe: 'Laufende Steuer-Compliance und Beratung für Unternehmen in den VAE.', category: 'other'},
      { id: 'v1', name: 'Snagging Request', description: 'Professional property inspection to identify defects and issues before handover.', nameDe: 'Snagging-Anfrage', descriptionDe: 'Professionelle Abnahmeprüfung, um Mängel vor der Übergabe zu finden.', category: 'legal'},
      { id: 'v2', name: 'Eviction Notice Request', description: 'Legal eviction notice preparation and processing in compliance with Dubai regulations.', nameDe: 'Räumungsmitteilung', descriptionDe: 'Erstellung und Bearbeitung einer Räumungsmitteilung gemäß den Vorschriften in Dubai.', category: 'legal'},
      { id: 'v3', name: 'Last Will', description: 'Professional drafting of your Last Will and Testament in accordance with UAE law.', nameDe: 'Testament', descriptionDe: 'Professionelle Erstellung Ihres Testaments nach dem Recht der VAE.', category: 'legal'},
      { id: 'v4', name: 'Property Breakdown Request', description: 'Detailed property subdivision and boundary marking services.', nameDe: 'Aufteilung einer Immobilie', descriptionDe: 'Detaillierte Aufteilung der Immobilie und Grenzmarkierung.', category: 'legal'},
      { id: 'v5', name: 'Gifting Request', description: 'Property gifting and transfer services with full legal compliance.', nameDe: 'Schenkung', descriptionDe: 'Schenkung und Übertragung von Immobilien unter voller Einhaltung der Rechtsvorschriften.', category: 'legal'},
      { id: 'v6', name: 'Document Attestation', description: 'Professional document attestation and legalization services.', nameDe: 'Beglaubigung von Dokumenten', descriptionDe: 'Professionelle Beglaubigung und Legalisierung von Dokumenten.', category: 'legal'},
      { id: 'v7', name: 'POA Request', description: 'Comprehensive Power of Attorney document preparation and notarization.', nameDe: 'Vollmacht (POA)', descriptionDe: 'Umfassende Erstellung und notarielle Beurkundung einer Vollmacht.', category: 'legal'},
      { id: 'v8', name: 'Escrow Request', description: 'Secure escrow account setup and management for property transactions.', nameDe: 'Treuhandkonto (Escrow)', descriptionDe: 'Sichere Einrichtung und Verwaltung eines Treuhandkontos für Immobilientransaktionen.', category: 'sales'},
      { id: 'v9', name: 'Property Listing', description: 'Professional property marketing and listing services.', nameDe: 'Immobilien-Inserat', descriptionDe: 'Professionelle Vermarktung und Inserierung Ihrer Immobilie.', category: 'sales'},
      { id: 'v10', name: 'Closed Deal Request', description: 'End-to-end transaction closure and handover management.', nameDe: 'Abschluss einer Transaktion', descriptionDe: 'Vollständige Abwicklung der Transaktion bis zur Übergabe.', category: 'sales'},
      { id: 'v11', name: 'Property Management', description: 'Comprehensive property management services for landlords.', nameDe: 'Hausverwaltung', descriptionDe: 'Umfassende Hausverwaltung für Vermieter.', category: 'sales'},
  ];

  // ============================================================
  // AUTO-RESTORE SESSION ON APP LOAD — keeps user logged in
  // ============================================================
  useEffect(() => {
    // Showcase runs as the sample investor. Restoring a cached session here
    // would put a REAL investor's name and data on screen during a demo —
    // whoever last signed in on this browser, which is usually a staff member.
    if (demoMode) return;

    const savedToken = localStorage.getItem('authToken');
    const savedInvestor = localStorage.getItem('investor');

    if (!savedToken || !savedInvestor) return;

    try {
      const investor = JSON.parse(savedInvestor);
      if (investor && investor.email) {
        console.log('🔄 Restoring session for:', investor.email);
        setCurrentInvestor(investor);
        setIsAuthenticated(true);
        // Staff land on the admin overview on a restored session too. Without
        // this, isAdmin only ever came back from the LOGIN response, so a
        // manager who reopened the app — which is most openings — got the
        // owner dashboard instead of their console, with no way back to it
        // short of signing out. The flag decides which screen renders; every
        // endpoint behind it re-checks on the server.
        if (isAdminAccount(investor.email)) setIsAdmin(true);
        if (investor.vipStatus) {
          setVipStatus(investor.vipStatus);
        }
      }
    } catch {
      // Corrupted data, clear it
      localStorage.removeItem('investor');
    }
  }, []); // Run once on mount

  // Unread messages from the management team. Polled slowly and only while
  // the app is in front: this is a badge, not the inbox — OwnerMessagesView
  // polls properly once the thread is actually open.
  useEffect(() => {
    if (!isAuthenticated || demoMode || isAdmin) return;
    let alive = true;
    const poll = () => {
      fetchMessageSummary()
        .then((s) => { if (alive) setUnreadMessages(s.unread || 0); })
        .catch(() => { /* badge only — a failed count is not worth a message */ });
    };
    poll();
    // This one is mounted on EVERY screen for the whole session, so it is the
    // poller that decides whether an app left open all day keeps a container
    // alive all day. pollWhileActive stops it once nobody is touching it.
    const stop = pollWhileActive(poll, 60000);
    return () => { alive = false; stop(); };
  }, [isAuthenticated, demoMode, isAdmin]);

  // Persistent session — no inactivity auto-logout.
  // Users stay signed in until they explicitly log out.

  // FIXED: Portfolio calculations - only count OTP-created units, exclude SOA units
  // Add the helper function first (if not already added)
// Around line 850, REPLACE the existing function:
const getUnitPortfolioData = useCallback((unit: Unit): {
  purchaseValue: number;
  dataSource: string;
  completionDate: string | null;
  completionSource: string;
  hasFinancialData: boolean;
  hasOTP: boolean;
  hasOQOOD: boolean;
  hasSPA: boolean;
} => {
  const unitDocs = documents.filter((doc: Document) => doc.unitId === unit.id);
  

  
  // ✅ Find document types
  const otpDoc = unitDocs.find((doc: Document) => {
    const types = [
      doc.type,
      doc.documentType,
      doc.extractedData?.type
    ].map(t => (t || '').toUpperCase());
    
    return types.includes('OTP');
  });
  
  const oqoodDoc = unitDocs.find((doc: Document) => {
    const types = [
      doc.type,
      doc.documentType,
      doc.extractedData?.type
    ].map(t => (t || '').toUpperCase().trim());
    
    const oqoodVariants = ['OQOOD', 'OQOOD CONTRACT', 'OQOOD DOCUMENT', 'OQOOD SALES', 'OQOOD INITIAL'];
    const isOqood = oqoodVariants.some(variant => types.some(type => type.includes(variant)));
    
    if (isOqood) {
    }
    
    return isOqood;
  });
  
  const spaDoc = unitDocs.find((doc: Document) => {
    const types = [
      doc.type,
      doc.documentType,
      doc.extractedData?.type
    ].map(t => (t || '').toUpperCase());
    
    return types.includes('SPA');
  });
  
  
  let purchaseValue = unit.purchaseValue || 0;
  let dataSource = 'unit';
  
  // ✅ Priority: OTP > OQOOD > SPA for PRICE
  if (otpDoc?.extractedData) {
    const otpPrice = otpDoc.extractedData.purchasePrice || otpDoc.extractedData.amount;
    if (otpPrice) {
      purchaseValue = parseFloat(String(otpPrice).replace(/[^0-9.]/g, ''));
      dataSource = 'OTP';
    }
  } else if (oqoodDoc?.extractedData) {
    
    const priceFields = [
      'originalPrice', 'Original Price', 'original_price',
      'contractPrice', 'Contract Price',
      'salePrice', 'Sale Price',
      'purchasePrice', 'amount', 'price'
    ];
    
    let oqoodPrice = null;
    
    for (const field of priceFields) {
      if (oqoodDoc.extractedData[field]) {
        const cleanValue = String(oqoodDoc.extractedData[field]).replace(/[^\d.]/g, '');
        const parsedPrice = parseFloat(cleanValue);
        
        if (!isNaN(parsedPrice) && parsedPrice > 0) {
          oqoodPrice = parsedPrice;
          break;
        }
      }
    }
    
    if (oqoodPrice && oqoodPrice > 0) {
      purchaseValue = oqoodPrice;
      dataSource = 'OQOOD';
    } else {
      console.error('❌ OQOOD document found but NO valid price extracted');
    }
  } else if (spaDoc?.extractedData?.purchasePrice) {
    purchaseValue = parseFloat(String(spaDoc.extractedData.purchasePrice).replace(/[^0-9.]/g, ''));
    dataSource = 'SPA';
  }
  
  // ✅ Priority: OTP > SPA > OQOOD for PURCHASE DATE
  let purchaseDate: string | null = null;
  let purchaseDateSource = 'none';
  
  if (otpDoc?.extractedData?.purchaseDate) {
    purchaseDate = otpDoc.extractedData.purchaseDate;
    purchaseDateSource = 'OTP';
  } else if (spaDoc?.extractedData?.purchaseDate) {
    purchaseDate = spaDoc.extractedData.purchaseDate;
    purchaseDateSource = 'SPA';
  } else if (oqoodDoc?.extractedData?.purchaseDate || oqoodDoc?.extractedData?.contractDate) {
    purchaseDate = oqoodDoc.extractedData.purchaseDate || oqoodDoc.extractedData.contractDate;
    purchaseDateSource = 'OQOOD';
  }
  
  // ✅ Completion date extraction
  let completionDate: string | null = unit.estimatedCompletionDate || null;
  let completionSource = 'unit';
  
  if (otpDoc?.extractedData?.estimatedCompletionDate) {
    completionDate = otpDoc.extractedData.estimatedCompletionDate;
    completionSource = 'OTP';
  } else if (spaDoc?.extractedData?.estimatedCompletionDate) {
    completionDate = spaDoc.extractedData.estimatedCompletionDate;
    completionSource = 'SPA';
  }
  
  const result = {
    purchaseValue,
    dataSource,
    purchaseDate,
    purchaseDateSource,
    completionDate,
    completionSource,
    hasFinancialData: purchaseValue > 0,
    hasOTP: !!otpDoc,
    hasOQOOD: !!oqoodDoc,
    hasSPA: !!spaDoc
  };
  
  
  return result;
}, [documents]);

const calculateAppreciatedValue = useCallback((purchasePrice: number, purchaseDate: string, completionDate: string): {
  currentValue: number;
  status: string;
  isOffplan: boolean;
  appreciationAmount: number;
  completionDate: string | null;
} => {
  
  // ✅ CRITICAL: Validate input first
  if (!purchasePrice || purchasePrice <= 0) {
    console.warn('⚠️ Invalid purchase price:', purchasePrice);
    return {
      currentValue: 0,
      status: 'Unknown',
      isOffplan: false,
      appreciationAmount: 0,
      completionDate: null
    };
  }
  
  // Validate price is in reasonable range (100K to 100M AED)
  if (purchasePrice < 100000 || purchasePrice > 100000000) {
    console.error('❌ Purchase price out of valid range:', purchasePrice);
    return {
      currentValue: purchasePrice, // Use as-is
      status: 'Unknown',
      isOffplan: false,
      appreciationAmount: 0,
      completionDate: completionDate
    };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const completion = new Date(completionDate);
  completion.setHours(0, 0, 0, 0);
  
  // ✅ CRITICAL FIX: Check if property is completed FIRST
  const isCompleted = completion <= today;
  
  
  if (isCompleted) {
    // ✅ Property is READY - use purchase price as current value
    
    return {
      currentValue: purchasePrice,
      status: 'Ready',
      isOffplan: false,
      appreciationAmount: 0,
      completionDate: completionDate
    };
  } else {
    // Property is OFFPLAN - calculate appreciation
    const purchase = new Date(purchaseDate);
    purchase.setHours(0, 0, 0, 0);
    
    const annualAppreciationRate = 0.15; // 15% per year
    
    // Calculate years elapsed
    const elapsedDays = Math.ceil((today.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24));
    const elapsedYears = elapsedDays / 365;
    
    // ✅ CRITICAL: Cap appreciation at 10 years maximum
    const maxYears = 10;
    const cappedYears = Math.min(elapsedYears, maxYears);
    
    const currentAppreciationRate = cappedYears * annualAppreciationRate;
    const currentValue = Math.round(purchasePrice * (1 + currentAppreciationRate));
    const appreciationAmount = currentValue - purchasePrice;
    
    
    return {
      currentValue: currentValue,
      status: 'Offplan',
      isOffplan: true,
      appreciationAmount: appreciationAmount,
      completionDate: completionDate
    };
  }
}, []);

const getUnitFinancialData = useCallback((unitId: string, allDocuments: Document[]) => {
  const unitDocs = allDocuments.filter(doc => doc.unitId === unitId);
  
  const otpDocs = unitDocs.filter(doc => 
    doc.extractedData?.type === 'OTP' || doc.documentType === 'OTP' || doc.type === 'OTP'
  );
  const oqoodDocs = unitDocs.filter(doc => 
    doc.extractedData?.type === 'OQOOD' || doc.documentType === 'OQOOD' || doc.type === 'OQOOD'
  );
  const spaDocs = unitDocs.filter(doc => 
    doc.extractedData?.type === 'SPA' || doc.documentType === 'SPA' || doc.type === 'SPA'
  );
  
  let portfolioValue = 0;
  let portfolioSource = 'none';
  
  if (otpDocs.length > 0 && otpDocs[0].extractedData?.purchasePrice) {
    portfolioValue = parseFloat(String(otpDocs[0].extractedData.purchasePrice).replace(/[^\d.]/g, ''));
    portfolioSource = 'OTP';
  } else if (oqoodDocs.length > 0) {
    const oqoodData = oqoodDocs[0].extractedData;
    const priceValue = oqoodData?.purchasePrice || 
      oqoodData?.propertyValue || 
      oqoodData?.amount ||
      oqoodData?.price ||
      oqoodData?.value || 0;
    
    portfolioValue = parseFloat(String(priceValue).replace(/[^\d.]/g, ''));
    
    if (portfolioValue > 0) {
      portfolioSource = 'OQOOD';
    }
  }
  
  // ✅ ADD THIS: Extract purchase date from documents
  let purchaseDate: string | null = null;
  let purchaseDateSource = 'none';
  
  if (otpDocs.length > 0 && otpDocs[0].extractedData?.purchaseDate) {
    purchaseDate = otpDocs[0].extractedData.purchaseDate;
    purchaseDateSource = 'OTP';
  } else if (spaDocs.length > 0 && spaDocs[0].extractedData?.purchaseDate) {
    purchaseDate = spaDocs[0].extractedData.purchaseDate;
    purchaseDateSource = 'SPA';
  } else if (oqoodDocs.length > 0 && (oqoodDocs[0].extractedData?.purchaseDate || oqoodDocs[0].extractedData?.contractDate)) {
    purchaseDate = oqoodDocs[0].extractedData.purchaseDate || oqoodDocs[0].extractedData.contractDate;
    purchaseDateSource = 'OQOOD';
  }
  
  let completionDate = null;
  let completionSource = 'none';
  
  if (otpDocs.length > 0 && otpDocs[0].extractedData?.estimatedCompletionDate) {
    completionDate = otpDocs[0].extractedData.estimatedCompletionDate;
    completionSource = 'OTP';
  } else if (spaDocs.length > 0 && spaDocs[0].extractedData?.estimatedCompletionDate) {
    completionDate = spaDocs[0].extractedData.estimatedCompletionDate;
    completionSource = 'SPA';
  }
  
  let appreciationData: {
    currentValue: number;
    status: string;
    isOffplan: boolean;
    appreciationAmount: number;
    completionDate: string | null;
  } = {
    currentValue: portfolioValue,
    status: 'Unknown',
    isOffplan: false,
    appreciationAmount: 0,
    completionDate: null
  };
  
  // ✅ NOW THIS WILL WORK: Check if we have both purchaseDate AND completionDate
  if (portfolioValue > 0 && purchaseDate && completionDate) {
    appreciationData = calculateAppreciatedValue(portfolioValue, purchaseDate, completionDate);
  } else if (portfolioValue > 0) {
    appreciationData = {
      currentValue: portfolioValue,
      status: 'Ready',
      isOffplan: false,
      appreciationAmount: 0,
      completionDate: null
    };
  }
  
  return {
    portfolioValue,
    portfolioSource,
    completionDate,
    completionSource,
    appreciationData,
    hasOTP: otpDocs.length > 0,
    hasOQOOD: oqoodDocs.length > 0,
    hasSPA: spaDocs.length > 0
  };
}, [calculateAppreciatedValue]);

const otpUnits = useMemo(() => {
  return units.filter((unit: Unit) => {
    const portfolioData = getUnitPortfolioData(unit);
    const hasValidPrice = portfolioData.purchaseValue > 0;
    
    
    return hasValidPrice && portfolioData.hasFinancialData;
  });
}, [units, getUnitPortfolioData]);

const [totalPortfolioValue, setTotalPortfolioValue] = useState<number>(0);
// The DLD valuation for the Properties banner — same sources, same order as
// Home: this session's payload, then the device copy, then (only if the
// device has none) one fetch after the units have loaded.
const [heroDld, setHeroDld] = useState<any>(() => {
  const id = currentInvestor?.id;
  return id && !demoMode ? (cachedMarketIntelligence(id) || storedFullMarketIntelligence(id) || storedMarketIntelligence(id)) : null;
});
useEffect(() => {
  const id = currentInvestor?.id;
  if (!id || demoMode) return;
  const have = cachedMarketIntelligence(id) || storedFullMarketIntelligence(id) || storedMarketIntelligence(id);
  if (have) { setHeroDld(have); return; }
  const token = localStorage.getItem('authToken');
  if (!token || !units.length) return;
  let alive = true;
  const timer = window.setTimeout(() => {
    fetchMarketIntelligence(API_URL, id, token).then((d) => { if (alive && d) setHeroDld(d); }).catch(() => {});
  }, 3000);
  return () => { alive = false; window.clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentInvestor?.id, units.length]);
 useEffect(() => {
     if (units && units.length > 0) {
       // Held units only. A sold unit's value left the portfolio with it —
       // counting it here keeps paying the owner twice (once in the headline
       // total, once in whatever the proceeds became) and would compound that
       // phantom value through the 15-year projections on this same screen.
       // The realised return is reported separately, on the Sold tab.
       const value = units.reduce((sum: number, u: any) => {
         if ((u.status || '').toLowerCase() === 'sold') return sum;
         return sum + (parseFloat(u.currentValue) || 0);
       }, 0);
       setTotalPortfolioValue(value);
     } else {
       setTotalPortfolioValue(0);
     }
   }, [units]);

  // Fetch WhatsApp group invite links from authenticated API (links not stored in frontend bundle)
  useEffect(() => {
    if (!units.length) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch(`${API_URL}/community-channels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ units: units.map(u => ({ developer: (u as any).developer, location: (u as any).location, project: (u as any).project })) }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.channels) setCommunityChannels(data.channels); })
      .catch(() => {});
  }, [units]);

const totalMonthlyRental = useMemo(() => {
  // Only count rental from OTP units (portfolio units with financial data)
  return otpUnits.reduce((sum: number, unit: Unit) => sum + (unit.monthlyRental || 0), 0);
}, [otpUnits]);

const totalGains = useMemo(() => {
  return otpUnits.reduce((sum: number, unit: Unit) => {
    const portfolioData = getUnitPortfolioData(unit);
    return sum + ((unit.currentValue || 0) - portfolioData.purchaseValue);
  }, 0);
}, [otpUnits, getUnitPortfolioData]);


  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (showProfileMenu && !target.closest('.relative')) {
      setShowProfileMenu(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [showProfileMenu]);

  const toggleFolder = (folderName: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderName)) {
        newSet.delete(folderName);
      } else {
        newSet.add(folderName);
      }
      return newSet;
    });
  };

  const loadInvestorData = useCallback(async (page = 1, append = false, retryCount = 0): Promise<void> => {
  if (!currentInvestor) return;
  // Showcase: sample units are already in state. A blocked call resolves to an
  // empty payload, and `setUnits(data.units || [])` below would wipe them —
  // leaving an empty portfolio in the middle of someone's demo.
  if (demoMode) return;

  setIsLoadingData(true);
  
  try {
    
    const response = await makeAPIRequest('getInvestorData', {
      investorId: currentInvestor.id,
      page: page,
      limit: 200,
      sortBy: 'uploadDate',
      sortOrder: 'desc',
      forceFirebase: true  // ✅ ALWAYS load from Firebase
    });
    
    const data = response.data || {};
    
    
    // ✅ CRITICAL CHECK: If units are empty but we expect them, retry
    if ((!data.units || data.units.length === 0) && retryCount < 3) {
      console.warn(`⚠️ No units received, retrying in 2 seconds... (Attempt ${retryCount + 1}/3)`);
      
      setIsLoadingData(false);
      
      // Wait and retry
      await new Promise(resolve => setTimeout(resolve, 2000));
      return loadInvestorData(page, append, retryCount + 1);
    }
    
    // Handle documents - append or replace
    if (append && page > 1) {
      setDocuments(prev => [...prev, ...(data.documents || [])]);
    } else {
      setDocuments(data.documents || []);
    }
    
    // Units and payments always replace
    setUnits(data.units || []);
    setPayments(data.payments || []);

    // Set VIP status from investor data
    if (data.investor?.vipStatus) {
      setVipStatus(data.investor.vipStatus);
      setCurrentInvestor(prev => {
        if (prev) {
          const updated = { ...prev, vipStatus: data.investor.vipStatus };
          localStorage.setItem('investor', JSON.stringify(updated));
          return updated;
        }
        return prev;
      });
    }

    // Store pagination info
    setPaginationInfo(data.pagination || response.pagination);
    setCurrentPage(page);
    
  } catch (error) {
    console.error('❌ Failed to load investor data:', error);
    
    // Retry on error
    if (retryCount < 3) {
      console.warn(`⚠️ Retrying after error... (Attempt ${retryCount + 1}/3)`);
      setIsLoadingData(false);
      await new Promise(resolve => setTimeout(resolve, 2000));
      return loadInvestorData(page, append, retryCount + 1);
    }
    
  } finally {
    setIsLoadingData(false);
  }
}, [currentInvestor, demoMode]);

// Load more documents (pagination)
const loadMoreDocuments = useCallback(async () : Promise<void> => {
  if (!paginationInfo?.hasMore || isLoadingData) {
    return;
  }
  
  const nextPage = currentPage + 1;
  await loadInvestorData(nextPage, true); // append = true
}, [paginationInfo, isLoadingData, currentPage, loadInvestorData]);

// Load document summary
const loadDocumentSummary = useCallback(async () : Promise<void> => {
  if (!currentInvestor) return;
  
  try {
    const data = await makeAPIRequest('getDocumentSummary', {
      investorId: currentInvestor.id
    });
    setDocumentSummary(data);
  } catch (error) {
    console.error('❌ Failed to load document summary:', error);
  }
}, [currentInvestor]);

// Search documents
const searchDocuments = useCallback(async () : Promise<void> => {
  if (!currentInvestor) return;
  
  // If no search term and "All" type, clear search results
  if (!searchTerm.trim() && selectedDocType === 'All') {
    setSearchResults([]);
    setIsSearching(false);
    return;
  }
  
  setIsSearching(true);
  
  try {
    const data = await makeAPIRequest('searchDocuments', {
      investorId: currentInvestor.id,
      searchTerm: searchTerm.trim(),
      documentType: selectedDocType === 'All' ? null : selectedDocType,
      limit: 100
    });
    
    setSearchResults(data.documents || []);
    
  } catch (error) {
    console.error('❌ Search failed:', error);
    setSearchResults([]);
  } finally {
    setIsSearching(false);
  }
}, [currentInvestor, searchTerm, selectedDocType]);

// Clear search
const clearSearch = useCallback(() => {
  setSearchTerm('');
  setSelectedDocType('All');
  setSearchResults([]);
}, []);

  const loadPortfolioValue = useCallback(async () : Promise<void> => {
  if (!currentInvestor?.id) return;

  try {
    
    const response = await makeAPIRequest('getPortfolioValueEnhanced', {
      investorId: currentInvestor.id
    });
    
    
    if (response && response.breakdown) {
      const updatedUnits = units.map(unit => {
        const unitData = response.breakdown.find((b: any) => b.unitName === unit.name);
        if (unitData) {
          return {
            ...unit,
            currentValue: unitData.currentValue,
            purchaseValue: unitData.purchaseValue,
            appreciationAmount: unitData.appreciationAmount,
            occupancyStatus: unitData.status,
            estimatedCompletionDate: unitData.completionDate
          };
        }
        return unit;
      });
      
      setUnits(updatedUnits);
    }
  } catch (error) {
    console.error('❌ Error loading portfolio:', error);
  }
}, [currentInvestor, units]);

// ✅ CORRECT
// Rent is never invented. This used to write 5% of the unit's value a year
// into monthlyRental for every unit without rent, and every rent total and
// yield in the app then counted it as income (2.57M "rental income" from three
// leases, 2026-10-09). A unit without a lease or recorded rent earns nothing;
// what it could earn at DLD market rent is shown separately (portfolioRent).
const fetchRentalIncomeEstimate = useCallback(async (_unit: Unit) => {
  return { success: false, message: 'No rent on record' };
}, []);

// Make sure this function exists
const reloadDataFromFirebase = useCallback(async (): Promise<void> => {
  if (!currentInvestor?.id) return;
  if (demoMode) return;   // same reason as loadInvestorData

  setIsLoadingData(true);
  
  try {
    const response = await fetch(
      `${API_URL}/getInvestorData`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investorId: currentInvestor.id,
          page: 1,
          limit: 50,
          forceFirebase: true
        }),
      }
    );

    const result = await response.json();
    
    if (result.success && result.data) {
      setUnits(result.data.units || []);
      setDocuments(result.data.documents || []);
      setPayments(result.data.payments || []);
      
      if (result.data.units?.length > 0) {
        // Held units only — same reason as the effect above.
        const portfolioValue = result.data.units.reduce((sum: number, u: any) => {
          if ((u.status || '').toLowerCase() === 'sold') return sum;
          return sum + (parseFloat(u.currentValue) || 0);
        }, 0);
        setTotalPortfolioValue(portfolioValue);
      }
    }
  } catch (error) {
    console.error('❌ Error reloading:', error);
  } finally {
    setIsLoadingData(false);
  }
}, [currentInvestor, demoMode, setUnits, setDocuments, setPayments, setTotalPortfolioValue, setIsLoadingData]);


// ========================================
// IMPORTANT: calculatePortfolioValue Function
// ========================================
// Make sure you have this function (or add it if missing)

const calculatePortfolioValue = (units: any[]) => {
  if (!units || units.length === 0) {
    return 0;
  }
  
  // Sum up currentValue of all units
  const total = units.reduce((sum, unit) => {
    const value = parseFloat(unit.currentValue) || 0;
    return sum + value;
  }, 0);
  
  
  return total;
};

// ✅ ADD THIS FUNCTION TO AUTO-FETCH RENTAL DATA FOR ALL UNITS
const updateAllUnitsRentalIncome = useCallback(async (): Promise<void> => {
  if (!units || units.length === 0) return;
  
  
  for (const unit of units) {
    // Only update if we don't have recent rental data
    if (!unit.rentalLastUpdated || !unit.monthlyRental) {
      await fetchRentalIncomeEstimate(unit);
      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
}, [units, fetchRentalIncomeEstimate]);

  useEffect(() => {
  
  if (!currentInvestor) {
    setUnits([]);
    setDocuments([]);
    setPayments([]);
    lastLoadedInvestorRef.current = null;
    return;
  }
  
  // Only load if investor actually changed
  if (lastLoadedInvestorRef.current === currentInvestor.email) {
    return;
  }
  
  lastLoadedInvestorRef.current = currentInvestor.email;
  
  // Reset pagination
  setCurrentPage(1);
  setPaginationInfo(null);
  setSearchResults([]);
  setSearchTerm('');
  setSelectedDocType('All');
  
  // Load data — needed for session restore (app reopen) and login
  loadInvestorData(1, false);
  loadDocumentSummary();

  // Load face auth setting
  const faceAuthEnabled = localStorage.getItem(`face_auth_enabled_${currentInvestor.email}`) === 'true';
  setIsFaceAuthEnabled(faceAuthEnabled);

  // CRM sync on session restore — fetch leadOwner, executiveAssistant & vipStatus
  if (currentInvestor.email) {
    (async () => {
      try {
        const crmResponse = await fetch(`${API_URL}/crm/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ odooId: currentInvestor.id, email: currentInvestor.email })
        });
        const crmData = await crmResponse.json();
        if (crmData.success && crmData.data) {
          setCurrentInvestor(prev => {
            const updated = {
              ...prev!,
              leadOwner: crmData.data.leadOwner,
              executiveAssistant: crmData.data.executiveAssistant,
              closeLeadId: crmData.data.leadId,
              vipStatus: crmData.data.vipStatus || prev?.vipStatus || null,
              crmSyncedAt: new Date().toISOString()
            };
            localStorage.setItem('investor', JSON.stringify(updated));
            return updated;
          });
          if (crmData.data.vipStatus) {
            setVipStatus(crmData.data.vipStatus);
          }
        }
      } catch (crmError) {
        console.error('❌ CRM sync on restore failed:', crmError);
      }
    })();
  }

}, [currentInvestor, loadInvestorData, loadDocumentSummary]);

// Force reload on app mount (when page refreshes)
useEffect(() => {
  
  // Check if we have a logged-in user
  const savedEmail = localStorage.getItem('investorEmail');
  const savedId = localStorage.getItem('investorId');
  
  
  if (savedEmail && currentInvestor && currentInvestor.email === savedEmail) {
    
    // Force reload data after a short delay
    setTimeout(() => {
      loadInvestorData(1, false)
        .then(() => {
        })
        .catch(err => {
          console.error('   ❌ Force load failed:', err);
        });
    }, 500);
  } else {
  }
}, []); // Empty array = only run once on mount

  useEffect(() => {
  if (otpUnits.length > 0 && currentInvestor) {
    const unitsNeedingRentalData = otpUnits.filter(unit => {
      if (!unit.monthlyRental || unit.monthlyRental === 0) return true;
      
      // Check if data is stale (older than 7 days)
      if (!unit.rentalLastUpdated) return true;
      
      const now = new Date();
      const updated = new Date(unit.rentalLastUpdated);
      const daysSinceUpdate = (now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
      
      return daysSinceUpdate > 7;
    });
    
    if (unitsNeedingRentalData.length > 0) {
      
      // Process in parallel batches
      const fetchRentalDataInBatches = async () => {
        const batchSize = 5; // Process 5 units at a time
        
        for (let i = 0; i < unitsNeedingRentalData.length; i += batchSize) {
          const batch = unitsNeedingRentalData.slice(i, i + batchSize);
          
          // Fetch all units in this batch in parallel
          await Promise.all(
            batch.map(unit => 
              fetchRentalIncomeEstimate(unit).catch(error => {
                console.error(`Failed to fetch rental for ${unit.name}:`, error);
                return { success: false };
              })
            )
          );
          
          // Small delay between batches
          if (i + batchSize < unitsNeedingRentalData.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
      };
      
      fetchRentalDataInBatches();
    }
  }
}, [otpUnits, currentInvestor]);
 // InvestMenter Logo Component (Updated to match actual logo)
const InvestMenterLogo = ({ size = 'large' }: { size?: 'small' | 'medium' | 'large' }) => {
  const scaleFactors = {
    small: 0.6,
    medium: 0.8, 
    large: 1.0
  };
  
  const scale = scaleFactors[size];
  
  return (
    <div className="flex items-center justify-center">
      <img 
        src="/asset-desk-wordmark.png"  // Changed from .svg to .jpeg
        alt="Asset Desk"
        width={500 * scale}
        height={300* scale}
        className="mx-auto"
      />
    </div>
  );
};

// ==================== TOTP HELPER ====================
async function verifyTOTP(secret: string, token: string): Promise<boolean> {
  const base32Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const cleanSecret = secret.toUpperCase().replace(/=+$/, '');
  let bits = 0, value = 0;
  const bytes: number[] = [];
  for (let ci = 0; ci < cleanSecret.length; ci++) {
    value = (value << 5) | base32Chars.indexOf(cleanSecret[ci]);
    bits += 5;
    if (bits >= 8) { bytes.push((value >>> (bits - 8)) & 255); bits -= 8; }
  }
  const keyBytes = new Uint8Array(bytes);
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  for (let offset = -1; offset <= 1; offset++) {
    const counter = Math.floor(Date.now() / 1000 / 30) + offset;
    const buf = new ArrayBuffer(8);
    new DataView(buf).setUint32(4, counter, false);
    const sigBuffer = await crypto.subtle.sign('HMAC', key, buf);
    const sig = new Uint8Array(sigBuffer);
    const idx = sig[19] & 0xf;
    const code = String((((sig[idx] & 0x7f) << 24) | (sig[idx+1] << 16) | (sig[idx+2] << 8) | sig[idx+3]) % 1000000).padStart(6, '0');
    if (code === token) return true;
  }
  return false;
}
// Auth form component
  // Auth form component
// Enhanced Auth form component with 2FA, Forgot Password and Forgot Email functionality
const AuthForm: React.FC = () => {
  // Check URL params for pending document registration flow
  const urlParams = new URLSearchParams(window.location.search);
  const pendingDocsId = urlParams.get('pendingDocs') || '';
  const registerFromEmail = urlParams.get('register') === 'true';

  // "Create account" on the Welcome screen opens straight onto sign-up.
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot-password' | 'forgot-email' | '2fa-verify' | '2fa-setup' | 'sms-login' | 'sms-verify'>(() => (registerFromEmail || signupRequested()) ? 'signup' : 'login');
  useEffect(() => { clearSignupRequest(); }, []);

  // Bank-style: on the phone, once Face ID is set up, the sign-in opens with
  // the Face ID prompt by itself. Not right after a deliberate sign-out (see
  // markSignedOut), and never in film mode or on the sign-up form.
  const faceAutoTried = useRef(false);
  useEffect(() => {
    if (faceAutoTried.current) return;
    faceAutoTried.current = true;
    if (!isNativeApp() || isFilmMode() || authMode !== 'login' || !shouldAutoPromptFaceId()) return;
    let alive = true;
    (async () => {
      if (alive && await hasBiometricCredentials()) handleFaceLogin();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    // Film mode arrives with the sample account already filled in: the shot is
    // the sign-in, not somebody typing an address one letter at a time.
    email: isFilmMode() ? DEMO_INVESTOR.email : '',
    phone: '',
    nationality: '',
    birthDate: '',
    password: isFilmMode() ? 'demo-account' : '',
    confirmPassword: ''
  });
  const [smsPhone, setSmsPhone] = useState('');
  const [smsOtp, setSmsOtp] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  // 2FA States
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQR, setTwoFactorQR] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [tempAuthData, setTempAuthData] = useState<any>(null);
  const [verificationMethod, setVerificationMethod] = useState<'authenticator' | 'backup'>('authenticator');
  // Show/Hide Password States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [showProfileNewPassword, setShowProfileNewPassword] = useState<boolean>(false);
const [profileNewPassword, setProfileNewPassword] = useState<string>('');
  
  // Recovery form states
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoveryNationality, setRecoveryNationality] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationStep, setShowVerificationStep] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showResetPasswordStep, setShowResetPasswordStep] = useState(false);
  const [recoveredEmail, setRecoveredEmail] = useState('');
    const [resetToken, setResetToken] = useState('');

  // ✅ FIX: AuthForm needs its OWN authLoading & authError states
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [accountLocked, setAccountLocked] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  // Terms & Privacy consent state (signup only)
  const [termsAccepted, setTermsAccepted] = useState(false);
  useEffect(() => {
    // Reset consent whenever the user switches between login / signup / recovery
    if (authMode !== 'signup') setTermsAccepted(false);
  }, [authMode]);

  // Confirmation dialog state - THIS IS THE KEY FIX
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showConfirmation = (message: string, callback: () => void) => {
    setConfirmMessage(message);
    setConfirmCallback(() => callback);
    setShowConfirmDialog(true);
  };

  const handleConfirmYes = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmDialog(false);
    setConfirmCallback(null);
  };

  const handleConfirmNo = () => {
    setShowConfirmDialog(false);
    setConfirmCallback(null);
  };

  // ---- Face ID, the way a banking app does it -----------------------------
  // After a password sign-in on the phone, offer Face ID once ("Use Face ID
  // next time?"). Yes stores the login in the Keychain behind Face ID; from
  // then on the sign-in screen opens with the Face ID prompt by itself.
  // Already set up: the saved password is refreshed, so a password change
  // never strands Face ID on the old one. Browser: nothing (no password kept).
  const pendingPasswordRef = useRef<string>('');
  const afterPasswordLogin = async (email: string, password: string) => {
    if (!isNativeApp() || !email || !password) return;
    try {
      if (await hasBiometricCredentials()) {
        await refreshBiometricPassword(email, password);
        return;
      }
      const declinedKey = `faceid_declined_${email.toLowerCase()}`;
      if (localStorage.getItem(declinedKey) === '1') return;
      const availability = await checkBiometricAvailability();
      if (!availability.success) return;
      if (!window.confirm(t('faceIdOffer'))) {
        localStorage.setItem(declinedKey, '1');
        return;
      }
      const enrolled = await enrollBiometric(email, password);
      if (enrolled.success) {
        localStorage.setItem(`face_auth_enabled_${email}`, 'true');
        setIsFaceAuthEnabled(true);
      }
    } catch (e) {
      console.warn('Face ID offer skipped:', e);
    }
  };

  const handleLogin = () => loginWith(formData.email, formData.password);

  const loginWith = async (email: string, password: string, viaFaceId = false) => {
  setAuthLoading(true);
  setAuthError('');
  setAccountLocked(false);
  setAttemptsRemaining(null);
  pendingPasswordRef.current = password;

  // Film mode: no backend to ask (its traffic is blocked), so accept the
  // sample account here. Any password — the point is the gesture, not a
  // credential check, and nothing behind this screen is real.
  if (isFilmMode()) {
    await new Promise((r) => setTimeout(r, 850));   // let the spinner be seen
    setCurrentInvestor(DEMO_INVESTOR as Investor);
    setUnits(DEMO_PORTAL_UNITS as Unit[]);
    setIsAuthenticated(true);
    setAuthLoading(false);
    return;
  }

  try {
    
    // ✅ Call the new /api/login endpoint
    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password
      })
    });

    const data = await response.json();

    if (data.success && data.requires2FA) {
      // Server-side 2FA required — store temp token, show TOTP input
      setTempAuthData({ email: data.email, tempToken: data.tempToken });
      setAuthMode('2fa-verify');
      setTwoFactorCode('');
    } else if (data.success && data.investor) {

      // Before the portal replaces this screen: offer / refresh Face ID.
      if (!viaFaceId) await afterPasswordLogin(email, password);

      // ✅ CRITICAL: Save to localStorage FIRST
      if (data.token) localStorage.setItem('authToken', data.token);
      localStorage.setItem('investorId', data.investor.id);
      localStorage.setItem('investorEmail', data.investor.email);
      localStorage.setItem('investor', JSON.stringify(data.investor));
      localStorage.setItem('lastActivity', Date.now().toString());

      {
        // Set React state
        setCurrentInvestor(data.investor);
        setIsAdmin(data.isAdmin === true || isAdminAccount(data.investor?.email));
        setIsAuthenticated(true);
        trackLogin('email');
        trackEvent('investor_login', { email: data.investor.email });

        // CRM sync is now handled server-side during login (admin-only endpoint)
        // If the login response includes CRM data, apply it
        if (data.crmData) {
          setCurrentInvestor(prev => {
            const updated = {
              ...prev!,
              leadOwner: data.crmData.leadOwner,
              executiveAssistant: data.crmData.executiveAssistant,
              closeLeadId: data.crmData.leadId,
              vipStatus: data.crmData.vipStatus || prev?.vipStatus || null,
              crmSyncedAt: new Date().toISOString()
            };
            localStorage.setItem('investor', JSON.stringify(updated));
            return updated;
          });
          if (data.crmData.vipStatus) {
            setVipStatus(data.crmData.vipStatus);
          }
        }
        
        
        // ✅ CRITICAL FIX: Force data reload after login
        setTimeout(async () => {
          try {
            const investorData = await makeAPIRequest('getInvestorData', {
              investorId: data.investor.id,
              page: 1,
              pageSize: 50,
              forceFirebase: true  // ✅ CRITICAL: Force Firebase load
            });
            
           if (investorData && investorData.data) {
  
  setUnits(investorData.data.units || []);
  setDocuments(investorData.data.documents || []);
  setPayments(investorData.data.payments || []);

    if (investorData.data.investor?.vipStatus) {
    setVipStatus(investorData.data.investor.vipStatus);
    setCurrentInvestor(prev => {
      const updated = { ...prev!, vipStatus: investorData.data.investor.vipStatus };
      localStorage.setItem('investor', JSON.stringify(updated));
      return updated;
    });
  }

            } else {
              console.warn('⚠️ No data returned from getInvestorData');
            }
          } catch (reloadError) {
            console.error('❌ Error reloading data after login:', reloadError);
            // Don't fail login, just log the error
          }
        }, 500); // Small delay to ensure state is set
        
      }
    } else {
      if (data.locked) {
        setAccountLocked(true);
        setAuthError('');
      } else if (data.emailNotVerified) {
        setAuthError(t('ipEmailNotVerified'));
      } else if (viaFaceId) {
        // The saved password no longer works (changed on another device):
        // the investor signs in with the password once, which re-saves it.
        setFormData(prev => ({ ...prev, email }));
        setAuthError(t('passwordMayHaveChanged'));
      } else {
        if (data.attemptsRemaining !== undefined) {
          setAttemptsRemaining(data.attemptsRemaining);
        }
        setAuthError(uiError(data.error, t('ipInvalidEmailOrPassword')));
      }
    }
  } catch (error) {
    console.error('❌ Login error:', error);
    setAuthError(t('ipLoginFailed'));
  }

  setAuthLoading(false);
};

// ==================== SMS LOGIN ====================

const handleSmsLoginSend = async () => {
  if (!smsPhone.trim()) {
    setAuthError(t('ipEnterPhoneNumber'));
    return;
  }
  setSmsSending(true);
  setAuthError('');
  try {
    const response = await fetch(`${API_URL}/sms-login/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: smsPhone.trim() })
    });
    const data = await response.json();
    if (response.ok) {
      setAuthMode('sms-verify');
    } else {
      setAuthError(uiError(data.error, t('ipFailedToSendCode')));
    }
  } catch {
    setAuthError(t('ipConnectionError'));
  }
  setSmsSending(false);
};

const handleSmsLoginVerify = async () => {
  if (!smsOtp.trim()) {
    setAuthError(t('ipEnterVerificationCode'));
    return;
  }
  setAuthLoading(true);
  setAuthError('');
  try {
    const response = await fetch(`${API_URL}/sms-login/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: smsPhone.trim(), otp: smsOtp.trim() })
    });
    const data = await response.json();
    if (data.success && data.requires2FA) {
      // Authenticator on: the WhatsApp code replaced the password, not the 2FA step
      setTempAuthData({ email: data.email, tempToken: data.tempToken });
      setAuthMode('2fa-verify');
      setTwoFactorCode('');
    } else if (data.success && data.investor) {
      if (data.token) localStorage.setItem('authToken', data.token);
      localStorage.setItem('investorId', data.investor.id);
      localStorage.setItem('investorEmail', data.investor.email);
      localStorage.setItem('investor', JSON.stringify(data.investor));
      localStorage.setItem('lastActivity', Date.now().toString());
      setCurrentInvestor(data.investor);
      setIsAuthenticated(true);
    } else {
      setAuthError(uiError(data.error, t('ipInvalidCode')));
    }
  } catch {
    setAuthError(t('ipVerificationFailed'));
  }
  setAuthLoading(false);
};

// ==================== BIOMETRIC LOGIN ====================

const handleFaceLogin = async () => {
  setAuthError('');
  setAuthLoading(true);
  
  try {
    // Check if biometric is available
    const availability = await checkBiometricAvailability();
    
    if (!availability.success) {
      setAuthError(t('biometricNotAvailable'));
      setAuthLoading(false);
      return;
    }

    // Check if credentials are stored
    const hasCredentials = await hasBiometricCredentials();
    
    if (!hasCredentials) {
      setAuthError(t('biometricNotSetUp'));
      setAuthLoading(false);
      return;
    }

    // Authenticate with biometrics
    const result = await authenticateWithBiometric();

    if (!result.success) {
      // Cancelling the Face ID sheet is a choice, not an error.
      if (!/cancel/i.test(result.error || '')) {
        setAuthError(uiError(result.error, t('authenticationFailed')));
      }
      setAuthLoading(false);
      return;
    }

    // Phone: Face ID unlocked the login saved in the Keychain — sign in with
    // it exactly as if it had been typed (so a 2FA code still applies). This
    // works after a sign-out too, unlike the token exchange below.
    if (result.username && result.password) {
      setAuthLoading(false);
      await loginWith(result.username, result.password, true);
      return;
    }

    // Browser (WebAuthn): biometric proof verified — exchange existing token for a fresh session (no password used)
    const storedToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/biometric-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
      },
    });
const data = await response.json();

if (data.success && data.investor) {
  // The renewed session — without this the old token kept being used and ran out.
  if (data.token) localStorage.setItem('authToken', data.token);
  localStorage.setItem('investorId', data.investor.id);
  localStorage.setItem('investorEmail', data.investor.email);
  localStorage.setItem('investor', JSON.stringify(data.investor));
  setCurrentInvestor(data.investor);
  setIsAdmin(data.isAdmin === true || isAdminAccount(data.investor?.email));
  setIsAuthenticated(true);

  // CRM sync
  try {
    const crmResponse = await fetch(`${API_URL}/crm/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ odooId: data.investor.id, email: data.investor.email })
    });
    const crmData = await crmResponse.json();
    if (crmData.success && crmData.data) {
      setCurrentInvestor(prev => {
        const updated = {
          ...prev!,
          leadOwner: crmData.data.leadOwner,
          executiveAssistant: crmData.data.executiveAssistant,
          closeLeadId: crmData.data.leadId,
          vipStatus: crmData.data.vipStatus || prev?.vipStatus || null,
          crmSyncedAt: new Date().toISOString()
        };
        localStorage.setItem('investor', JSON.stringify(updated));
        return updated;
      });
      if (crmData.data.vipStatus) {
        setVipStatus(crmData.data.vipStatus);
      }
    }
  } catch (crmError) {
    console.error('CRM sync failed:', crmError);
  }

  // Reload units/documents/payments
  setTimeout(async () => {
    try {
      const investorData = await makeAPIRequest('getInvestorData', {
        investorId: data.investor.id,
        page: 1,
        pageSize: 50,
        forceFirebase: true
      });
      if (investorData?.data) {
        setUnits(investorData.data.units || []);
        setDocuments(investorData.data.documents || []);
        setPayments(investorData.data.payments || []);
        if (investorData.data.investor?.vipStatus) {
          setVipStatus(investorData.data.investor.vipStatus);
          setCurrentInvestor(prev => {
            const updated = { ...prev!, vipStatus: investorData.data.investor.vipStatus };
            localStorage.setItem('investor', JSON.stringify(updated));
            return updated;
          });
        }
      }
    } catch (e) { console.error('Data reload failed:', e); }
  }, 500);

} else {
  setAuthError(t('passwordMayHaveChanged'));
}
    // Use stored credentials to login

  } catch (error: any) {
    console.error('Biometric login error:', error);
    setAuthError(uiError(error.message, t('authenticationFailed')));
  }
  
  setAuthLoading(false);
};

const cancelFaceLogin = () => {
  if (streamRef.current) {
    streamRef.current.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }
  setShowFaceAuth(false);
  setAuthError('');
};

const authenticateWithFace = async () => {
  // This is now handled by handleFaceLogin using native biometrics
  // Kept for backward compatibility
  await handleFaceLogin();
  
  setAuthLoading(false);
};

  const handleTwoFactorVerification = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setAuthError(t('ipEnter6DigitCode'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken: tempAuthData.tempToken, code: twoFactorCode }),
      });
      const data = await response.json();

      if (data.success && data.investor) {
        // A password sign-in that needed the code: offer / refresh Face ID too.
        if (pendingPasswordRef.current) {
          await afterPasswordLogin(data.investor.email, pendingPasswordRef.current);
          pendingPasswordRef.current = '';
        }
        if (data.token) localStorage.setItem('authToken', data.token);
        localStorage.setItem('investorId', data.investor.id);
        localStorage.setItem('investorEmail', data.investor.email);
        localStorage.setItem('investor', JSON.stringify(data.investor));
        setCurrentInvestor(data.investor);
        setIsAdmin(data.isAdmin === true || isAdminAccount(data.investor?.email));
        setIsAuthenticated(true);
        setTempAuthData(null);
      } else {
        setAuthError(uiError(data.error, t('ipInvalidVerificationCodeRetry')));
      }
    } catch (error) {
      setAuthError(t('ipVerificationFailed'));
    }
    
    setAuthLoading(false);
  };

  const handleSetup2FA = async () => {
  setAuthLoading(true);
  try {
    const token = localStorage.getItem('authToken');
    const resp = await fetch(`${API_URL}/2fa/setup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const data = await resp.json();
    if (!data.success) throw new Error(data.error || 'Setup failed');

    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.qrUrl)}`;
    setTwoFactorSecret(data.secret);
    setTwoFactorQR(qrCodeUrl);
    setBackupCodes(data.backupCodes);
    setShowBackupCodes(true);
  } catch (error) {
    setAuthError(t('ipFailedSetup2fa'));
  }
  setAuthLoading(false);
};

  const handleEnable2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setAuthError(t('ipEnter6DigitFromAuthenticator'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
    const token = localStorage.getItem('authToken');
    const resp = await fetch(`${API_URL}/2fa/enable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ code: twoFactorCode }),
    });
    const data = await resp.json();
    if (data.success) {
      setTwoFactorEnabled(true);
      alert(t('ipTwoFaEnabledAlert'));
      setAuthMode('login');
      setIsAuthenticated(true);
    } else {
      setAuthError(uiError(data.error, t('ipInvalidCodeCheckAuthenticator')));
    }
  } catch (error) {
    setAuthError(t('ipFailedEnable2fa'));
  }
    setAuthLoading(false);
  };

  const handleSignup = async () => {
    if (formData.password !== formData.confirmPassword) {
      setAuthError(t('ipPasswordsDoNotMatch'));
      return;
    }
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setAuthError(t('ipFillAllRequiredFields'));
      return;
    }
    if (!termsAccepted) {
      setAuthError(t('ipAcceptTermsToContinue'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phone: formData.phone,
          nationality: formData.nationality,
          birthDate: formData.birthDate,
          pendingDocs: pendingDocsId || undefined,
          // Proof-of-consent — server requires this and stores it on the investor record.
          // Field name `gdpr_consent_at` mirrors the equivalent column on DubaiSell's profiles
          // table so the two schemas read the same in audit / support contexts.
          gdpr_consent_at: new Date().toISOString(),
          termsVersion: POLICY_VERSION,
          privacyVersion: POLICY_VERSION,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAuthError(uiError(data.error, t('ipSignupFailed')));
      } else {
        // Show verification notice — don't log in until email is verified
        setAuthError('');
        setAuthSuccess(pendingDocsId
          ? t('ipAccountCreatedPendingDocs')
          : t('ipAccountCreatedVerify'));
        setAuthMode('login');
        setFormData({ name: '', email: '', phone: '', nationality: '', birthDate: '', password: '', confirmPassword: '' });
      }
    } catch (error: any) {
      setAuthError(t('ipSignupFailedConnection'));
    }
    setAuthLoading(false);
  };

  const handleUAEPassAuth = async () => {
    setAuthLoading(true);
    setAuthError('');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const uaePassUser: Investor = {
        id: 'uaepass_' + Date.now().toString(),
        name: 'Ahmed Al Mansouri',
        email: 'ahmed.almansouri@emirates.ae',
        phone: '+971501234567',
        nationality: 'UAE',
        birthDate: '1985-03-15',
        password: ''
      };

      await makeAPIRequest('createInvestor', { data: uaePassUser });
      setCurrentInvestor(uaePassUser);
      setIsAuthenticated(true);
    } catch (error: any) {
      setAuthError(t('ipUaePassFailed'));
    }
    
    setAuthLoading(false);
  };


// ==================== FORGOT PASSWORD — REAL API CALLS ====================

const handleForgotPassword = async () => {
    if (!recoveryEmail) {
      setAuthError(t('ipEnterEmailAddress'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail.trim().toLowerCase() })
      });

      const data = await response.json();

      if (data.success) {
        setShowVerificationStep(true);
        setAuthError('');
      } else {
        setAuthError(uiError(data.error, t('ipFailedSendResetCode')));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setAuthError(t('ipNetworkErrorRetry'));
    }

    setAuthLoading(false);
  };


// =============================================================
// FUNCTION 2: handleVerifyCode
// =============================================================
// SEARCH FOR: "const handleVerifyCode = async"
// DELETE the entire function
// PASTE THIS instead:

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setAuthError(t('ipEnterVerificationCode'));
      return;
    }

    if (verificationCode.length !== 6) {
      setAuthError(t('ipEnterFull6DigitCode'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/forgot-password/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recoveryEmail.trim().toLowerCase(),
          otp: verificationCode
        })
      });

      const data = await response.json();

      if (data.success) {
        setResetToken(data.resetToken);
        setShowResetPasswordStep(true);
        setShowVerificationStep(false);
        setAuthError('');
      } else {
        setAuthError(uiError(data.error, t('ipInvalidVerificationCode')));
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setAuthError(t('ipNetworkErrorRetry'));
    }

    setAuthLoading(false);
  };


// =============================================================
// FUNCTION 3: handleResetPassword
// =============================================================
// SEARCH FOR: "const handleResetPassword = async"
// DELETE the entire function
// PASTE THIS instead:

  const handleResetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setAuthError(t('ipFillBothPasswordFields'));
      return;
    }

    if (newPassword.length < 6) {
      setAuthError(t('ipPasswordMin6'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setAuthError(t('ipPasswordsDoNotMatch'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(`${API_URL}/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recoveryEmail.trim().toLowerCase(),
          resetToken: resetToken,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(t('ipPasswordResetSuccess'));

        setAuthMode('login');
        setRecoveryEmail('');
        setVerificationCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowVerificationStep(false);
        setShowResetPasswordStep(false);
        setResetToken('');
        setFormData({ ...formData, email: recoveryEmail, password: '' });
      } else {
        setAuthError(uiError(data.error, t('ipFailedResetPassword')));
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setAuthError(t('ipNetworkErrorRetry'));
    }

    setAuthLoading(false);
  };

  const handleForgotEmail = async () => {
    if (!recoveryPhone || !recoveryNationality) {
      setAuthError(t('ipFillPhoneAndNationality'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const maskedEmail = 'inv****@test.com';
      setRecoveredEmail(maskedEmail);
      alert(`${t('ipRecoveredEmailIs')} ${maskedEmail}\n\n${t('ipRecoveredEmailNote')}`);
    } catch (error) {
      setAuthError(t('ipNoAccountForPhoneNationality'));
    }
    
    setAuthLoading(false);
  };

  // Render 2FA verification screen
  if (authMode === '2fa-verify') {
    return (
      <div className="auth-lux auth-lux-page" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ width: '100%', maxWidth: '450px' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img 
              src="/asset-desk-wordmark.png" 
              alt="Asset Desk"
              className="auth-lux-logo"
            />
            <div className="auth-lux-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          </div>
          
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.09)',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)',
            padding: '1.75rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: '0.5rem',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {t('ipTwoFactorAuthTitle')}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem'
            }}>
              {t('ipEnter6DigitCodeLabel')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem',
                marginBottom: '1rem',
                padding: '0.25rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '0.5rem'
              }}>
                <button
                  onClick={() => setVerificationMethod('authenticator')}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: verificationMethod === 'authenticator' ? '#ffffff' : 'transparent',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: verificationMethod === 'authenticator' ? '#000000' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('ipAuthenticatorApp')}
                </button>
                <button
                  onClick={() => setVerificationMethod('backup')}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: verificationMethod === 'backup' ? '#ffffff' : 'transparent',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: verificationMethod === 'backup' ? '#000000' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t('ipBackupCode')}
                </button>
              </div>

              <div>
                <Label htmlFor="2fa-code" style={{ color: '#000000', fontWeight: '600' }}>
                  {verificationMethod === 'authenticator' ? t('ipAuthenticationCode') : t('ipBackupCode')}
                </Label>
                <Input
                  id="2fa-code"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={verificationMethod === 'authenticator' ? '000000' : t('ipEnterBackupCode')}
                  maxLength={verificationMethod === 'authenticator' ? 6 : 10}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.875rem',
                    fontSize: '1.5rem',
                    textAlign: 'center',
                    letterSpacing: '0.5rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontFamily: 'monospace'
                  }}
                />
                {verificationMethod === 'authenticator' && (
                  <p style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    marginTop: '0.5rem'
                  }}>
                    {t('ipOpenAuthenticatorApp')}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="trust-device"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  style={{ width: '1rem', height: '1rem' }}
                />
                <Label htmlFor="trust-device" style={{ 
                  fontSize: '0.875rem', 
                  color: '#6b7280',
                  marginBottom: 0,
                  cursor: 'pointer'
                }}>
                  {t('ipTrustDevice30Days')}
                </Label>
              </div>

              {authError && (
                <div style={{
                  padding: '0.875rem',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  color: '#dc2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {authError}
                </div>
              )}

              <Button 
                onClick={handleTwoFactorVerification}
                disabled={authLoading}
                style={{
                  width: '100%',
                  background: '#000000',
                  color: '#ffffff',
                  padding: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {authLoading ? t('ipVerifying') : t('ipVerifyAndSignIn')}
              </Button>

              <button
                onClick={() => {
                  setAuthMode('login');
                  setTwoFactorCode('');
                  setTempAuthData(null);
                  setAuthError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#B99A4C',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
              >
                ← {t('ipBackToLogin')}
              </button>

              <div style={{
                textAlign: 'center',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginBottom: '0.5rem'
                }}>
                  {t('ipLostAuthenticatorAccess')}
                </p>
                <button
  onClick={() => {
    setAuthError(t('ipContactSupportRecovery'));
  }}
                  style={{
                    color: '#B99A4C',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  {t('ipContactSupport')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render 2FA setup screen
  if (authMode === '2fa-setup') {
    return (
      <div className="auth-lux auth-lux-page" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ width: '100%', maxWidth: '500px' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img 
              src="/asset-desk-wordmark.png" 
              alt="Asset Desk"
              className="auth-lux-logo"
            />
            <div className="auth-lux-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          </div>
          
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.09)',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)',
            padding: '1.75rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: '0.5rem',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {t('ipSetUpTwoFactorAuth')}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem'
            }}>
              {t('ipEnhanceSecurityWith2fa')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {!showBackupCodes ? (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <Button 
                    onClick={handleSetup2FA}
                    disabled={authLoading}
                    style={{
                      background: '#000000',
                      color: '#ffffff',
                      padding: '1rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: authLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {authLoading ? t('ipGenerating') : t('ipGenerateQrCode')}
                  </Button>
                </div>
              ) : (
                <>
                  <div style={{ 
                    backgroundColor: '#f9fafb', 
                    padding: '1.5rem', 
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#000000',
                      marginBottom: '1rem'
                    }}>
                      {t('ipStep1ScanQr')}
                    </h3>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      marginBottom: '1rem'
                    }}>
                      {t('scanQrCode')}
                    </p>
                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                      <img 
                        src={twoFactorQR} 
                        alt="2FA QR Code"
                        style={{ 
                          border: '2px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          padding: '0.5rem',
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                    <div style={{
                      backgroundColor: '#ffffff',
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: '0.5rem'
                      }}>
                        {t('cantScanEnterManually')}
                      </p>
                      <code style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        color: '#000000',
                        fontWeight: '600',
                        letterSpacing: '0.1rem',
                        wordBreak: 'break-all'
                      }}>
                        {twoFactorSecret}
                      </code>
                    </div>
                  </div>

                  <div style={{ 
                    backgroundColor: '#fff7ed', 
                    padding: '1.5rem', 
                    borderRadius: '0.5rem',
                    border: '1px solid #fed7aa'
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#c2410c',
                      marginBottom: '1rem'
                    }}>
                      {t('step2SaveBackupCodes')}
                    </h3>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#ea580c',
                      marginBottom: '1rem'
                    }}>
                      {t('saveBackupCodesWarning')}
                    </p>
                    <div style={{
                      backgroundColor: '#ffffff',
                      padding: '1rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #fed7aa'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}>
                        {backupCodes.map((code, index) => (
                          <div key={index} style={{ color: '#000000' }}>
                            {index + 1}. {code}
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const codesText = backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');
                        navigator.clipboard.writeText(codesText);
                        alert(t('ipBackupCodesCopied'));
                      }}
                      style={{
                        marginTop: '0.75rem',
                        width: '100%',
                        padding: '0.5rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #fed7aa',
                        borderRadius: '0.375rem',
                        color: '#ea580c',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {t('copyBackupCodes')}
                    </button>
                  </div>

                  <div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#000000',
                      marginBottom: '0.5rem'
                    }}>
                      {t('step3VerifySetup')}
                    </h3>
                    <Label htmlFor="verify-2fa" style={{ color: '#000000', fontWeight: '600' }}>
                      {t('enterCodeFromAuthenticator')}
                    </Label>
                    <Input
                      id="verify-2fa"
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.875rem',
                        fontSize: '1.5rem',
                        textAlign: 'center',
                        letterSpacing: '0.5rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>

                  {authError && (
                    <div style={{
                      padding: '0.875rem',
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '0.5rem',
                      color: '#dc2626',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}>
                      {authError}
                    </div>
                  )}

                  <Button 
                    onClick={handleEnable2FA}
                    disabled={authLoading || twoFactorCode.length !== 6}
                    style={{
                      width: '100%',
                      background: '#000000',
                      color: '#ffffff',
                      padding: '1rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: authLoading || twoFactorCode.length !== 6 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: twoFactorCode.length !== 6 ? 0.5 : 1
                    }}
                  >
                    {authLoading ? t('ipEnabling') : t('ipEnable2fa')}
                  </Button>

                 <button
  onClick={() => {
    showConfirmation(
      t('ipSkip2faConfirm'),
      () => {
        setAuthMode('login');
        setIsAuthenticated(true);
      }
    );
  }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {t('ipSkipForNow')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render SMS login screens
  if (authMode === 'sms-login' || authMode === 'sms-verify') {
    return (
      <div className="auth-lux auth-lux-page" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#ffffff',
          border: '1px solid rgba(0,0,0,0.09)',
          borderRadius: '3px',
          padding: '1.75rem',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}><Smartphone size={40} /></div>
            <h2 style={{ color: '#1a1a2e', margin: '0 0 0.25rem', fontSize: '1.25rem', fontWeight: 700 }}>
              {authMode === 'sms-login' ? t('ipSignInWithWhatsApp') : t('ipEnterVerificationCodeTitle')}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
              {authMode === 'sms-login'
                ? t('ipEnterRegisteredPhoneWhatsApp')
                : `${t('ipWeSentWhatsAppCodeTo')} ${smsPhone}`}
            </p>
          </div>

          {authError && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              color: '#dc2626',
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }}>
              {authError}
            </div>
          )}

          {authMode === 'sms-login' ? (
            <>
              <input
                type="tel"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                placeholder="+971 50 xxx xxxx"
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  marginBottom: '1rem',
                  boxSizing: 'border-box'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSmsLoginSend()}
              />
              <button
                onClick={handleSmsLoginSend}
                disabled={smsSending || !smsPhone.trim()}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'linear-gradient(135deg, #D6B160, #c4a555)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: smsSending ? 'not-allowed' : 'pointer',
                  marginBottom: '1rem'
                }}
              >
                {smsSending ? t('ipSending') : t('ipSendCode')}
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={smsOtp}
                onChange={(e) => setSmsOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('ipEnter6DigitCodePlaceholder')}
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  fontSize: '1.5rem',
                  letterSpacing: '8px',
                  textAlign: 'center',
                  outline: 'none',
                  marginBottom: '1rem',
                  boxSizing: 'border-box',
                  fontFamily: "'Courier New', monospace"
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSmsLoginVerify()}
                autoFocus
              />
              <button
                onClick={handleSmsLoginVerify}
                disabled={authLoading || smsOtp.length < 6}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'linear-gradient(135deg, #D6B160, #c4a555)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '0.75rem'
                }}
              >
                {authLoading ? t('ipVerifying') : t('ipVerifyAndSignIn')}
              </button>
              <button
                onClick={() => { handleSmsLoginSend(); setSmsOtp(''); }}
                disabled={smsSending}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'transparent',
                  color: '#B99A4C',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '0.5rem'
                }}
              >
                {smsSending ? t('ipSending') : t('ipResendCode')}
              </button>
            </>
          )}

          <button
            onClick={() => { setAuthMode('login'); setAuthError(''); setSmsPhone(''); setSmsOtp(''); }}
            style={{
              width: '100%',
              padding: '0.5rem',
              background: 'transparent',
              color: '#6b7280',
              border: 'none',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            ← {t('ipBackToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Render forgot password screen
  if (authMode === 'forgot-password') {
    return (
      <div className="auth-lux auth-lux-page" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ width: '100%', maxWidth: '450px' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img 
              src="/asset-desk-wordmark.png" 
              alt="Asset Desk"
              className="auth-lux-logo"
            />
            <div className="auth-lux-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          </div>
          
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.09)',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)',
            padding: '1.75rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: '0.5rem',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {t('ipResetPassword')}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem'
            }}>
              {showResetPasswordStep
                ? t('ipEnterYourNewPassword')
                : showVerificationStep
                ? t('ipEnterCodeSentToEmail')
                : t('ipEnterEmailForResetCode')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {!showVerificationStep && !showResetPasswordStep && (
                <div>
                  <Label htmlFor="recovery-email" style={{ color: '#000000', fontWeight: '600' }}>
                    {t('emailAddress')}
                  </Label>
                  <Input
                    id="recovery-email"
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder={t('ipEnterRegisteredEmail')}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.875rem',
                      fontSize: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem'
                    }}
                  />
                </div>
              )}

              {showVerificationStep && (
                <div>
                  <Label htmlFor="verification-code" style={{ color: '#000000', fontWeight: '600' }}>
                    {t('ipVerificationCodeLabel')}
                  </Label>
                  <Input
                    id="verification-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder={t('ipEnter6DigitCodePlaceholder')}
                    maxLength={6}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.875rem',
                      fontSize: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <p style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    marginTop: '0.5rem'
                  }}>
                    {t('ipCheckEmailForCode')}
                  </p>
                  <button
  onClick={async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await fetch(`${API_URL}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail.trim().toLowerCase() })
      });
      const data = await response.json();
      if (data.success) {
        setAuthError('');
        alert(t('ipNewCodeSent'));
      } else {
        setAuthError(uiError(data.error, t('ipFailedResendCode')));
      }
    } catch (error) {
      setAuthError(t('ipNetworkError'));
    }
    setAuthLoading(false);
  }}
  disabled={authLoading}
  style={{
    background: 'none',
    border: 'none',
    color: '#B99A4C',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    marginTop: '0.25rem'
  }}
>
  {t('ipDidntReceiveResend')}
</button>
                </div>
              )}

              {showResetPasswordStep && (
  <>
    <div>
      <Label htmlFor="new-password" style={{ color: '#000000', fontWeight: '600' }}>
        {t('newPassword')}
      </Label>
      <div style={{ position: 'relative' }}>
        <Input
          id="new-password"
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t('enterNewPassword')}
          style={{
            marginTop: '0.5rem',
            padding: '0.875rem',
            paddingRight: '3rem',
            fontSize: '0.875rem',
            border: '2px solid #e5e7eb',
            borderRadius: '0.5rem',
            width: '100%'
          }}
        />
        <button
          type="button"
          onClick={() => setShowNewPassword(!showNewPassword)}
          style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            marginTop: '0.25rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            color: '#6b7280'
          }}
        >
          {showNewPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
        </button>
      </div>
    </div>

    <div>
      <Label htmlFor="confirm-new-password" style={{ color: '#000000', fontWeight: '600' }}>
        {t('ipConfirmNewPassword')}
      </Label>
      <div style={{ position: 'relative' }}>
        <Input
          id="confirm-new-password"
          type={showConfirmNewPassword ? 'text' : 'password'}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          placeholder={t('ipConfirmNewPasswordPlaceholder')}
          style={{
            marginTop: '0.5rem',
            padding: '0.875rem',
            paddingRight: '3rem',
            fontSize: '0.875rem',
            border: '2px solid #e5e7eb',
            borderRadius: '0.5rem',
            width: '100%'
          }}
        />
        <button
          type="button"
          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
          style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            marginTop: '0.25rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            color: '#6b7280'
          }}
        >
          {showConfirmNewPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
        </button>
      </div>
    </div>
  </>
)}


              {authError && (
                <div style={{
                  padding: '0.875rem',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  color: '#dc2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {authError}
                </div>
              )}

              <Button 
  type="button"
  onClick={
    showResetPasswordStep 
      ? handleResetPassword 
      : showVerificationStep 
      ? handleVerifyCode 
      : handleForgotPassword
  }
  disabled={authLoading}
                
                style={{
                  width: '100%',
                  background: '#000000',
                  color: '#ffffff',
                  padding: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {authLoading ? t('ipProcessing') :
                  showResetPasswordStep ? t('ipResetPassword') :
                  showVerificationStep ? t('ipVerifyCode') :
                  t('ipSendResetCode')
                }
              </Button>

              <button
                onClick={() => {
                  setAuthMode('login');
                  setRecoveryEmail('');
                  setVerificationCode('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                  setShowVerificationStep(false);
                  setShowResetPasswordStep(false);
                   setResetToken(''); 
                  setAuthError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#B99A4C',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
              >
                ← {t('ipBackToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render forgot email screen
  if (authMode === 'forgot-email') {
    return (
      <div className="auth-lux auth-lux-page" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ width: '100%', maxWidth: '450px' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img 
              src="/asset-desk-wordmark.png" 
              alt="Asset Desk"
              className="auth-lux-logo"
            />
            <div className="auth-lux-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
          </div>
          
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.09)',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)',
            padding: '1.75rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: '0.5rem',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {t('ipRecoverEmailAddress')}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem'
            }}>
              {t('ipRecoverEmailDesc')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <Label htmlFor="recovery-phone" style={{ color: '#000000', fontWeight: '600' }}>
                  {t('phoneNumber')}
                </Label>
                <Input
                  id="recovery-phone"
                  type="tel"
                  value={recoveryPhone}
                  onChange={(e) => setRecoveryPhone(e.target.value)}
                  placeholder={t('ipEnterRegisteredPhone')}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.875rem',
                    fontSize: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem'
                  }}
                />
              </div>

              <div>
                <Label htmlFor="recovery-nationality" style={{ color: '#000000', fontWeight: '600' }}>
                  {t('nationality')}
                </Label>
                <Select 
                  value={recoveryNationality} 
                  onValueChange={(value) => setRecoveryNationality(value)}
                  style={{ 
                    marginTop: '0.5rem',
                    padding: '0.875rem',
                    fontSize: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem'
                  }}
                >
                  <SelectItem value="">{t('ipSelectNationality')}</SelectItem>
                  <SelectItem value="UAE">United Arab Emirates</SelectItem>
                  <SelectItem value="USA">United States</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                  <SelectItem value="Germany">Germany</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                </Select>
              </div>

              {recoveredEmail && (
                <div style={{
                  padding: '0.875rem',
                  backgroundColor: '#dbeafe',
                  border: '1px solid #60a5fa',
                  borderRadius: '0.5rem',
                  color: '#1e40af',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {t('ipYourEmailAddress')}: {recoveredEmail}
                </div>
              )}

              {authError && (
                <div style={{
                  padding: '0.875rem',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  color: '#dc2626',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {authError}
                </div>
              )}

              <Button 
                onClick={handleForgotEmail}
                disabled={authLoading}
                style={{
                  width: '100%',
                  background: '#000000',
                  color: '#ffffff',
                  padding: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {authLoading ? t('ipSearching') : t('ipFindMyEmail')}
              </Button>

              <button
                onClick={() => {
                  setAuthMode('login');
                  setRecoveryPhone('');
                  setRecoveryNationality('');
                  setRecoveredEmail('');
                  setAuthError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#B99A4C',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
              >
                ← {t('ipBackToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main login/signup form
  return (
    <div className="auth-lux" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      /* Keeps the sheet clear of the hairline frame at inset 14px */
      padding: '2.5rem 2rem',
      position: 'relative'
    }}>
      <div className="auth-lux-frame" aria-hidden="true" />

      {/* Back to role selection (Owner / Tenant / Guest) */}
      {onChangeRole && (
        <button
          onClick={onChangeRole}
          className="auth-lux-back"
          style={{
            position: 'absolute',
            top: 'calc(30px + env(safe-area-inset-top, 0px))',
            left: 32,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            zIndex: 5
          }}
        >
          <ArrowLeft size={13} strokeWidth={2} /> {t('back')}
        </button>
      )}

       {/* Face Authentication Modal */}
    {showFaceAuth && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}>
        <div className="auth-lux-modal" style={{
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0,0,0,0.09)',
          borderRadius: '3px',
          padding: '1.75rem',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 30px 60px -25px rgba(0, 0, 0, 0.7)'
        }}>
          <div className="auth-lux-eyebrow" style={{ textAlign: 'center' }}>{t('ipBiometricVerification')}</div>
          <h3 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: '30px',
            fontWeight: 600,
            lineHeight: 1.08,
            color: '#000000',
            margin: '9px 0 0',
            textAlign: 'center'
          }}>
            {t('ipFaceAuthentication')}
          </h3>

          <p style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '16px',
            color: '#6f6960',
            margin: '9px 0 22px',
            textAlign: 'center'
          }}>
            {t('ipPositionFaceCentre')}
          </p>

          <div style={{
            position: 'relative',
            backgroundColor: '#000000',
            borderRadius: '2px',
            overflow: 'hidden',
            marginBottom: '1.5rem'
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            
            {/* Face outline guide */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '250px',
              border: '3px solid #D6B160',
              borderRadius: '50%',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
              pointerEvents: 'none'
            }} />
          </div>

          {authError && (
            <div style={{
              padding: '0.875rem',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              color: '#dc2626',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '1rem'
            }}>
              {authError}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '0.75rem'
          }}>
            <button
              onClick={cancelFaceLogin}
              disabled={authLoading}
              className="auth-lux-modalbtn"
              style={{
                flex: 1,
                backgroundColor: '#ffffff',
                color: '#4a453d',
                padding: '0.95rem',
                fontSize: '10.5px',
                fontWeight: '600',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                border: '1px solid rgba(0,0,0,0.14)',
                borderRadius: '2px',
                cursor: authLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {t('cancel')}
            </button>
            <button
              onClick={authenticateWithFace}
              disabled={authLoading}
              className="auth-lux-primary"
              style={{
                flex: 1,
                background: '#000000',
                color: '#ffffff',
                padding: '0.95rem',
                fontSize: '10.5px',
                fontWeight: '600',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                border: 'none',
                borderRadius: '2px',
                cursor: authLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {authLoading ? t('ipAuthenticating') : t('ipAuthenticate')}
            </button>
          </div>

          <p style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: '#a9a297',
            marginTop: '1.1rem',
            textAlign: 'center'
          }}>
            {t('ipFaceDataEncryptedSecure')}
          </p>
        </div>
      </div>
    )}

      <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center' }} className="auth-logo-container">
          {/* Transparent PNG straight onto the paper — no plate. */}
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="auth-lux-logo" />
        </div>

        <div className="auth-lux-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }} className="auth-welcome-text">
          <div className="auth-lux-eyebrow">{t('ipPrivateClientAccess')}</div>
          <h1 className="auth-lux-title">
            {authMode === 'login' ? t('welcomeBack') : t('ipJoinInvestMenter')}
          </h1>
          <p className="auth-lux-sub">
            {authMode === 'login'
              ? t('ipAccessPremiumPortfolio')
              : t('ipCreatePremiumAccount')
            }
          </p>
        </div>

        <div className="auth-lux-panel" style={{
          background: '#ffffff',
          border: '1px solid rgba(0,0,0,0.09)',
          borderRadius: '3px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 18px 40px -28px rgba(30,24,10,0.30)',
          padding: '1.75rem'
        }}>
          {/* Who you are, asked on the sign-in itself rather than on a screen
              before it. Login only: switching account type halfway through a
              sign-up would throw away what has been typed. */}
          {onSwitchRole && authMode === 'login' && (
            <RoleSwitch value="owner" onChange={onSwitchRole} />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>
            {authMode === 'signup' && (
              <>
                <div>
                  <Label htmlFor="name" style={{ color: '#000000', fontWeight: '600' }}>{t('fullName')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={t('ipEnterFullName')}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.875rem',
                      fontSize: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem'
                    }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <Label htmlFor="nationality" style={{ color: '#000000', fontWeight: '600' }}>{t('nationality')} <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '0.85rem', fontStyle: 'italic' }}>{t('ipOptionalParen')}</span></Label>
                    <Select 
                      value={formData.nationality} 
                      onValueChange={(value) => handleInputChange('nationality', value)}
                      style={{ marginTop: '0.5rem' }}
                    >
                      <SelectItem value="">{t('ipSelectNationality')}</SelectItem>
                      <SelectItem value="Afghanistan">Afghanistan</SelectItem>
<SelectItem value="Albania">Albania</SelectItem>
<SelectItem value="Algeria">Algeria</SelectItem>
<SelectItem value="Andorra">Andorra</SelectItem>
<SelectItem value="Angola">Angola</SelectItem>
<SelectItem value="Antigua and Barbuda">Antigua and Barbuda</SelectItem>
<SelectItem value="Argentina">Argentina</SelectItem>
<SelectItem value="Armenia">Armenia</SelectItem>
<SelectItem value="Australia">Australia</SelectItem>
<SelectItem value="Austria">Austria</SelectItem>
<SelectItem value="Azerbaijan">Azerbaijan</SelectItem>
<SelectItem value="Bahamas">Bahamas</SelectItem>
<SelectItem value="Bahrain">Bahrain</SelectItem>
<SelectItem value="Bangladesh">Bangladesh</SelectItem>
<SelectItem value="Barbados">Barbados</SelectItem>
<SelectItem value="Belarus">Belarus</SelectItem>
<SelectItem value="Belgium">Belgium</SelectItem>
<SelectItem value="Belize">Belize</SelectItem>
<SelectItem value="Benin">Benin</SelectItem>
<SelectItem value="Bhutan">Bhutan</SelectItem>
<SelectItem value="Bolivia">Bolivia</SelectItem>
<SelectItem value="Bosnia and Herzegovina">Bosnia and Herzegovina</SelectItem>
<SelectItem value="Botswana">Botswana</SelectItem>
<SelectItem value="Brazil">Brazil</SelectItem>
<SelectItem value="Brunei">Brunei</SelectItem>
<SelectItem value="Bulgaria">Bulgaria</SelectItem>
<SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
<SelectItem value="Burundi">Burundi</SelectItem>
<SelectItem value="Cabo Verde">Cabo Verde</SelectItem>
<SelectItem value="Cambodia">Cambodia</SelectItem>
<SelectItem value="Cameroon">Cameroon</SelectItem>
<SelectItem value="Canada">Canada</SelectItem>
<SelectItem value="Central African Republic">Central African Republic</SelectItem>
<SelectItem value="Chad">Chad</SelectItem>
<SelectItem value="Chile">Chile</SelectItem>
<SelectItem value="China">China</SelectItem>
<SelectItem value="Colombia">Colombia</SelectItem>
<SelectItem value="Comoros">Comoros</SelectItem>
<SelectItem value="Congo">Congo</SelectItem>
<SelectItem value="Costa Rica">Costa Rica</SelectItem>
<SelectItem value="Croatia">Croatia</SelectItem>
<SelectItem value="Cuba">Cuba</SelectItem>
<SelectItem value="Cyprus">Cyprus</SelectItem>
<SelectItem value="Czech Republic">Czech Republic</SelectItem>
<SelectItem value="Democratic Republic of the Congo">Democratic Republic of the Congo</SelectItem>
<SelectItem value="Denmark">Denmark</SelectItem>
<SelectItem value="Djibouti">Djibouti</SelectItem>
<SelectItem value="Dominica">Dominica</SelectItem>
<SelectItem value="Dominican Republic">Dominican Republic</SelectItem>
<SelectItem value="Ecuador">Ecuador</SelectItem>
<SelectItem value="Egypt">Egypt</SelectItem>
<SelectItem value="El Salvador">El Salvador</SelectItem>
<SelectItem value="Equatorial Guinea">Equatorial Guinea</SelectItem>
<SelectItem value="Eritrea">Eritrea</SelectItem>
<SelectItem value="Estonia">Estonia</SelectItem>
<SelectItem value="Eswatini">Eswatini</SelectItem>
<SelectItem value="Ethiopia">Ethiopia</SelectItem>
<SelectItem value="Fiji">Fiji</SelectItem>
<SelectItem value="Finland">Finland</SelectItem>
<SelectItem value="France">France</SelectItem>
<SelectItem value="Gabon">Gabon</SelectItem>
<SelectItem value="Gambia">Gambia</SelectItem>
<SelectItem value="Georgia">Georgia</SelectItem>
<SelectItem value="Germany">Germany</SelectItem>
<SelectItem value="Ghana">Ghana</SelectItem>
<SelectItem value="Greece">Greece</SelectItem>
<SelectItem value="Grenada">Grenada</SelectItem>
<SelectItem value="Guatemala">Guatemala</SelectItem>
<SelectItem value="Guinea">Guinea</SelectItem>
<SelectItem value="Guinea-Bissau">Guinea-Bissau</SelectItem>
<SelectItem value="Guyana">Guyana</SelectItem>
<SelectItem value="Haiti">Haiti</SelectItem>
<SelectItem value="Honduras">Honduras</SelectItem>
<SelectItem value="Hungary">Hungary</SelectItem>
<SelectItem value="Iceland">Iceland</SelectItem>
<SelectItem value="India">India</SelectItem>
<SelectItem value="Indonesia">Indonesia</SelectItem>
<SelectItem value="Iran">Iran</SelectItem>
<SelectItem value="Iraq">Iraq</SelectItem>
<SelectItem value="Ireland">Ireland</SelectItem>
<SelectItem value="Israel">Israel</SelectItem>
<SelectItem value="Italy">Italy</SelectItem>
<SelectItem value="Ivory Coast">Ivory Coast</SelectItem>
<SelectItem value="Jamaica">Jamaica</SelectItem>
<SelectItem value="Japan">Japan</SelectItem>
<SelectItem value="Jordan">Jordan</SelectItem>
<SelectItem value="Kazakhstan">Kazakhstan</SelectItem>
<SelectItem value="Kenya">Kenya</SelectItem>
<SelectItem value="Kiribati">Kiribati</SelectItem>
<SelectItem value="Kuwait">Kuwait</SelectItem>
<SelectItem value="Kyrgyzstan">Kyrgyzstan</SelectItem>
<SelectItem value="Laos">Laos</SelectItem>
<SelectItem value="Latvia">Latvia</SelectItem>
<SelectItem value="Lebanon">Lebanon</SelectItem>
<SelectItem value="Lesotho">Lesotho</SelectItem>
<SelectItem value="Liberia">Liberia</SelectItem>
<SelectItem value="Libya">Libya</SelectItem>
<SelectItem value="Liechtenstein">Liechtenstein</SelectItem>
<SelectItem value="Lithuania">Lithuania</SelectItem>
<SelectItem value="Luxembourg">Luxembourg</SelectItem>
<SelectItem value="Madagascar">Madagascar</SelectItem>
<SelectItem value="Malawi">Malawi</SelectItem>
<SelectItem value="Malaysia">Malaysia</SelectItem>
<SelectItem value="Maldives">Maldives</SelectItem>
<SelectItem value="Mali">Mali</SelectItem>
<SelectItem value="Malta">Malta</SelectItem>
<SelectItem value="Marshall Islands">Marshall Islands</SelectItem>
<SelectItem value="Mauritania">Mauritania</SelectItem>
<SelectItem value="Mauritius">Mauritius</SelectItem>
<SelectItem value="Mexico">Mexico</SelectItem>
<SelectItem value="Micronesia">Micronesia</SelectItem>
<SelectItem value="Moldova">Moldova</SelectItem>
<SelectItem value="Monaco">Monaco</SelectItem>
<SelectItem value="Mongolia">Mongolia</SelectItem>
<SelectItem value="Montenegro">Montenegro</SelectItem>
<SelectItem value="Morocco">Morocco</SelectItem>
<SelectItem value="Mozambique">Mozambique</SelectItem>
<SelectItem value="Myanmar">Myanmar</SelectItem>
<SelectItem value="Namibia">Namibia</SelectItem>
<SelectItem value="Nauru">Nauru</SelectItem>
<SelectItem value="Nepal">Nepal</SelectItem>
<SelectItem value="Netherlands">Netherlands</SelectItem>
<SelectItem value="New Zealand">New Zealand</SelectItem>
<SelectItem value="Nicaragua">Nicaragua</SelectItem>
<SelectItem value="Niger">Niger</SelectItem>
<SelectItem value="Nigeria">Nigeria</SelectItem>
<SelectItem value="North Korea">North Korea</SelectItem>
<SelectItem value="North Macedonia">North Macedonia</SelectItem>
<SelectItem value="Norway">Norway</SelectItem>
<SelectItem value="Oman">Oman</SelectItem>
<SelectItem value="Pakistan">Pakistan</SelectItem>
<SelectItem value="Palau">Palau</SelectItem>
<SelectItem value="Palestine">Palestine</SelectItem>
<SelectItem value="Panama">Panama</SelectItem>
<SelectItem value="Papua New Guinea">Papua New Guinea</SelectItem>
<SelectItem value="Paraguay">Paraguay</SelectItem>
<SelectItem value="Peru">Peru</SelectItem>
<SelectItem value="Philippines">Philippines</SelectItem>
<SelectItem value="Poland">Poland</SelectItem>
<SelectItem value="Portugal">Portugal</SelectItem>
<SelectItem value="Qatar">Qatar</SelectItem>
<SelectItem value="Romania">Romania</SelectItem>
<SelectItem value="Russia">Russia</SelectItem>
<SelectItem value="Rwanda">Rwanda</SelectItem>
<SelectItem value="Saint Kitts and Nevis">Saint Kitts and Nevis</SelectItem>
<SelectItem value="Saint Lucia">Saint Lucia</SelectItem>
<SelectItem value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</SelectItem>
<SelectItem value="Samoa">Samoa</SelectItem>
<SelectItem value="San Marino">San Marino</SelectItem>
<SelectItem value="Sao Tome and Principe">Sao Tome and Principe</SelectItem>
<SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
<SelectItem value="Senegal">Senegal</SelectItem>
<SelectItem value="Serbia">Serbia</SelectItem>
<SelectItem value="Seychelles">Seychelles</SelectItem>
<SelectItem value="Sierra Leone">Sierra Leone</SelectItem>
<SelectItem value="Singapore">Singapore</SelectItem>
<SelectItem value="Slovakia">Slovakia</SelectItem>
<SelectItem value="Slovenia">Slovenia</SelectItem>
<SelectItem value="Solomon Islands">Solomon Islands</SelectItem>
<SelectItem value="Somalia">Somalia</SelectItem>
<SelectItem value="South Africa">South Africa</SelectItem>
<SelectItem value="South Korea">South Korea</SelectItem>
<SelectItem value="South Sudan">South Sudan</SelectItem>
<SelectItem value="Spain">Spain</SelectItem>
<SelectItem value="Sri Lanka">Sri Lanka</SelectItem>
<SelectItem value="Sudan">Sudan</SelectItem>
<SelectItem value="Suriname">Suriname</SelectItem>
<SelectItem value="Sweden">Sweden</SelectItem>
<SelectItem value="Switzerland">Switzerland</SelectItem>
<SelectItem value="Syria">Syria</SelectItem>
<SelectItem value="Taiwan">Taiwan</SelectItem>
<SelectItem value="Tajikistan">Tajikistan</SelectItem>
<SelectItem value="Tanzania">Tanzania</SelectItem>
<SelectItem value="Thailand">Thailand</SelectItem>
<SelectItem value="Timor-Leste">Timor-Leste</SelectItem>
<SelectItem value="Togo">Togo</SelectItem>
<SelectItem value="Tonga">Tonga</SelectItem>
<SelectItem value="Trinidad and Tobago">Trinidad and Tobago</SelectItem>
<SelectItem value="Tunisia">Tunisia</SelectItem>
<SelectItem value="Turkey">Turkey</SelectItem>
<SelectItem value="Turkmenistan">Turkmenistan</SelectItem>
<SelectItem value="Tuvalu">Tuvalu</SelectItem>
<SelectItem value="Uganda">Uganda</SelectItem>
<SelectItem value="Ukraine">Ukraine</SelectItem>
<SelectItem value="United Arab Emirates">United Arab Emirates</SelectItem>
<SelectItem value="United Kingdom">United Kingdom</SelectItem>
<SelectItem value="United States">United States</SelectItem>
<SelectItem value="Uruguay">Uruguay</SelectItem>
<SelectItem value="Uzbekistan">Uzbekistan</SelectItem>
<SelectItem value="Vanuatu">Vanuatu</SelectItem>
<SelectItem value="Vatican City">Vatican City</SelectItem>
<SelectItem value="Venezuela">Venezuela</SelectItem>
<SelectItem value="Vietnam">Vietnam</SelectItem>
<SelectItem value="Yemen">Yemen</SelectItem>
<SelectItem value="Zambia">Zambia</SelectItem>
<SelectItem value="Zimbabwe">Zimbabwe</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="birthDate" style={{ color: '#000000', fontWeight: '600' }}>{t('dateOfBirth')} <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '0.85rem', fontStyle: 'italic' }}>{t('ipOptionalParen')}</span></Label>
                    <Input
                      id="birthDate"
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => handleInputChange('birthDate', e.target.value)}
                      style={{ marginTop: '0.5rem' }}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Label htmlFor="email" style={{ color: '#000000', fontWeight: '600' }}>
                  {authMode === 'login' ? t('ipUsername') : t('emailAddress')}
                </Label>
                {authMode === 'login' && (
                  <button
                    onClick={() => setAuthMode('forgot-email')}
                    style={{
                      background: 'none',
                      border: 'none',
                      /* Darker gold: #D6B160 at 11px on white fails contrast */
                      color: '#B99A4C',
                      fontSize: '0.6875rem',
                      fontWeight: '600',
                      letterSpacing: '0.01em',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      padding: 0,
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {t('ipForgotEmail')}
                  </button>
                )}
              </div>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('ipEnterYourEmail')}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.875rem',
                  fontSize: '0.875rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem'
                }}
              />
            </div>

            {authMode === 'signup' && (
              <div>
                <Label htmlFor="phone" style={{ color: '#000000', fontWeight: '600' }}>{t('phoneNumber')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('ipEnterYourPhone')}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.875rem',
                    fontSize: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem'
                  }}
                />
              </div>
            )}
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Label htmlFor="password" style={{ color: '#000000', fontWeight: '600' }}>{t('ipPassword')}</Label>
                {authMode === 'login' && (
                  <button
                    onClick={() => setAuthMode('forgot-password')}
                    style={{
                      background: 'none',
                      border: 'none',
                      /* Darker gold: #D6B160 at 11px on white fails contrast */
                      color: '#B99A4C',
                      fontSize: '0.6875rem',
                      fontWeight: '600',
                      letterSpacing: '0.01em',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      padding: 0,
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {t('ipForgotPassword')}
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={t('ipEnterYourPassword')}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.875rem',
                    paddingRight: '3rem',
                    fontSize: '0.875rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    width: '100%'
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#D6B160';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#a9a297';
                  }}
                  aria-label={showPassword ? t('ipHidePassword') : t('ipShowPassword')}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    marginTop: '0.25rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#a9a297',
                    transition: 'color 0.2s ease'
                  }}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
            </div>

            {authMode === 'signup' && (
              <div style={{ position: 'relative' }}>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="••••••••••••"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.875rem',
                      paddingRight: '3rem',
                      fontSize: '0.875rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      width: '100%'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginTop: '0.25rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6b7280'
                    }}
                    aria-label={showConfirmPassword ? t('ipHidePassword') : t('ipShowPassword')}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
            )}
            
            {/* 🔒 Account Locked Banner */}
            {accountLocked && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '0.5rem',
                color: '#856404',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}><Lock size={24} /></div>
                <div style={{ fontWeight: '700', marginBottom: '0.25rem' }}>{t('ipAccountLocked')}</div>
                <div style={{ marginBottom: '0.75rem' }}>
                  {t('ipAccountLockedDesc')}
                </div>
                <button
                  onClick={() => { setAuthMode('forgot-password'); setAccountLocked(false); }}
                  style={{
                    background: '#D6B160',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.375rem',
                    padding: '0.5rem 1.25rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  {t('ipResetPasswordToUnlock')} →
                </button>
              </div>
            )}

            {/* ✅ Success Message (e.g. after registration) */}
            {authSuccess && (
              <div style={{
                padding: '0.875rem',
                backgroundColor: '#dcfce7',
                border: '1px solid #bbf7d0',
                borderRadius: '0.5rem',
                color: '#166534',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {authSuccess}
              </div>
            )}

            {/* ❌ Wrong Password Error with Attempt Counter */}
            {authError && !accountLocked && (
              <div style={{
                padding: '0.875rem',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                color: '#dc2626',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {authError}
                {attemptsRemaining !== null && attemptsRemaining <= 2 && (
                  <div style={{ marginTop: '0.375rem', fontSize: '0.8rem', color: '#b91c1c', fontWeight: '600' }}>
                    <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {t('ipWarningPrefix')} {attemptsRemaining === 1 ? t('ipOneAttempt') : `${attemptsRemaining} ${t('ipAttemptsPlural')}`} {t('ipBeforeLockoutSuffix')}
                  </div>
                )}
              </div>
            )}
            
            {/* Terms & Privacy consent checkbox — required for signup (GDPR Art. 7, App Store policy) */}
            {authMode === 'signup' && (
              <label
                htmlFor="terms-accept"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  backgroundColor: termsAccepted ? 'rgba(214,177,96,0.08)' : '#fbfaf7',
                  border: `1px solid ${termsAccepted ? 'rgba(214,177,96,0.6)' : 'rgba(0,0,0,0.10)'}`,
                  borderRadius: '2px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <input
                  id="terms-accept"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{
                    width: '22px',
                    height: '22px',
                    marginTop: '2px',
                    flexShrink: 0,
                    accentColor: '#D6B160',
                    cursor: 'pointer'
                  }}
                  aria-describedby="terms-accept-text"
                />
                <span
                  id="terms-accept-text"
                  style={{
                    fontSize: '0.8125rem',
                    color: '#4a453d',
                    lineHeight: '1.5'
                  }}
                >
                  {t('ipAgeAndAgree')}{' '}
                  <a
                    href={TERMS_URL}
                    onClick={openPolicy(TERMS_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {t('ipTermsOfService')}
                  </a>
                  {' '}{t('ipAnd')}{' '}
                  <a
                    href={PRIVACY_URL}
                    onClick={openPolicy(PRIVACY_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {t('ipPrivacyPolicy')}
                  </a>
                  .
                </span>
              </label>
            )}

            <Button
              onClick={authMode === 'login' ? handleLogin : handleSignup}
              disabled={authLoading || (authMode === 'signup' && !termsAccepted)}
              className="auth-lux-primary"
              style={{
                width: '100%',
                /* Ink bar; gold is kept for accents and the hover state. */
                background: (authMode === 'signup' && !termsAccepted) ? '#dcd7cd' : '#000000',
                color: (authMode === 'signup' && !termsAccepted) ? '#8d867b' : '#ffffff',
                padding: '1.05rem',
                fontSize: '0.6875rem',
                fontWeight: '600',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                border: 'none',
                borderRadius: '2px',
                marginTop: '0.35rem',
                cursor: (authLoading || (authMode === 'signup' && !termsAccepted)) ? 'not-allowed' : 'pointer'
              }}
            >
              {authLoading ? t('loading') : (authMode === 'login' ? t('ipSignIn') : t('ipCreateAccount'))}
            </Button>

            {/* Alternative sign-in methods — deliberately demoted to a pair
                of text links so they read as secondary to the ink CTA. */}
            {authMode === 'login' && (
              <div className="auth-lux-alt">
                <button
                  onClick={handleFaceLogin}
                  disabled={authLoading}
                  title={t('ipSignInWithFaceRecognition')}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 20a6 6 0 0 0-12 0"></path>
                    <circle cx="12" cy="10" r="4"></circle>
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                  <span>Face ID</span>
                </button>

                <button onClick={() => { setAuthMode('sms-login'); setAuthError(''); }}>
                  <Phone size={14} />
                  <span>WhatsApp</span>
                </button>
              </div>
            )}

 {/* TEMPORARILY HIDDEN - OR Divider for UAE PASS
            <div style={{ position: 'relative', textAlign: 'center', margin: '1rem 0' }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#e5e7eb'
              }}></div>
              <span style={{
                position: 'relative',
                backgroundColor: '#ffffff',
                padding: '0 1rem',
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>OR</span>
            </div>

            <Button 
              onClick={handleUAEPassAuth}
              disabled={authLoading}
              style={{
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '1rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem'
              }}
            >
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Continue with</span>
              <span style={{
                backgroundColor: '#2563eb',
                color: '#ffffff',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '700'
              }}>
                UAE PASS
              </span>
            </Button>
            */}

            <div className="auth-lux-switchmode" style={{
              textAlign: 'center',
              paddingTop: '1.1rem',
              marginTop: '0.2rem',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
              <button
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'signup' : 'login');
                  setAuthError('');
                  setAuthSuccess('');
                  setFormData({ name: '', email: '', phone: '', nationality: '', birthDate: '', password: '', confirmPassword: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#B99A4C',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
              >
                {authMode === 'login'
                  ? t('ipNoAccountSignUp')
                  : t('ipHaveAccountSignIn')
                }
              </button>
            </div>
          </div>
        </div>

        <div className="auth-lux-footer" style={{
          textAlign: 'center',
          marginTop: '1.75rem',
          color: '#a9a297',
          fontSize: '0.6875rem',
          fontWeight: 500,
          letterSpacing: '0.01em'
        }}>
          <p>{t('footerText')}</p>
        </div>
      </div>
{showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#000000',
              marginBottom: '1rem',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {t('ipConfirmAction')}
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem',
              lineHeight: '1.5'
            }}>
              {confirmMessage}
            </p>
            <div style={{
              display: 'flex',
              gap: '0.75rem'
            }}>
              <Button
                onClick={handleConfirmNo}
                style={{
                  flex: 1,
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('ipNo')}
              </Button>
              <Button
                onClick={handleConfirmYes}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #D6B160 0%, #c4a555 100%)',
                  color: '#ffffff',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('ipYes')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  // File handling
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  
const processDocument = async (file: File, type: string, category?: string, unitId?: string) => {
   if (uploadingDocument) {
    return;
  }
// Line 3790 (now 3793)
  
  setUploadingDocument(true);
  
  try {
    // ⭐⭐⭐ CRITICAL FIX #1: CHECK FOR DUPLICATES BEFORE UPLOADING ⭐⭐⭐
    // ========================================================================
    if (category === 'Unit Documents' && unitId && type) {
      
      // Find any existing document of the same type in the same unit
      const existingDocOfSameType = documents.find(doc => {
        // Must match the unit
        if (doc.unitId !== unitId) return false;
        
        // Get document type from any possible field
        const docType = doc.documentType || doc.type || doc.extractedData?.type;
        
        // Compare types (case-insensitive)
        const normalizedDocType = docType?.toString().toUpperCase().trim();
        const normalizedUploadType = type.toString().toUpperCase().trim();
        
        const isMatch = normalizedDocType === normalizedUploadType;
        
        if (isMatch) {
        }
        
        return isMatch;
      });
      
      // If duplicate found, STOP and show error
      if (existingDocOfSameType) {
        const uploadDate = new Date(existingDocOfSameType.uploadDate).toLocaleDateString('en-AE', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Show error alert
        alert(
          `${t('ipDuplicateDetected')}\n\n` +
          `${t('ipDuplicateCannotUploadA')} ${type} ${t('ipDuplicateCannotUploadB')}\n\n` +
          `${t('ipDocumentLabel')} "${existingDocOfSameType.name}"\n` +
          `${t('ipUploadedOnLabel')} ${uploadDate}\n\n` +
          `${t('ipDuplicateTipA')} ${type} ${t('ipDuplicateTipB')}`
        );
        
        setUploadingDocument(false);
        return; // ⭐ EXIT IMMEDIATELY - Don't proceed with upload
      }
      
    }
    // ========================================================================
    // END OF DUPLICATE CHECK
    
    // Convert file to base64
    const base64File = await fileToBase64(file);
    
    // Normalize category name
    let backendCategory = category || 'Personal Documents';
    if (category === 'Units' || category === 'Unit Documents') {
      backendCategory = 'Unit Documents';
    }
    
    // Prepare document data
    const documentData = {
      id: Date.now().toString(),
      investorId: currentInvestor!.id,
      name: file.name,
      type: type,
      category: backendCategory,
      unitId: unitId || null,
      fileData: base64File,
      fileSize: file.size,
      fileType: file.type
    };
    
    
    // ⭐⭐⭐ CRITICAL FIX #2: PROPER ERROR HANDLING ⭐⭐⭐
    // ========================================================================
    let backendResponse;
    
    try {
      backendResponse = await makeAPIRequest('createDocumentWithCategory', { data: documentData });
      
    } catch (backendError: any) {
      console.error('❌ Backend error:', backendError);
      
      // Check if it's a duplicate error from backend
      if (backendError.message?.includes('already exists') || 
          backendError.message?.includes('duplicate')) {
        alert(
          `${t('ipUploadFailedTitle')}\n\n` +
          `${backendError.message}\n\n` +
          `${t('ipDeleteExistingFirst')}`
        );
      } else {
        alert(
          `${t('ipUploadFailedTitle')}\n\n` +
          `${t('ipErrorLabel')} ${uiError(backendError.message, t('ipUnknownErrorOccurred'))}\n\n` +
          `${t('ipTryAgainOrContactSupport')}`
        );
      }
      
      setUploadingDocument(false);
      return; // Exit on error
    }
    // ========================================================================
    
    // Create processed document object
    const processedDocument: Document = {
      id: backendResponse.id || documentData.id,
      investorId: documentData.investorId,
      name: backendResponse.fileName || documentData.name,
      type: backendResponse.documentType || backendResponse.type || documentData.type,
      documentType: backendResponse.documentType || backendResponse.type || documentData.type,
      category: documentData.category,
      unitId: documentData.unitId,
      uploadDate: backendResponse.uploadDate || new Date().toISOString(),
       status: backendResponse.status || backendResponse.data?.status || 'Processed',
      verificationStatus: backendResponse.data?.verificationStatus || backendResponse.verificationStatus || undefined,
      verificationNote: backendResponse.data?.verificationNote || undefined,
      extractedData: backendResponse.extractedInfo || backendResponse.extractedData,
      fileUrl: backendResponse.fileUrl,
      fileData: base64File,
      fileSize: documentData.fileSize,
      fileType: documentData.fileType
    };

    // Update documents array (remove any temporary entry, add new one)
    const updatedDocuments = [...documents.filter(doc => doc.id !== documentData.id), processedDocument];
    setDocuments(prev => {
  const exists = prev.some(doc => 
    doc.id === processedDocument.id || 
    (doc.name === processedDocument.name && doc.unitId === processedDocument.unitId)
  );
  
  if (exists) {
    return prev;
  }
  
  return [...prev, processedDocument];
});
    
    
    // ⭐⭐⭐ CRITICAL FIX #3: SUCCESS FEEDBACK ⭐⭐⭐
    // ========================================================================
    // Show success message
   const isUnitDoc = backendCategory === 'Unit Documents' && 
      (type === 'OTP' || type === 'OQOOD' || type === 'SPA');
    
    if (isUnitDoc) {
      alert(
        `${t('ipUploadSuccessTitle')}\n\n` +
        `${t('ipDocumentLabel')} ${file.name}\n` +
        `${t('ipTypeLabel')} ${type}\n` +
        `${t('ipCategoryLabel')} ${backendCategory}\n\n` +
        `${t('ipAiExtractedData')}\n` +
        `${t('ipTeamWillVerify')}\n` +
        `${t('ipStatusChangeOnceVerified')}`
      );
    } else {
      alert(
        `${t('ipUploadSuccessTitle')}\n\n` +
        `${t('ipDocumentLabel')} ${file.name}\n` +
        `${t('ipTypeLabel')} ${type}\n` +
        `${t('ipCategoryLabel')} ${backendCategory}\n\n` +
        `${t('ipDocUploadedProcessed')}`
      );
    }
    // ========================================================================
    
    // NOW PROCESS UNIT UPDATES (if applicable)
    const docType = processedDocument.extractedData?.type || type;

      if ((docType === 'OTP' || docType === 'OQOOD' || docType === 'SPA') && 
        processedDocument.extractedData && category === 'Unit Documents') {
      
      // ✅ NEW: Don't update unit data immediately — let admin verify first
      // Just link the document to the unit
      if (unitId) {
        processedDocument.unitId = unitId;
        setDocuments(prev => prev.map(doc => 
          doc.id === processedDocument.id 
            ? { ...doc, unitId, verificationStatus: 'pending_verification' } 
            : doc
        ));
      } else {
        // Try to match by property identifiers for auto-linking
        const docData = processedDocument.extractedData;
        const propertyIds = [
          docData.property, docData.unitNumber, docData.propertyName
        ].filter(Boolean).map(id => String(id).toLowerCase().trim());
        
        const projectIds = [
          docData.project, docData.developer, docData.projectName
        ].filter(Boolean).map(id => String(id).toLowerCase().trim());
        
        const matchedUnit = units.find(u => {
          if (u.investorId !== currentInvestor!.id) return false;
          const nameMatch = propertyIds.some(id => 
            u.name?.toLowerCase().includes(id) || u.unitNumber?.toLowerCase().includes(id)
          );
          const projectMatch = projectIds.some(id => u.project?.toLowerCase().includes(id));
          return nameMatch && projectMatch;
        });
        
        if (matchedUnit) {
          processedDocument.unitId = matchedUnit.id;
          setDocuments(prev => prev.map(doc => 
            doc.id === processedDocument.id 
              ? { ...doc, unitId: matchedUnit.id, verificationStatus: 'pending_verification' } 
              : doc
          ));
        } else {
        }
      }
    }
      // FIXED: For SOA documents, try to match with existing units but don't create portfolio units
      if (category === 'Units' && type === 'SOA Document' && processedDocument.extractedData) {
        const soaData = processedDocument.extractedData;
        
        const matchingUnit = units.find(unit => 
          soaData.unitNumber && unit.unitNumber === soaData.unitNumber ||
          soaData.unitDetails && unit.name.includes(soaData.unitDetails) ||
          soaData.project && unit.project === soaData.project
        );
        
        if (matchingUnit) {
          processedDocument.unitId = matchingUnit.id;
          setDocuments(prev => 
            prev.map(doc => doc.id === processedDocument.id ? 
              { ...doc, unitId: matchingUnit.id } : doc
            )
          );
        } else if (soaData.unitNumber || soaData.unitDetails) {
          const soaUnit: Unit = {
            id: Date.now().toString() + '_soa_unit',
            investorId: currentInvestor!.id,
            name: soaData.unitDetails || soaData.unitNumber || `SOA Unit from ${file.name}`,
            unitNumber: soaData.unitNumber || 'N/A',
            project: soaData.project || 'Unknown Project',
            type: 'Unknown',
            area: '0',
            currentValue: 0,
            purchaseValue: 0,
            monthlyRental: 0,
            occupancyStatus: 'Vacant',
            location: 'Dubai, UAE'
          };
          
          setUnits(prev => [...prev, soaUnit]);
          processedDocument.unitId = soaUnit.id;
          await makeAPIRequest('createUnitWithForceFolder', { data: soaUnit });
        }
      }

    } catch (error: any) {
      console.error('Document processing failed:', error);

         alert(
      `${t('ipProcessingErrorTitle')}\n\n` +
      `${t('ipProcessingErrorBody')}\n\n` +
      `${uiError(error.message, t('ipUnknownError'))}\n\n` +
      `${t('ipTryAgainOrContactSupportShort')}`
    );
    
      const errorDocument: Document = {
        id: Date.now().toString(),
        investorId: currentInvestor!.id,
        name: file.name,
        type: type,
        category: category || 'Personal Documents',
        unitId: unitId || null,
        uploadDate: new Date().toISOString(),
        status: 'Error',
        fileData: await fileToBase64(file),
        extractedData: null
      };
      
      setDocuments(prev => [...prev, errorDocument]);
    } finally {
      setUploadingDocument(false);
    }
  };
  
  // ============================================
// ✅ FIXED: Download Document Function
// ============================================
const downloadDocument = useCallback(async (doc: Document): Promise<void> => {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) { alert(t('ipLoginToDownload')); return; }
  const docId = (doc as any).id || (doc as any).documentId;
  const url = `${API_URL.replace('/api', '')}/api/document/${encodeURIComponent(docId)}/download-auth?token=${encodeURIComponent(authToken)}`;

  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url });
    } catch (err) {
      alert(t('ipCouldNotDownload'));
    }
    return;
  }

  const link = globalThis.document.createElement('a');
  link.href = url;
  link.download = (doc as any).fileName || doc.name || 'document.pdf';
  globalThis.document.body.appendChild(link);
  link.click();
  globalThis.document.body.removeChild(link);
  // `t` is part of the closure: it changes identity when the language changes,
  // so it must be a dependency or these alerts freeze in the boot language.
}, [t]);

const viewDocument = useCallback(async (doc: Document): Promise<void> => {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) { alert(t('ipLoginToView')); return; }
  const docId = (doc as any).id || (doc as any).documentId;

  if (Capacitor.isNativePlatform()) {
    const url = `${API_URL.replace('/api', '')}/api/document/${encodeURIComponent(docId)}/view?token=${encodeURIComponent(authToken)}`;
    try {
      await Browser.open({ url });
    } catch (err) {
      alert(t('ipCouldNotOpen'));
    }
    return;
  }

  try {
    const res = await fetch(`${API_URL.replace('/api', '')}/api/document/${encodeURIComponent(docId)}/view`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    });
    if (!res.ok) throw new Error('Failed to load document');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } catch (err) {
    alert(t('ipCouldNotOpen'));
  }
  // `t` must be a dependency — see downloadDocument above.
}, [t]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;




  if (documentCategory === 'Units' && !selectedUnitForDoc) {
    alert(t('ipSelectUnitFirstAlert'));
    return;
  }

  if (file.size > 100 * 1024 * 1024) {
    alert(t('ipFileTooLarge'));
    return;
  }
  
await processDocument(file, documentType, documentCategory, selectedUnitForDoc);

   event.target.value = '';
// ✅ NO RELOAD - Let React state handle updates

};
  const handlePaymentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedUnit && paymentAmount && paymentDate) {
      await processDocument(file, 'Payment Proof');
      setSelectedUnit('');
      setPaymentAmount('');
      setPaymentDate('');
    }
  };

  // ============================================
  // SMART UPLOAD - One button upload for elderly investors
  // AI auto-detects document type, category, routes to correct folder
  // All documents go through admin verification first
  // ============================================
  const [smartUploading, setSmartUploading] = useState(false);
  const [smartUploadProgress, setSmartUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [smartUploadResults, setSmartUploadResults] = useState<any[] | null>(null);

  // unitId: the unit whose page the upload came from — the server files the
  // document under it instead of guessing the unit from the AI read.
  const handleSmartUpload = async (event: React.ChangeEvent<HTMLInputElement>, unitId?: string) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    if (selectedFiles.length > 20) {
      alert(t('ipMax20Files'));
      event.target.value = '';
      return;
    }

    // Check file sizes
    for (let i = 0; i < selectedFiles.length; i++) {
      if (selectedFiles[i].size > 100 * 1024 * 1024) {
        alert(`${t('ipFileTooLargeNamedPre')}${selectedFiles[i].name}${t('ipFileTooLargeNamedPost')}`);
        event.target.value = '';
        return;
      }
    }

    setSmartUploading(true);
    setSmartUploadProgress({ current: 0, total: selectedFiles.length, fileName: t('ipPreparingFiles') });

    try {
      // Convert all files to base64
      const filesData = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setSmartUploadProgress({ current: i + 1, total: selectedFiles.length, fileName: `${t('ipReading')}: ${file.name}` });
        const base64 = await fileToBase64(file);
        filesData.push({
          name: file.name,
          fileData: base64,
          fileType: file.type,
          fileSize: file.size
        });
      }

      setSmartUploadProgress({ current: 0, total: selectedFiles.length, fileName: t('ipAiAnalyzing') });

      // Send all files to backend for AI processing
      const response = await fetch(`${API_URL}/smartUpload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          files: filesData,
          investorId: currentInvestor!.id,
          ...(unitId ? { unitId } : {})
        })
      });

      const result = await response.json();

      if (result.success) {
        // Add successfully uploaded documents to local state
        if (result.results) {
          setSmartUploadResults(result.results);

          // Reload documents from server to get the full records
          if (reloadDataFromFirebase) {
            await reloadDataFromFirebase();
          }
        }

        // Show results summary
        const successDocs = result.results.filter((r: any) => r.success);
        const failedDocs = result.results.filter((r: any) => !r.success);

        let summary = `${successDocs.length} ${t('ipDocsUploadedSuccessfully')}\n\n`;

        if (successDocs.length > 0) {
          summary += `${t('ipDocumentsDetected')}\n`;
          successDocs.forEach((doc: any) => {
            const unitInfo = doc.matchedUnit ? ` -> ${doc.matchedUnit.name}` : ` ${t('ipUnmatchedAdminWillAssign')}`;
            summary += `  ${doc.fileName}\n    ${t('ipTypeLabel')} ${doc.detectedType} | ${t('ipCategoryLabel')} ${doc.detectedCategory}${unitInfo}\n`;
          });
          summary += `\n${t('ipPendingAdminVerification')}`;
        }

        if (failedDocs.length > 0) {
          summary += `\n\n${failedDocs.length} ${t('ipFilesFailed')}\n`;
          failedDocs.forEach((doc: any) => {
            summary += `  ${doc.fileName}: ${doc.error}\n`;
          });
        }

        alert(summary);
      } else {
        alert(`${t('ipUploadFailedLabel')} ${uiError(result.error, t('ipUnknownError'))}`);
      }

    } catch (error: any) {
      console.error('Smart upload error:', error);
      alert(`${t('ipUploadErrorLabel')} ${uiError(error.message, t('ipPleaseTryAgain'))}`);
    } finally {
      setSmartUploading(false);
      setSmartUploadProgress({ current: 0, total: 0, fileName: '' });
      event.target.value = '';
    }
  };

  const createNewUnit = async () => { 
  if (!newUnitForm.name || !newUnitForm.unitNumber || !newUnitForm.project) {
    alert(t('ipFillRequiredUnitFields'));
    return;
  }

  // ✅ PRESERVE LOGIN STATE before any async operations
  const savedInvestorId = localStorage.getItem('investorId');
  const savedInvestorEmail = localStorage.getItem('investorEmail');
  
  if (!savedInvestorId || !currentInvestor) {
    alert(t('ipSessionExpired'));
    return;
  }

  try {
    
    const newUnit: Unit = {
      id: Date.now().toString(),
      investorId: currentInvestor.email,
      name: newUnitForm.name,
      unitNumber: newUnitForm.unitNumber,
      project: newUnitForm.project,
      type: newUnitForm.type || 'Studio',
      area: newUnitForm.area || '0',
      currentValue: 0,
      purchaseValue: 0,
      monthlyRental: 0,
      occupancyStatus: 'Vacant',
      location: 'Dubai, UAE'
    };

    // Call backend to create unit
    const backendUnit = await makeAPIRequest('createUnitWithForceFolder', { data: newUnit });
    
    if (backendUnit) {
      
      // ✅ RESTORE LOGIN STATE (in case it was cleared)
      localStorage.setItem('investorId', savedInvestorId);
      if (savedInvestorEmail) {
        localStorage.setItem('investorEmail', savedInvestorEmail);
      }
      
      // Update units state (add new unit to existing array)
      setUnits(prev => {
        const exists = prev.some(u => u.id === backendUnit.id);
        if (exists) {
          return prev;
        }
        return [...prev, backendUnit];
      });
      
      // Reset form and close dialog
      setNewUnitForm({ name: '', unitNumber: '', project: '', type: '', area: '' });
      setShowAddUnitForm(false);
      
      alert(`${t('ipUnitAddedPre')}${backendUnit.name}${t('ipUnitAddedPost')}`);
      
    } else {
      console.error('❌ Backend did not return unit data');
      alert(t('ipUnitCreationFailed'));
    }
    
  } catch (error) {
    console.error('❌ Error creating unit:', error);
    
    // ✅ RESTORE LOGIN STATE even on error
    if (savedInvestorId) {
      localStorage.setItem('investorId', savedInvestorId);
    }
    if (savedInvestorEmail) {
      localStorage.setItem('investorEmail', savedInvestorEmail);
    }
    
    alert(t('ipFailedToCreateUnit') + ' ' + (error instanceof Error ? error.message : t('ipUnknownError')));
  }

};

// Add this NEW function after createNewUnit
// One changed unit back from a child view (e.g. a sale recorded in
// PropertyView). Merged in place rather than refetched, so the Portfolio
// report and the Properties tabs move to the new state together.
const handleUnitUpdated = (updated: any) => {
  if (!updated?.id) return;
  setUnits(prev => prev.map(u => (u.id === updated.id ? { ...u, ...updated } : u)));
};

const handleUnitCreated = (newUnit: Unit) => {
  
  // Add to units array
  setUnits(prev => {
    const exists = prev.some(u => u.id === newUnit.id);
    if (exists) return prev;
    return [...prev, newUnit];
  });
  
};

  // Chatbot functionality
// Enhanced chatbot functionality for the investor portal
const sendChatMessage = async (message: string) => {
  if (!message.trim()) return;
  
  const userMessage = {
    id: Date.now().toString(),
    text: message,
    isUser: true,
    timestamp: new Date()
  };
  
  setChatMessages(prev => [...prev, userMessage]);
  setChatInput('');
  setIsChatLoading(true);

  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/chatbot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({
        message: message,
        history: chatMessages.slice(-20).map(m => ({ text: m.text, isUser: m.isUser })),
        investorId: currentInvestor?.id || 'guest',
        investorName: currentInvestor?.name || 'App User',
        investorEmail: currentInvestor?.email || ''
      })
    });

    const data = await response.json();
    
    const botMessage = {
      id: (Date.now() + 1).toString(),
      text: data.message || data.response || t('ipAiDefaultGreeting'),
      isUser: false,
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, botMessage]);

  } catch (error) {
    console.error('Chat error:', error);
    
    const errorMessage = {
      id: (Date.now() + 1).toString(),
      text: t('ipAiErrorMessage'),
      isUser: false,
      timestamp: new Date()
      
    };
    
    setChatMessages(prev => [...prev, errorMessage]);
  } finally {
    setIsChatLoading(false);
  }
};

// Enhanced fallback system
const determineAppropriateFallback = (message: string): string => {
  const lowerMessage = message.toLowerCase();
  
  // Dubai real estate fallbacks
  if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('value')) {
    return "Dubai property prices vary significantly by area and property type. Prime locations like Downtown Dubai and Dubai Marina typically command higher prices, while emerging areas offer better value. For current market prices, I recommend checking with local real estate agents or recent sales data. Would you like information about a specific area or property type?";
  }
  
  if (lowerMessage.includes('visa') || lowerMessage.includes('residency') || lowerMessage.includes('golden visa')) {
    return "Dubai offers several visa options through property investment, including the Golden Visa for investments over AED 2 million. Property investments can also qualify for renewable residence visas. The specific requirements depend on the investment amount and property type. Would you like more details about investor visa requirements?";
  }
  
  if (lowerMessage.includes('area') || lowerMessage.includes('location') || lowerMessage.includes('neighborhood')) {
    return "Popular investment areas in Dubai include Downtown Dubai, Dubai Marina, Jumeirah Lake Towers (JLT), Business Bay, Dubai Hills Estate, and emerging areas like Dubai South and Mohammed Bin Rashid City (MBR City). Each offers different advantages for investors in terms of rental yields, capital appreciation, and lifestyle amenities.";
  }
  
  if (lowerMessage.includes('rental') || lowerMessage.includes('yield') || lowerMessage.includes('roi')) {
    return "Rental yields in Dubai typically range from 4-8% annually, depending on location and property type. Areas like Dubai South and International City often offer higher yields, while prime areas like Downtown offer lower yields but better capital appreciation potential. Your current portfolio shows properties that can help track actual performance.";
  }
  
  // General investment fallbacks
  if (lowerMessage.includes('investment') || lowerMessage.includes('portfolio') || lowerMessage.includes('diversif')) {
    return "Investment diversification is crucial for managing risk. While Dubai real estate can be a strong component of your portfolio, consider spreading investments across different asset classes, geographic locations, and property types. Your current Dubai properties are a good foundation - have you considered other markets or investment vehicles?";
  }
  
  if (lowerMessage.includes('tax') || lowerMessage.includes('finance')) {
    return "Tax implications vary greatly depending on your residency and citizenship. Dubai has no personal income tax, but your home country may have tax obligations on foreign property income and capital gains. I recommend consulting with a tax professional familiar with international property investments for personalized advice.";
  }
  
  // General helpful fallback
  return "I'm currently experiencing technical difficulties, but I'm here to help with both Dubai real estate questions and general investment advice. Some popular topics I can assist with include:\n\n• Dubai property market analysis and trends\n• Investment strategies and portfolio management\n• Visa and residency options through property investment\n• Financial planning for real estate investors\n• General business and economic questions\n\nWhat specific area would you like to discuss?";
};

// Optional: Add context-aware suggestions based on user's portfolio
const getPersonalizedSuggestions = (units: Unit[], documents: Document[]) => {
  const suggestions = [];
  
  if (units.length === 0) {
    suggestions.push("Ask about: 'What areas in Dubai are best for first-time investors?'");
  }
  
  if (units.some(unit => unit.occupancyStatus === 'Vacant')) {
    suggestions.push("Ask about: 'How can I improve occupancy rates for my Dubai properties?'");
  }
  
  if (units.length > 0) {
    const totalValue = units.reduce((sum, unit) => sum + unit.currentValue, 0);
    if (totalValue > 500000) {
      suggestions.push("Ask about: 'Dubai Golden Visa requirements for property investors'");
    }
  }
  
  return suggestions;
};

  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chatInput.trim() && !isChatLoading) {
      sendChatMessage(chatInput);
    }
  };

  const ProfileView = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  

  // Face recognition state
  const [showFaceEnrollment, setShowFaceEnrollment] = useState<boolean>(false);
  const [isCapturingFace, setIsCapturingFace] = useState<boolean>(false);
  const [faceAuthEnabled, setFaceAuthEnabled] = useState<boolean>(false);
  const [showProfileNewPassword, setShowProfileNewPassword] = useState<boolean>(false);  // ← ADD THIS
  const faceVideoRef = useRef<HTMLVideoElement>(null);
  const faceCanvasRef = useRef<HTMLCanvasElement>(null);
  const faceStreamRef = useRef<MediaStream | null>(null);
  
  const [showDeleteAccount, setShowDeleteAccount] = useState<boolean>(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState<string>('');
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);

  const [profileForm, setProfileForm] = useState({
    name: currentInvestor?.name || '',
    email: currentInvestor?.email || '',
    phone: currentInvestor?.phone || '',
    nationality: currentInvestor?.nationality || '',
    birthDate: currentInvestor?.birthDate || ''
  });

  // The password card's own result line: the page-level message sits at the
  // top of Profile, out of sight of this form, so a change looked like nothing.
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileUpdate = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      // Email is the account ID and cannot be changed here — send only editable fields
      const result = await makeAPIRequest('update-profile', {
        name: profileForm.name,
        phone: profileForm.phone,
        nationality: profileForm.nationality,
        birthDate: profileForm.birthDate
      });

      const updatedInvestor = {
        ...currentInvestor,
        ...(result?.data || {}),
        name: profileForm.name,
        phone: profileForm.phone,
        nationality: profileForm.nationality,
        birthDate: profileForm.birthDate
      } as Investor;

      setCurrentInvestor(updatedInvestor);
      localStorage.setItem('investor', JSON.stringify(updatedInvestor));
      setIsEditing(false);
      setMessage({ type: 'success', text: t('ipProfileUpdated') });
    } catch (error: any) {
      setMessage({ type: 'error', text: uiError(error?.message, t('ipFailedUpdateProfile')) });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg(null);

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMsg({ type: 'error', text: t('ipFillAllPasswordFields') });
      return;
    }
    // The current password is checked by the server only. The app never holds
    // it, so comparing it here failed every time and nothing was ever sent.
    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: t('ipNewPasswordMin6') });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg({ type: 'error', text: t('ipNewPasswordsDoNotMatch') });
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${API_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 401 && /current password/i.test(data?.error || '')) {
        setPasswordMsg({ type: 'error', text: t('ipCurrentPasswordIncorrect') });
        return;
      }
      if (!resp.ok || !data.success) throw new Error(uiError(data?.error, t('ipFailedChangePassword')));
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMsg({ type: 'success', text: t('ipPasswordChanged') });
    } catch (error: any) {
      setPasswordMsg({ type: 'error', text: uiError(error?.message, t('ipFailedChangePassword')) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setIsDeletingAccount(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${API_URL}/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: currentInvestor?.email }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(uiError(data.error, t('ipFailedDeleteAccount')));
      // A deleted account must not stay behind Face ID on this phone.
      try { await disableBiometric(currentInvestor?.email); } catch { /* ignore */ }
      markSignedOut();
      localStorage.removeItem('authToken');
      localStorage.removeItem('investorEmail');
      setCurrentInvestor(null);
      setIsAuthenticated(false);
      setShowDeleteAccount(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: uiError(error.message, t('ipFailedDeleteAccountRetry')) });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
const [show2FASetup, setShow2FASetup] = useState(false);
const [twoFactorQR, setTwoFactorQR] = useState('');
const [twoFactorSecret, setTwoFactorSecret] = useState('');
const [backupCodes, setBackupCodes] = useState<string[]>([]);
const [showBackupCodes, setShowBackupCodes] = useState(false);
const [verificationCode, setVerificationCode] = useState('');
const [authError, setAuthError] = useState('');
const [authLoading, setAuthLoading] = useState(false);

// Check if 2FA is enabled — read from investor profile (set by backend on login)
useEffect(() => {
  setTwoFactorEnabled(currentInvestor?.twoFactorEnabled === true);
  const isFaceAuthEnabled = localStorage.getItem(`face_auth_enabled_${currentInvestor?.email}`) === 'true';
  setFaceAuthEnabled(isFaceAuthEnabled);
}, [currentInvestor]);

const _getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Handler to start 2FA setup — generates secret server-side
const handleEnable2FA = async () => {
  setAuthLoading(true);
  try {
    const resp = await fetch(`${API_URL}/2fa/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._getAuthHeader() },
    });
    const data = await resp.json();
    if (!data.success) throw new Error(data.error);
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.qrUrl)}`;
    setTwoFactorSecret(data.secret);
    setTwoFactorQR(qrCodeUrl);
    setBackupCodes(data.backupCodes);
    setShow2FASetup(true);
    setShowBackupCodes(false);
  } catch (error) {
    setAuthError(t('ipFailedSetup2fa'));
  }
  setAuthLoading(false);
};

// Handler to verify and enable 2FA
const handleVerifyAndEnable2FA = async () => {
  if (!verificationCode || verificationCode.length !== 6) {
    setAuthError(t('ipEnter6DigitFromAuthenticator'));
    return;
  }
  setAuthLoading(true);
  setAuthError('');
  try {
    const resp = await fetch(`${API_URL}/2fa/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._getAuthHeader() },
      body: JSON.stringify({ code: verificationCode }),
    });
    const data = await resp.json();
    if (data.success) {
      setTwoFactorEnabled(true);
      setShow2FASetup(false);
      setShowBackupCodes(false);
      setVerificationCode('');
      alert(t('ipTwoFaEnabledAlert'));
    } else {
      setAuthError(uiError(data.error, t('ipInvalidCodeCheckAuthenticator')));
    }
  } catch (error) {
    setAuthError(t('ipFailedEnable2fa'));
  }
  setAuthLoading(false);
};

// Handler to disable 2FA
const handleDisable2FA = async () => {
  const code = window.prompt(t('ipEnterCodeToDisable2fa'));
  if (!code) return;
  try {
    const resp = await fetch(`${API_URL}/2fa/disable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._getAuthHeader() },
      body: JSON.stringify({ code }),
    });
    const data = await resp.json();
    if (data.success) {
      setTwoFactorEnabled(false);
      setShow2FASetup(false);
      alert(t('ipTwoFaDisabledAlert'));
    } else {
      alert(uiError(data.error, t('ipFailedDisable2faCheckCode')));
    }
  } catch {
    alert(t('ipFailedDisable2fa'));
  }
};

// Handler to view backup codes — codes shown at setup time only; stored server-side
const handleViewBackupCodes = () => {
  alert(t('ipBackupCodesShownOnce'));
};

// Handler to regenerate backup codes
const handleRegenerateBackupCodes = async () => {
  if (!window.confirm(t('ipRegenerateBackupCodesConfirm'))) return;
  try {
    const resp = await fetch(`${API_URL}/2fa/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._getAuthHeader() },
    });
    const data = await resp.json();
    if (data.success) {
      setBackupCodes(data.backupCodes);
      setShowBackupCodes(true);
      alert(t('ipNewBackupCodesGenerated'));
    }
  } catch {
    alert(t('ipFailedRegenerateBackupCodes'));
  }
};

// ==================== BIOMETRIC AUTHENTICATION ====================

const [biometryType, setBiometryType] = useState<string>('Biometric');

const startFaceEnrollment = async () => {
  try {
    // Check if biometric is available
    const availability = await checkBiometricAvailability();
    
    if (!availability.success) {
      alert(uiError(availability.error, t('biometricNotAvailable')));
      return;
    }
    
    setBiometryType(availability.biometryType || 'Biometric');

    // Need password to store credentials securely
    const password = prompt(t('enterPasswordToEnable'));
    if (!password) return;

    // Verify password first with backend
    try {
      const verifyResult = await makeAPIRequest('login', {
        email: currentInvestor?.email,
        password: password
      });

      if (!verifyResult || verifyResult.error) {
        alert(t('incorrectPassword'));
        return;
      }

      // Enroll biometric with credentials
      const result = await enrollBiometric(currentInvestor?.email || '', password);
      
      if (result.success) {
        setFaceAuthEnabled(true);
        setIsFaceAuthEnabled(true);
        localStorage.setItem(`face_auth_enabled_${currentInvestor?.email}`, 'true');
        alert(t('biometricLoginEnabled'));
      } else {
        alert(uiError(result.error, t('authenticationFailed')));
      }
    } catch (error: any) {
      console.error('Password verification failed:', error);
      alert(t('incorrectPassword'));
    }
  } catch (error: any) {
    console.error('Biometric enrollment error:', error);
    alert(t('biometricNotAvailable'));
  }
};

const stopFaceEnrollment = () => {
  // No longer needed for native biometric, but kept for compatibility
  if (faceStreamRef.current) {
    faceStreamRef.current.getTracks().forEach(track => track.stop());
    faceStreamRef.current = null;
  }
  setShowFaceEnrollment(false);
  setIsCapturingFace(false);
};

const captureFaceData = async () => {
  // This is now handled by startFaceEnrollment using native biometrics
  // Kept for backward compatibility if needed
};

const handleDisableFaceAuth = async () => {
  if (!window.confirm(t('disableBiometricLogin') + '?')) {
    return;
  }

  try {
    const result = await disableBiometric();
    
    if (result.success) {
      localStorage.removeItem(`face_auth_enabled_${currentInvestor?.email}`);
      localStorage.removeItem(`face_data_${currentInvestor?.email}`);
      setFaceAuthEnabled(false);
      setIsFaceAuthEnabled(false);
      alert(t('biometricLoginDisabled'));
    } else {
      alert(uiError(result.error, t('authenticationFailed')));
    }
  } catch (error) {
    console.error('Error disabling biometric:', error);
    alert(t('authenticationFailed'));
  }
};

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <p className="font-medium">{message.text}</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('profileInformation')}
              </CardTitle>
              <CardDescription>{t('managePersonalInfo')}</CardDescription>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {t('editProfile')}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="profile-name">{t('fullName')}</Label>
              <Input
                id="profile-name"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>

            <div>
              <Label htmlFor="profile-email">{t('emailAddress')} <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '0.85rem', fontStyle: 'italic' }}>{t('ipCannotBeChanged')}</span></Label>
              <Input
                id="profile-email"
                type="email"
                value={profileForm.email}
                disabled
                className="bg-gray-50 text-gray-500"
              />
            </div>

            <div>
              <Label htmlFor="profile-phone">{t('phoneNumber')}</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>

            <div>
              <Label htmlFor="profile-nationality">{t('nationality')} <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '0.85rem', fontStyle: 'italic' }}>{t('ipOptionalParen')}</span></Label>
              <Input
                id="profile-nationality"
                value={profileForm.nationality}
                onChange={(e) => setProfileForm({ ...profileForm, nationality: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>

            <div>
              <Label htmlFor="profile-birthdate">{t('dateOfBirth')} <span style={{ color: '#6b7280', fontWeight: '400', fontSize: '0.85rem', fontStyle: 'italic' }}>{t('ipOptionalParen')}</span></Label>
              <Input
                id="profile-birthdate"
                type="date"
                value={profileForm.birthDate}
                onChange={(e) => setProfileForm({ ...profileForm, birthDate: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>

            <div>
              <Label>{t('accountId')}</Label>
              <Input
                value={currentInvestor?.id || ''}
                disabled
                className="bg-gray-50 text-gray-500"
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleProfileUpdate}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSaving ? t('ipSaving') : t('ipSaveChanges')}
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setProfileForm({
                    name: currentInvestor?.name || '',
                    email: currentInvestor?.email || '',
                    phone: currentInvestor?.phone || '',
                    nationality: currentInvestor?.nationality || '',
                    birthDate: currentInvestor?.birthDate || ''
                  });
                }}
                variant="outline"
              >
                {t('cancel')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">{t('changePassword')}</CardTitle>
          <CardDescription>{t('updateAccountPassword')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="current-password">{t('currentPassword')}</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder={t('enterCurrentPassword')}
              />
            </div>
<div>
  <Label htmlFor="new-password" style={{ color: '#000000', fontWeight: '600' }}>
    {t('newPassword')}
  </Label>
  <div style={{ position: 'relative' }}>
    <Input
      id="new-password"
      type={showProfileNewPassword ? 'text' : 'password'}
      value={passwordForm.newPassword}
      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
      placeholder={t('enterNewPassword')}
      style={{
        marginTop: '0.5rem',
        padding: '0.875rem',
        paddingRight: '3rem',
        fontSize: '0.875rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        width: '100%'
      }}
    />
    <button
      type="button"
      onClick={() => setShowProfileNewPassword(!showProfileNewPassword)}
      style={{
        position: 'absolute',
        right: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        marginTop: '0.25rem',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        color: '#6b7280'
      }}
    >
      {showProfileNewPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
    </button>
  </div>
</div>
<div>
  <Label htmlFor="confirm-password" style={{ color: '#000000', fontWeight: '600' }}>
    {t('confirmPassword') || 'Confirm Password'}
  </Label>
  <div style={{ position: 'relative' }}>
    <Input
      id="confirm-password"
      type="password"
      value={passwordForm.confirmPassword}
      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
      placeholder={t('ipConfirmNewPasswordPlaceholder')}
      style={{
        marginTop: '0.5rem',
        padding: '0.875rem',
        fontSize: '0.875rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        width: '100%'
      }}
    />
  </div>
</div>
</div>

          {passwordMsg && (
            <div
              role={passwordMsg.type === 'error' ? 'alert' : 'status'}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '12px 14px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                background: passwordMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                color: passwordMsg.type === 'success' ? '#166534' : '#b91c1c',
                border: `1px solid ${passwordMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              }}
            >
              {passwordMsg.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              {passwordMsg.text}
            </div>
          )}
          <div className="pt-2">
            <button
              type="button"
              onClick={handlePasswordChange}
              disabled={isSaving}
              style={{
                padding: '12px 22px', borderRadius: '10px', border: 'none',
                background: '#000', color: '#fff', fontSize: '15px', fontWeight: 700,
                cursor: isSaving ? 'default' : 'pointer', opacity: isSaving ? 0.6 : 1,
              }}
            >
              {isSaving ? t('ipUpdating') : t('ipUpdatePassword')}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">{t('ipAccountStatistics')}</CardTitle>
          <CardDescription>{t('ipAccountActivitySummary')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Four figures, one system. These were a blue tile, a green tile, a
              purple tile and an orange tile — four hues on a screen whose
              brand guide allows gold, white and black. Colour was carrying no
              information here: the numbers are not categories, and tinting
              each one differently only made them harder to compare. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, background: M.hair, border: `1px solid ${M.hair}`, borderRadius: 12, overflow: 'hidden' }}>
            <StatCell label={t('totalProperties')} value={String(units.length)} />
            <StatCell label={t('documents')} value={String(documents.length)} />
            <StatCell
              /* pmPortfolioValue, not portfolioValue: the latter is the
                 all-caps 'PORTFOLIO VALUE' used as a badge elsewhere, and it
                 shouted next to three sentence-case labels. */
              label={t('pmPortfolioValue')}
              value={`AED ${(totalPortfolioValue || 0).toLocaleString('en-AE')}`}
            />
            <StatCell
              label={t('accountAge')}
              value={`${currentInvestor?.createdAt
                ? Math.floor((Date.now() - new Date(currentInvestor.createdAt).getTime()) / (1000 * 60 * 60 * 24))
                : 0} ${t('days')}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* ===== PERSONAL DOCUMENTS =====
          Passport, visa, Emirates ID. These belong to the person, not to a
          property, so they live on the profile rather than behind a tab in the
          Properties screen — which is where they used to sit, as one of four
          top tabs on a screen about buildings. Same filter as before, so
          nothing that was visible there is hidden now. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">{t('personalDocuments')}</CardTitle>
          <CardDescription>{t('ipPersonalDocsSub')}</CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const personalDocs = documents.filter((d: any) =>
              d.category === 'Personal Documents' ||
              ['Passport', 'Visa', 'ID'].includes(d.documentType) ||
              ['Passport', 'Visa', 'ID'].includes(d.type));

            return (
              <>
                {!demoMode && (
                  <label
                    htmlFor="smart-upload-profile"
                    className="mb-4"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '13px 16px', borderRadius: 14,
                      background: smartUploading ? '#b8942e' : 'linear-gradient(135deg, #D6B160, #b8942e)',
                      color: '#fff', fontSize: 14, fontWeight: 700,
                      cursor: smartUploading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {smartUploading
                      ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /><span>{smartUploadProgress?.fileName || t('ipProcessing')}</span></>
                      : <><Upload size={18} /> {t('smartUploadBtn')}</>}
                    <input
                      id="smart-upload-profile"
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSmartUpload}
                      disabled={smartUploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}

                {personalDocs.length === 0 ? (
                  <div className="text-center py-8 px-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
                    <FileText size={36} className="mx-auto mb-3 text-gray-400" />
                    <p className="text-sm font-semibold text-gray-700 mb-1">{t('noPersonalDocs')}</p>
                    <p className="text-xs text-gray-500">{t('uploadPassportVisa')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {personalDocs.map((doc: any) => (
                      <div key={doc.id} className="bg-white rounded-xl p-4 border border-gray-200 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D6B160]/15 flex items-center justify-center flex-shrink-0">
                          <FileText size={20} color="#8A6C22" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">{doc.name || doc.fileName || t('ipDocument')}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.documentType || doc.type || t('ipDocument')}
                            {doc.date ? ` · ${doc.date}` : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => downloadDocument(doc)}
                          className="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold"
                          style={{ background: 'rgba(214,177,96,0.15)', color: '#8A6C22', border: '1px solid rgba(214,177,96,0.3)' }}
                        >
                          {t('ipDownload')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Investment Team Card */}
      {(true) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900">{t('yourInvestmentTeam')}</CardTitle>
            <CardDescription>{t('dedicatedContacts')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(true) && (
                <ContactCard icon={User} label={t('investmentAdvisor')} name={currentInvestor?.leadOwner || t('noAssignedAdvisor')} />
              )}
              {(true) && (
                <ContactCard icon={MessageCircle} label={t('supportContact')} name={currentInvestor?.executiveAssistant || t('noAssignedSupport')} />
              )}
            </div>
          </CardContent>
        </Card>
      )}

       <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '1rem',
      padding: '2rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginTop: '1.5rem'
    }}>
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#000000',
        marginBottom: '1.5rem',
        fontFamily: 'Montserrat, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Lock size={20} /> {t('securitySettings')}
      </h3>

      {/* 2FA Status and Toggle */}
      <div style={{
        padding: '1.5rem',
        /* Was amber-on-cream with a 2px amber rule — a fifth colour doing the
           job of a state, on a screen limited to gold, white and black. Off is
           now a quiet gold-tinted panel; on keeps the functional green, which
           is the one place colour genuinely carries meaning here. */
        backgroundColor: twoFactorEnabled ? M.okBg : M.tint,
        border: `1px solid ${twoFactorEnabled ? M.okInk : M.tintEdge}`,
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1rem'
        }}>
          <div>
            <h4 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '0.5rem'
            }}>
              {t('twoFactorAuth')}
            </h4>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              {twoFactorEnabled 
                ? t('twoFaEnabledDesc')
                : t('twoFaDisabledDesc')
              }
            </p>
          </div>
          <div style={{
            backgroundColor: twoFactorEnabled ? '#10b981' : '#9ca3af',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}>
            {twoFactorEnabled ? t('enabled') : t('disabled')}
          </div>
        </div>

        {!twoFactorEnabled && !show2FASetup && (
          <button
            onClick={handleEnable2FA}
            disabled={authLoading}
            style={{
              background: 'linear-gradient(135deg, #D6B160 0%, #c4a555 100%)',
              color: '#ffffff',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: authLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {authLoading ? t('settingUp') : t('enableTwoFactorAuth')}
          </button>
        )}

        {twoFactorEnabled && (
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleViewBackupCodes}
              style={{
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('viewBackupCodes')}
            </button>
            <button
              onClick={handleRegenerateBackupCodes}
              style={{
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('ipRegenerateBackupCodes')}
            </button>
            <button
              onClick={handleDisable2FA}
              style={{
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #fecaca',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('ipDisable2fa')}
            </button>
          </div>
        )}
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && !twoFactorEnabled && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#f9fafb',
          border: '2px solid #e5e7eb',
          borderRadius: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#000000',
            marginBottom: '1.5rem'
          }}>
            {t('ipSetUpTwoFactorAuth')}
          </h4>

          <div style={{ marginBottom: '1.5rem' }}>
            <h5 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '0.75rem'
            }}>
              {t('ipStep1ScanQr')}
            </h5>
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1rem'
            }}>
              {t('scanQrCode')}
            </p>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '1rem'
            }}>
              <img 
                src={twoFactorQR} 
                alt="2FA QR Code"
                style={{ 
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#ffffff',
                  maxWidth: '200px'
                }}
              />
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginBottom: '0.5rem'
              }}>
                {t('cantScanEnterManually')}
              </p>
              <code style={{
                display: 'block',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                color: '#000000',
                fontWeight: '600',
                letterSpacing: '0.1rem',
                wordBreak: 'break-all'
              }}>
                {twoFactorSecret}
              </code>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <h5 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '0.75rem'
            }}>
              {t('step2SaveBackupCodes')}
            </h5>
            <div style={{
              backgroundColor: '#ffffff',
              padding: '1rem',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb',
              marginBottom: '0.75rem'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                {backupCodes.map((code, index) => (
                  <div key={index} style={{ color: '#000000' }}>
                    {index + 1}. {code}
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => {
                const codesText = backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');
                navigator.clipboard.writeText(codesText);
                alert(t('ipBackupCodesCopied'));
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#ffffff',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                color: '#000000',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('copyBackupCodes')}
            </button>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <h5 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '0.75rem'
            }}>
              {t('step3VerifySetup')}
            </h5>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '0.5rem'
            }}>
              {t('enterCodeFromAuthenticator')}
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{
                width: '100%',
                padding: '0.875rem',
                fontSize: '1.5rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontFamily: 'monospace'
              }}
            />
          </div>

          {authError && (
            <div style={{
              padding: '0.875rem',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              color: '#dc2626',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '1rem'
            }}>
              {authError}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '0.75rem'
          }}>
            <button
              onClick={() => {
                setShow2FASetup(false);
                setVerificationCode('');
                setAuthError('');
              }}
              style={{
                flex: 1,
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleVerifyAndEnable2FA}
              disabled={authLoading || verificationCode.length !== 6}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #D6B160 0%, #c4a555 100%)',
                color: '#ffffff',
                padding: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: authLoading || verificationCode.length !== 6 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: verificationCode.length !== 6 ? 0.5 : 1
              }}
            >
              {authLoading ? t('ipVerifying') : t('ipEnable2fa')}
            </button>
          </div>
        </div>
      )}

      {/* Backup Codes Display Modal */}
      {showBackupCodes && twoFactorEnabled && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#fff7ed',
          border: '2px solid #fed7aa',
          borderRadius: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#c2410c',
            marginBottom: '1rem'
          }}>
            {t('ipYourBackupCodes')}
          </h4>
          <p style={{
            fontSize: '0.875rem',
            color: '#ea580c',
            marginBottom: '1rem'
          }}>
            {t('saveBackupCodesWarning')}
          </p>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '0.5rem',
            border: '1px solid #fed7aa',
            marginBottom: '0.75rem'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.5rem',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              {backupCodes.map((code, index) => (
                <div key={index} style={{ color: '#000000' }}>
                  {index + 1}. {code}
                </div>
              ))}
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '0.75rem'
          }}>
            <button
              onClick={() => setShowBackupCodes(false)}
              style={{
                flex: 1,
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #fed7aa',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              {t('close')}
            </button>
            <button
              onClick={() => {
                const codesText = backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');
                navigator.clipboard.writeText(codesText);
                alert(t('ipBackupCodesCopied'));
              }}
              style={{
                flex: 1,
                backgroundColor: '#ea580c',
                color: '#ffffff',
                padding: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              {t('copyCodes')}
            </button>
          </div>
        </div>
      )}
    </div>

      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '1rem',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        marginTop: '1.5rem'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#000000',
          marginBottom: '1.5rem',
          fontFamily: 'Montserrat, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <User size={20} /> {t('facialRecognitionLogin')}
        </h3>

        <div style={{
          padding: '1.5rem',
          backgroundColor: faceAuthEnabled ? M.okBg : M.tint,
          border: `1px solid ${faceAuthEnabled ? M.okInk : M.tintEdge}`,
          borderRadius: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem'
          }}>
            <div>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#000000',
                marginBottom: '0.5rem'
              }}>
                {t('faceRecognition')}
              </h4>
              <p style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                {faceAuthEnabled 
                  ? t('faceRecognitionEnabledDesc')
                  : t('faceRecognitionDisabledDesc')
                }
              </p>
            </div>
            <div style={{
              backgroundColor: faceAuthEnabled ? '#10b981' : '#9ca3af',
              color: '#ffffff',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              {faceAuthEnabled ? t('enabled') : t('disabled')}
            </div>
          </div>

          {!faceAuthEnabled && !showFaceEnrollment && (
            <button
              onClick={startFaceEnrollment}
              style={{
                background: 'linear-gradient(135deg, #D6B160 0%, #c4a555 100%)',
                color: '#ffffff',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('enableFaceRecognition')}
            </button>
          )}

          {faceAuthEnabled && (
            <button
              onClick={handleDisableFaceAuth}
              style={{
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                border: '2px solid #fecaca',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('disableFaceRecognition')}
            </button>
          )}
        </div>

        {/* Face Enrollment Modal */}
        {showFaceEnrollment && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f9fafb',
            border: '2px solid #e5e7eb',
            borderRadius: '0.75rem'
          }}>
            <h4 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '1rem'
            }}>
              {t('ipEnrollYourFace')}
            </h4>
            
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1rem'
            }}>
              {t('positionFaceInstruction')}
            </p>

            <div style={{
              position: 'relative',
              backgroundColor: '#000000',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              marginBottom: '1rem'
            }}>
              <video
                ref={faceVideoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              <canvas
                ref={faceCanvasRef}
                style={{ display: 'none' }}
              />
              
              {/* Face outline guide */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '280px',
                height: '350px',
                border: '3px solid #D6B160',
                borderRadius: '50%',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                pointerEvents: 'none'
              }} />
            </div>

            <div style={{
              display: 'flex',
              gap: '0.75rem'
            }}>
              <button
                onClick={stopFaceEnrollment}
                disabled={isCapturingFace}
                style={{
                  flex: 1,
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: isCapturingFace ? 'not-allowed' : 'pointer',
                  opacity: isCapturingFace ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={captureFaceData}
                disabled={isCapturingFace}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #D6B160 0%, #c4a555 100%)',
                  color: '#ffffff',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: isCapturingFace ? 'not-allowed' : 'pointer',
                  opacity: isCapturingFace ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {isCapturingFace ? t('ipCapturing') : t('ipCaptureFace')}
              </button>
            </div>

            <p style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              marginTop: '1rem',
              textAlign: 'center'
            }}>
              {t('faceDataEncrypted')}
            </p>
          </div>
        )}
      </div>

      {/* Delete Account Section */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '1rem',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        marginTop: '1.5rem',
        border: '2px solid #fecaca'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#dc2626',
          marginBottom: '1rem',
          fontFamily: 'Montserrat, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={20} /> {t('ipDeleteAccount')}
        </h3>

        {!showDeleteAccount ? (
          <div>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {t('ipDeleteAccountDesc')}
            </p>
            <button
              onClick={() => setShowDeleteAccount(true)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ffffff',
                color: '#dc2626',
                border: '2px solid #dc2626',
                borderRadius: '0.5rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {t('ipDeleteMyAccount')}
            </button>
          </div>
        ) : (
          <div>
            <div style={{
              padding: '1rem',
              backgroundColor: '#fef2f2',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              border: '1px solid #fecaca'
            }}>
              <p style={{ color: '#dc2626', fontWeight: '600', marginBottom: '0.5rem' }}>
                {t('ipDeleteAccountWarning')}
              </p>
              <ul style={{ color: '#6b7280', fontSize: '0.875rem', paddingLeft: '1.25rem', margin: 0 }}>
                <li>{t('ipDeleteBullet1')}</li>
                <li>{t('ipDeleteBullet2')}</li>
                <li>{t('ipDeleteBullet3')}</li>
              </ul>
            </div>
            <p style={{ color: '#374151', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              {t('ipTypeToConfirmPre')} <strong>DELETE</strong> {t('ipTypeToConfirmPost')}
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t('ipTypeDeleteToConfirm')}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                marginBottom: '1rem',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: deleteConfirmText === 'DELETE' ? '#dc2626' : '#d1d5db',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
              >
                {isDeletingAccount ? t('ipDeleting') : t('ipPermanentlyDeleteAccount')}
              </button>
              <button
                onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(''); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
);
  };

  // Dashboard view
// Enhanced Dashboard View - Replace the entire DashboardView component
// Complete DashboardView - Replace the entire DashboardView component with this

const DashboardView = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('15');
  const [capitalGrowthRate] = useState(15); // 15% annual property appreciation
  const [rentalGrowthRate] = useState(3);   // 3% annual rental growth

  // Calculate projections
  const calculateProjections = (years: number) => {
    const currentValue = totalPortfolioValue || 0;
    const currentMonthlyRental = totalMonthlyRental || 0;
    const currentAnnualRental = currentMonthlyRental * 12;
    
    // 1. Property Value Appreciation (15% annually)
    const projectedPropertyValue = Math.round(currentValue * Math.pow(1 + capitalGrowthRate / 100, years));
    
    // 2. Rental Income Growth (3% annually, based on Bayut data)
    const projectedAnnualRental = Math.round(currentAnnualRental * Math.pow(1 + rentalGrowthRate / 100, years));
    const projectedMonthlyRental = Math.round(projectedAnnualRental / 12);
    
    // 3. Cumulative Rental Income (all rental earned over the years)
    let cumulativeRentalIncome = 0;
    for (let i = 1; i <= years; i++) {
      cumulativeRentalIncome += currentAnnualRental * Math.pow(1 + rentalGrowthRate / 100, i);
    }
    
    // 4. Total Returns
    const capitalGain = projectedPropertyValue - currentValue;
    const totalReturn = capitalGain + cumulativeRentalIncome;
    
    return {
      projectedPropertyValue,
      projectedMonthlyRental,
      projectedAnnualRental,
      cumulativeRentalIncome: Math.round(cumulativeRentalIncome),
      capitalGain: Math.round(capitalGain),
      totalReturn: Math.round(totalReturn),
      currentValue,
      currentMonthlyRental,
      currentAnnualRental
    };
  };

  const projections = calculateProjections(parseInt(selectedTimeframe));

  // Generate Capital Growth Chart Data
  const generateCapitalChart = () => {
    const years = parseInt(selectedTimeframe);
    const data = [];
    const currentValue = totalPortfolioValue || 0;
    
    for (let i = 0; i <= years; i++) {
      const yearValue = currentValue * Math.pow(1 + capitalGrowthRate / 100, i);
      const lowerRange = yearValue * 0.90;
      const upperRange = yearValue * 1.10;
      
      let label = i === 0 ? t('ipNow') : i === 1 ? t('ipOneYear') : `${i} ${t('ipYears')}`;
      
      data.push({
        year: label,
        yearNum: i,
        portfolio: i === 0 ? currentValue : null,
        projection: yearValue,
        lowerRange,
        upperRange
      });
    }
    return data;
  };

  // Generate Rental Income Chart Data
  const generateRentalChart = () => {
    const years = parseInt(selectedTimeframe);
    const data = [];
    const currentAnnualRental = (totalMonthlyRental || 0) * 12;
    let cumulativeRental = 0;
    
    for (let i = 0; i <= years; i++) {
      const annualRental = currentAnnualRental * Math.pow(1 + rentalGrowthRate / 100, i);
      const monthlyRental = annualRental / 12;
      
      if (i > 0) {
        cumulativeRental += annualRental;
      }
      
      let label = i === 0 ? t('ipNow') : i === 1 ? t('ipOneYear') : `${i} ${t('ipYears')}`;
      
      data.push({
        year: label,
        yearNum: i,
        monthlyRental: monthlyRental,
        annualRental: annualRental,
        cumulativeRental: cumulativeRental
      });
    }
    return data;
  };

  const capitalChartData = generateCapitalChart();
  const rentalChartData = generateRentalChart();
  const maxCapitalValue = Math.max(...capitalChartData.map(d => d.upperRange));
  const maxRentalValue = Math.max(...rentalChartData.map(d => d.cumulativeRental));

  // Calculate all-time returns
  const allTimeReturns = totalGains || 0;
  const allTimeReturnsPercent = totalPortfolioValue > 0 
    ? ((allTimeReturns / (totalPortfolioValue - allTimeReturns)) * 100).toFixed(2)
    : 0;

  return (
    <div className="dashboard-container">
      {/* Fixed Portfolio Value Header - Mobile Only */}
      <div className="sticky-portfolio-header">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D6B160] rounded-full flex items-center justify-center">
              <Home className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('portfolio')}</h3>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
            <span className="text-xs font-medium text-gray-600">AED</span>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {t('ipPortfolioValueLabel')}
          </p>
          <h2 className="text-xl font-bold text-gray-900">
            ‎د.إ {(totalPortfolioValue || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </h2>
        </div>
      </div>

      {/* Scrollable Content - with padding for fixed header */}
      <div className="dashboard-scrollable-content space-y-6">
        {/* Invest Button Card */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-lg p-6 border border-gray-100">
          {/* Action Button - Invest via Calendly */}
          <button
            onClick={() => window.open('https://calendly.com/d/cs9h-hxd-2zx/dubai-immobilien-gesprach-mit-investmenter', '_blank')}
            className="w-full flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-900 hover:bg-gray-800 text-white transition-colors"
          >
            <TrendingUp className="h-6 w-6" />
            <span className="text-sm font-medium">{t('ipInvest')}</span>
          </button>
        </div>

        {/* All Time Returns Card */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-lg p-6 border border-gray-100">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('ipAllTimeReturns')}</span>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-gray-900">
                ‎د.إ {allTimeReturns.toLocaleString('en-AE')}
              </span>
              <span className="bg-green-100 text-green-700 text-sm font-semibold px-2 py-1 rounded-full">
                +{allTimeReturnsPercent}%
              </span>
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(parseFloat(allTimeReturnsPercent.toString()), 100)}%` }}
              />
            </div>
          </div>
        </div>

      {/* Rental Income & Appreciation */}
     {/* Rental Income & Appreciation */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  {/* Total Rental Income Card */}
  <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
          <CreditCard className="h-5 w-5 text-green-600" />
        </div>
        <span className="font-medium text-gray-900">{t('ipTotalRentalIncome')}</span>
      </div>
      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
    <div className="flex items-end justify-between">
      <div>
        <p className="text-3xl font-bold text-gray-900">
          ‎د.إ {((totalMonthlyRental || 0) * 12).toLocaleString('en-AE')}
        </p>
        <p className="text-sm text-gray-500 mt-1">{t('ipAnnualIncome')}</p>
      </div>
      <div className="flex items-end gap-1 h-16">
        {[30, 40, 35, 50, 45, 60, 55, 70].map((height, i) => (
          <div 
            key={i}
            className="w-4 bg-gradient-to-t from-green-400 to-emerald-300 rounded-t"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>
  </div>

  {/* Total Appreciation Card */}
  <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <TrendingUp className="h-5 w-5 text-blue-600" />
        </div>
        <span className="font-medium text-gray-900">{t('ipTotalAppreciation')}</span>
      </div>
      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <div className="space-y-3">
      <div>
        <p className="text-3xl font-bold text-gray-900">
          ‎د.إ {(totalGains || 0).toLocaleString('en-AE')}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {t('ipFromPurchaseToToday')}
        </p>
      </div>
      
      {/* Appreciation Percentage & Breakdown */}
      <div className="bg-blue-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-blue-700 font-medium">{t('ipGrowthRate')}</span>
          <span className="text-lg font-bold text-blue-900">
            {totalPortfolioValue > 0 && totalGains > 0
              ? `+${((totalGains / (totalPortfolioValue - totalGains)) * 100).toFixed(1)}%`
              : '0%'
            }
          </span>
        </div>
        
        <div className="pt-2 border-t border-blue-200 text-xs text-blue-600 space-y-1">
          <div className="flex justify-between">
            <span>{t('ipPurchaseValueColon')}</span>
            <span className="font-semibold">
              ‎د.إ {((totalPortfolioValue || 0) - (totalGains || 0)).toLocaleString('en-AE')}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{t('ipCurrentValueColon')}</span>
            <span className="font-semibold">
              ‎د.إ {(totalPortfolioValue || 0).toLocaleString('en-AE')}
            </span>
          </div>
          <div className="flex justify-between text-green-600 font-bold">
            <span>{t('ipCapitalGainColon')}</span>
            <span>+‎د.إ {(totalGains || 0).toLocaleString('en-AE')}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

      {/* Enhanced Projections Card with TWO Separate Charts */}
      <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#D6B160] bg-opacity-10 rounded-full flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-[#D6B160]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{t('ipInvestmentGrowth')}</h3>
          <span className="ml-auto text-2xl font-bold text-gray-900">
            {t('ipCurrentColon')} ‎د.إ {(totalPortfolioValue || 0).toLocaleString('en-AE', { minimumFractionDigits: 0 })}
          </span>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Home className="h-4 w-4 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-600">{t('ipPropertyValueYear')}{selectedTimeframe}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ‎د.إ {projections.projectedPropertyValue.toLocaleString('en-AE')}
            </p>
            <p className="text-xs text-green-600 mt-1">
              +{((projections.capitalGain / projections.currentValue) * 100).toFixed(0)}% {t('ipGrowthSuffix')}
            </p>
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">{t('ipCumulativeRentalYear')}{selectedTimeframe}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              ‎د.إ {projections.cumulativeRentalIncome.toLocaleString('en-AE')}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {t('ipTotalRentCollectedShort')}
            </p>
          </div>

          <div className="border border-purple-200 rounded-xl p-4 bg-purple-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm text-purple-900 font-semibold">{t('ipTotalReturnYear')}{selectedTimeframe}</span>
            </div>
            <p className="text-2xl font-bold text-purple-900">
              ‎د.إ {projections.totalReturn.toLocaleString('en-AE')}
            </p>
            <p className="text-xs text-purple-700 mt-1">
              {t('ipCapitalPlusRental')}
            </p>
          </div>
        </div>

        {/* CHART 1: Property Value Growth (Capital Appreciation) */}
        <div className="mb-8 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Home className="h-5 w-5 text-yellow-600" />
            {t('ipPropertyValueGrowthChart')}
          </h4>
          
          <div className="relative" style={{ height: '300px', paddingLeft: '60px', paddingBottom: '40px' }}>
            {/* Y-axis */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: '40px',
              width: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              paddingRight: '8px'
            }}>
              <span className="text-xs text-gray-500">{Math.round(maxCapitalValue / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxCapitalValue * 0.75 / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxCapitalValue * 0.5 / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxCapitalValue * 0.25 / 1000)}K</span>
              <span className="text-xs text-gray-500">0</span>
            </div>

            {/* Chart container */}
            <div style={{
              position: 'absolute',
              left: '60px',
              right: 0,
              top: 0,
              bottom: '40px'
            }}>
              {/* Grid lines */}
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ borderTop: '1px solid #e5e7eb', width: '100%' }} />
                ))}
              </div>

              {/* SVG Chart */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="capitalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 0.05 }} />
                  </linearGradient>
                </defs>

                {(() => {
                  const years = parseInt(selectedTimeframe);
                  const step = years <= 5 ? 1 : 5;
                  const dataPoints = [];
                  
                  for (let i = 0; i <= years; i += step) {
                    const point = capitalChartData.find(d => d.yearNum === i);
                    if (point) {
                      dataPoints.push({
                        year: i,
                        x: (i / years) * 100,
                        y: 100 - (point.projection / maxCapitalValue) * 100
                      });
                    }
                  }

                  if (dataPoints[dataPoints.length - 1].year !== years) {
                    const lastPoint = capitalChartData[capitalChartData.length - 1];
                    dataPoints.push({
                      year: years,
                      x: 100,
                      y: 100 - (lastPoint.projection / maxCapitalValue) * 100
                    });
                  }

                  let pathData = `M ${dataPoints[0].x} ${dataPoints[0].y}`;
                  for (let i = 1; i < dataPoints.length; i++) {
                    const prev = dataPoints[i - 1];
                    const curr = dataPoints[i];
                    const cpX = (prev.x + curr.x) / 2;
                    pathData += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
                  }

                  const areaPath = `${pathData} L 100 100 L 0 100 Z`;

                  return (
                    <g>
                      <path d={areaPath} fill="url(#capitalGradient)" />
                      <path d={pathData} fill="none" stroke="#f59e0b" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
                      <circle cx={dataPoints[0].x} cy={dataPoints[0].y} r="1.5" fill="#fbbf24" />
                      <circle cx={dataPoints[dataPoints.length - 1].x} cy={dataPoints[dataPoints.length - 1].y} r="1.5" fill="#f59e0b" />
                    </g>
                  );
                })()}
              </svg>
            </div>

            {/* X-axis labels */}
            <div style={{
              position: 'absolute',
              left: '60px',
              right: 0,
              bottom: 0,
              height: '40px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '8px'
            }}>
              {(() => {
                const years = parseInt(selectedTimeframe);
                const labels = ['Y1'];
                if (years >= 5) labels.push('Y5');
                if (years >= 10) labels.push('Y10');
                if (years >= 15) labels.push('Y15');
                if (years >= 20) labels.push('Y20');
                return labels.map((label) => (
                  <span key={label} className="text-xs text-gray-500 font-medium">{label}</span>
                ));
              })()}
            </div>
          </div>
        </div>

        {/* CHART 2: Cumulative Rental Income Growth */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            {t('ipCumulativeRentalChart')}
          </h4>
          
          <div className="relative" style={{ height: '300px', paddingLeft: '60px', paddingBottom: '40px' }}>
            {/* Y-axis */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: '40px',
              width: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              paddingRight: '8px'
            }}>
              <span className="text-xs text-gray-500">{Math.round(maxRentalValue / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxRentalValue * 0.75 / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxRentalValue * 0.5 / 1000)}K</span>
              <span className="text-xs text-gray-500">{Math.round(maxRentalValue * 0.25 / 1000)}K</span>
              <span className="text-xs text-gray-500">0</span>
            </div>

            {/* Chart container */}
            <div style={{
              position: 'absolute',
              left: '60px',
              right: 0,
              top: 0,
              bottom: '40px'
            }}>
              {/* Grid lines */}
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ borderTop: '1px solid #e5e7eb', width: '100%' }} />
                ))}
              </div>

              {/* SVG Chart */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="rentalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.05 }} />
                  </linearGradient>
                </defs>

                {(() => {
                  const years = parseInt(selectedTimeframe);
                  const step = years <= 5 ? 1 : 5;
                  const dataPoints = [];
                  
                  for (let i = 0; i <= years; i += step) {
                    const point = rentalChartData.find(d => d.yearNum === i);
                    if (point) {
                      dataPoints.push({
                        year: i,
                        x: (i / years) * 100,
                        y: 100 - (point.cumulativeRental / maxRentalValue) * 100
                      });
                    }
                  }

                  if (dataPoints[dataPoints.length - 1].year !== years) {
                    const lastPoint = rentalChartData[rentalChartData.length - 1];
                    dataPoints.push({
                      year: years,
                      x: 100,
                      y: 100 - (lastPoint.cumulativeRental / maxRentalValue) * 100
                    });
                  }

                  let pathData = `M ${dataPoints[0].x} ${dataPoints[0].y}`;
                  for (let i = 1; i < dataPoints.length; i++) {
                    const prev = dataPoints[i - 1];
                    const curr = dataPoints[i];
                    const cpX = (prev.x + curr.x) / 2;
                    pathData += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
                  }

                  const areaPath = `${pathData} L 100 100 L 0 100 Z`;

                  return (
                    <g>
                      <path d={areaPath} fill="url(#rentalGradient)" />
                      <path d={pathData} fill="none" stroke="#10b981" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
                      <circle cx={dataPoints[0].x} cy={dataPoints[0].y} r="1.5" fill="#34d399" />
                      <circle cx={dataPoints[dataPoints.length - 1].x} cy={dataPoints[dataPoints.length - 1].y} r="1.5" fill="#10b981" />
                    </g>
                  );
                })()}
              </svg>
            </div>

            {/* X-axis labels */}
            <div style={{
              position: 'absolute',
              left: '60px',
              right: 0,
              bottom: 0,
              height: '40px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '8px'
            }}>
              {(() => {
                const years = parseInt(selectedTimeframe);
                const labels = ['Y1'];
                if (years >= 5) labels.push('Y5');
                if (years >= 10) labels.push('Y10');
                if (years >= 15) labels.push('Y15');
                if (years >= 20) labels.push('Y20');
                return labels.map((label) => (
                  <span key={label} className="text-xs text-gray-500 font-medium">{label}</span>
                ));
              })()}
            </div>
          </div>

          {/* Rental Details Table */}
          <div className="mt-4 bg-white rounded-lg p-4 border border-green-100">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">{t('ipStartingMonthlyRental')}</p>
                <p className="font-semibold text-gray-900">‎د.إ {projections.currentMonthlyRental.toLocaleString('en-AE')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('ipFromBayutApi')}</p>
              </div>
              <div>
                <p className="text-gray-600">{t('ipYearAbbrev')}{selectedTimeframe} {t('ipMonthlyRentalYear')}</p>
                <p className="font-semibold text-gray-900">‎د.إ {projections.projectedMonthlyRental.toLocaleString('en-AE')}</p>
                <p className="text-xs text-green-600 mt-1">{t('ipThreePercentGrowth')}</p>
              </div>
              <div>
                <p className="text-gray-600">{t('ipTotalRentCollected')}</p>
                <p className="font-semibold text-green-600">‎د.إ {projections.cumulativeRentalIncome.toLocaleString('en-AE')}</p>
                <p className="text-xs text-gray-500 mt-1">{t('ipAllRentOver')} {selectedTimeframe} {t('ipYears')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 gap-4 mt-6">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="5">{t('ipProjection5')}</option>
            <option value="10">{t('ipProjection10')}</option>
            <option value="15">{t('ipProjection15')}</option>
            <option value="20">{t('ipProjection20')}</option>
          </select>
        </div>

        {/* Explanation Box */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h5 className="font-semibold text-gray-900 mb-2">{t('ipHowProjectionsWork')}</h5>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-yellow-500 mt-0.5">●</span>
              <span><strong>{t('ipProjPropertyValueLabel')}</strong> {t('ipProjPropertyValueDesc')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">●</span>
              <span><strong>{t('ipProjRentalIncomeLabel')}</strong> {t('ipProjRentalIncomeDesc')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">●</span>
              <span><strong>{t('ipProjTotalReturnLabel')}</strong> {t('ipProjTotalReturnDesc')}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Calculate Potential Button */}
      <button
        onClick={() => setActiveTab('analysis')}
        className="w-full bg-white border-2 border-gray-200 hover:border-[#D6B160] rounded-2xl p-4 flex items-center justify-between transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 group-hover:bg-[#D6B160] group-hover:bg-opacity-10 rounded-full flex items-center justify-center transition-colors">
            <svg className="h-5 w-5 text-gray-600 group-hover:text-[#D6B160]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="font-semibold text-gray-900">{t('ipCalculateMyPotential')}</span>
        </div>
        <svg className="h-5 w-5 text-gray-400 group-hover:text-[#D6B160] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Property Cards Section */}
      {otpUnits.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">{t('yourProperties')}</h3>
            <button
              onClick={() => setActiveTab('portfolio')}
              className="text-sm text-[#D6B160] hover:text-[#c4a555] font-medium flex items-center gap-1"
            >
              {t('ipViewAll')}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otpUnits.slice(0, 4).map((unit) => {
              const portfolioData = getUnitPortfolioData(unit);
              const fiveYearReturn = unit.purchaseValue > 0 
                ? (((unit.currentValue - unit.purchaseValue) / unit.purchaseValue) * 100).toFixed(2)
                : 0;
              const yearlyReturn = parseFloat(fiveYearReturn.toString()) / 5;
              const rentalYield = unit.currentValue > 0
                ? (((unit.monthlyRental * 12) / unit.currentValue) * 100).toFixed(2)
                : 0;

              return (
                <div key={unit.id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
                  {/* Trending Badge */}
                  <div className="relative">
                    <div className="absolute top-3 left-3 z-10 flex gap-2">
                      <span className="bg-white bg-opacity-90 backdrop-blur-sm text-gray-900 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <Flame size={14} className="text-red-500" /> {t('ipTrending')}
                      </span>
                      <span className="bg-green-500 bg-opacity-90 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <span>✓</span> {t('ipCapitalGrowth')}
                      </span>
                    </div>
                    
                    {/* Property Image Placeholder */}
                    <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <Home className="h-16 w-16 text-gray-400" />
                    </div>

                    {/* Pagination Dots */}
                    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-white' : 'bg-white bg-opacity-50'}`} />
                      ))}
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Property Details */}
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Home className="h-3 w-3" />
                        2
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        #{unit.unitNumber}
                      </span>
                      <span className="flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        {unit.occupancyStatus}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Dubai
                      </span>
                    </div>

                    <h4 className="font-bold text-gray-900 mb-3 line-clamp-1">{unit.name}</h4>

                    {/* Price and Funding */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-bold text-[#D6B160]">
                        ‎د.إ {unit.currentValue.toLocaleString('en-AE', { minimumFractionDigits: 0 })}
                      </span>
                      <span className="text-sm font-semibold text-gray-600">
                        {unit.currentValue > 0 && unit.purchaseValue > 0
                          ? `${((unit.purchaseValue / unit.currentValue) * 100).toFixed(0)}% ${t('ipFundedSuffix')}`
                          : 'N/A'
                        }
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                      <div 
                        className="h-full bg-[#D6B160] rounded-full transition-all duration-500"
                        style={{ 
                          width: unit.currentValue > 0 && unit.purchaseValue > 0
                            ? `${Math.min((unit.purchaseValue / unit.currentValue) * 100, 100)}%`
                            : '0%'
                        }}
                      />
                    </div>

                    {/* Returns */}
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs mb-1">{t('ipFiveYearTotalReturn')}</p>
                        <p className="font-bold text-gray-900">{fiveYearReturn}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs mb-1">{t('ipYearlyInvestmentReturn')}</p>
                        <p className="font-bold text-gray-900">{yearlyReturn.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs mb-1">{t('ipProjectedNetYield')}</p>
                        <p className="font-bold text-gray-900">{rentalYield}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {otpUnits.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Home className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('noPropertiesYet')}</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {t('ipUploadOtpToAddProperty')}
          </p>
          <button
            onClick={() => setActiveTab('documents')}
            className="bg-[#D6B160] hover:bg-[#c4a555] text-white px-6 py-3 rounded-xl font-medium transition-colors inline-flex items-center gap-2"
          >
            <Upload className="h-5 w-5" />
            {t('uploadDocument')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
};
  // Enhanced Analysis View with DLD Market Intelligence
const AnalysisView = () => {
  const [dldData, setDldData] = useState<any>(null);
  const [dldLoading, setDldLoading] = useState(false);
  const [dldError, setDldError] = useState<string | null>(null);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Fetch DLD market intelligence
  useEffect(() => {
    if (!currentInvestor?.id) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    setDldLoading(true);
    fetch(`${API_URL}/dld/market-intelligence?investorId=${currentInvestor.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.success) setDldData(data.data);
        setDldLoading(false);
      })
      .catch(() => {
        setDldError('Could not load market data');
        setDldLoading(false);
      });
  }, [currentInvestor]);

  const handleSyncDLD = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/dld/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ investorId: currentInvestor?.id })
      });
      if (res.ok) {
        // Refetch intelligence
        const intRes = await fetch(`${API_URL}/dld/market-intelligence?investorId=${currentInvestor?.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (intRes.ok) {
          const data = await intRes.json();
          if (data?.success) setDldData(data.data);
        }
      }
    } catch { /* ignore */ }
    setSyncing(false);
  };

  // Calculate per-unit metrics (combined book + DLD)
  const unitAnalysis = otpUnits.map(unit => {
    const purchaseValue = unit.purchaseValue || 0;
    // Use appreciationAmount when currentValue not updated in DB
    let apprAmount = (unit as any).appreciationAmount || 0;
    if (apprAmount === 0 && (unit as any).purchaseDate) {
      const purchase = new Date((unit as any).purchaseDate);
      const now = new Date();
      const yearsSince = (now.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 365);
      apprAmount = Math.round(purchaseValue * 0.08 * yearsSince); // 8% annual
    }
    const currentValue = apprAmount > 0 ? purchaseValue + apprAmount : (unit.currentValue || 0);
    const monthlyRental = unit.monthlyRental || 0;
    const capitalGain = currentValue - purchaseValue;
    const capitalROI = purchaseValue > 0 ? ((capitalGain / purchaseValue) * 100) : 0;
    const annualRental = monthlyRental * 12;
    const rentalYield = currentValue > 0 ? ((annualRental / currentValue) * 100) : 0;
    const totalROI = purchaseValue > 0 ? (((capitalGain + annualRental) / purchaseValue) * 100) : 0;

    // Find DLD intelligence for this unit
    // Match by unit ID first, then fallback to project name (IDs may differ between Firestore and memory)
    let dldUnit = dldData?.units?.find((u: any) => u.unitId === unit.id);
    if ((!dldUnit || dldUnit.comparablesCount === 0) && unit.project) {
      const pp = (unit.project as string).toLowerCase().trim();
      const byProject = dldData?.units?.find((u: any) => {
        const up = (u.project || '').toLowerCase().trim();
        return up === pp || up.includes(pp) || pp.includes(up);
      });
      if (byProject && byProject.comparablesCount > 0) dldUnit = byProject;
    }

    return {
      ...unit,
      effectiveValue: currentValue, // the computed current value (with appreciation)
      capitalGain, capitalROI, annualRental, rentalYield, totalROI,
      dld: dldUnit || null
    };
  }).sort((a, b) => b.capitalROI - a.capitalROI);

  const portfolio = dldData?.portfolio;
  const hasDLD = dldData && dldData.units && dldData.units.length > 0;

  const confidenceColor = (c: string) =>
    c === 'high' ? '#10b981' : c === 'medium' ? '#f59e0b' : '#6b7280';
  const confidenceLabel = (c: string) =>
    c === 'high' ? t('vrConfidenceHigh') : c === 'medium' ? t('vrConfidenceMedium') : t('vrConfidenceLow');

  return (
    <div className="space-y-6">

      {/* DLD POWERED HEADER */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: 0, right: 0, width: '200px', height: '200px',
          background: 'radial-gradient(circle, rgba(214,177,96,0.2) 0%, transparent 70%)',
          borderRadius: '50%', transform: 'translate(30%, -30%)'
        }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #D6B160, #c4a555)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Activity size={22} color="#fff" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>{t('ipDldMarketIntelligence')}</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.7 }}>
                  {t('ipPoweredByDld')}
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Manual "Sync Market Data" removed: DLD data updates automatically
                (UAE-side daily sync); the manual endpoint is UAE-blocked (403)
                from this deployment anyway. */}
            {dldData?.lastSync && (
              <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                {t('ipUpdatedAutomatically')} · {new Date(dldData.lastSync).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* PORTFOLIO OVERVIEW - DLD Enhanced */}
      {otpUnits.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {/* Total Invested */}
          <div style={{
            background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            borderRadius: '1rem', padding: '1.25rem',
            border: '1px solid #bfdbfe'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <DollarSign size={18} color="#2563eb" />
              <span style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: '600' }}>{t('ipCapitalInvested')}</span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1e3a8a', margin: 0 }}>
              AED {otpUnits.reduce((sum, u) => sum + (u.purchaseValue || 0), 0).toLocaleString('en-AE')}
            </p>
          </div>

          {/* DLD Market Value */}
          <div style={{
            background: portfolio?.dldMarketValue
              ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
              : 'linear-gradient(135deg, #f9fafb, #f3f4f6)',
            borderRadius: '1rem', padding: '1.25rem',
            border: portfolio?.dldMarketValue ? '1px solid #86efac' : '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Target size={18} color={portfolio?.dldMarketValue ? '#16a34a' : '#6b7280'} />
              <span style={{ fontSize: '0.8rem', color: portfolio?.dldMarketValue ? '#16a34a' : '#6b7280', fontWeight: '600' }}>
                {t('ipDldMarketValue')}
              </span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: '800', color: portfolio?.dldMarketValue ? '#14532d' : '#374151', margin: 0 }}>
              {portfolio?.dldMarketValue
                ? `AED ${portfolio.dldMarketValue.toLocaleString('en-AE')}`
                : `AED ${(totalPortfolioValue || 0).toLocaleString('en-AE')}`
              }
            </p>
            {portfolio?.dldMarketValue && (
              <p style={{ fontSize: '0.7rem', color: '#16a34a', margin: '0.25rem 0 0', fontWeight: '500' }}>
                {t('ipBasedOnDldComparables')}
              </p>
            )}
          </div>

          {/* Unrealized Gain / Appreciation */}
          <div style={{
            background: (portfolio?.unrealizedGain || totalGains) > 0
              ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
              : 'linear-gradient(135deg, #fef2f2, #fecaca)',
            borderRadius: '1rem', padding: '1.25rem',
            border: (portfolio?.unrealizedGain || totalGains) > 0 ? '1px solid #6ee7b7' : '1px solid #fca5a5'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <TrendingUp size={18} color={(portfolio?.unrealizedGain || totalGains) > 0 ? '#10b981' : '#ef4444'} />
              <span style={{ fontSize: '0.8rem', color: (portfolio?.unrealizedGain || totalGains) > 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                {t('ipUnrealizedGain')}
              </span>
            </div>
            <p style={{
              fontSize: '1.5rem', fontWeight: '800', margin: 0,
              color: (portfolio?.unrealizedGain || totalGains) > 0 ? '#065f46' : '#991b1b'
            }}>
              {(portfolio?.unrealizedGain || totalGains) > 0 ? '+' : ''}AED {(portfolio?.unrealizedGain || totalGains || 0).toLocaleString('en-AE')}
            </p>
            {/* portfolioAppreciation arrives as a STRING from the DLD API — coerce
                before .toFixed() or this throws and blanks the screen. */}
            <p style={{ fontSize: '0.8rem', fontWeight: '700', margin: '0.25rem 0 0',
              color: (Number(portfolio?.portfolioAppreciation) || 0) >= 0 ? '#059669' : '#dc2626'
            }}>
              {(Number(portfolio?.portfolioAppreciation) || (totalGains && totalPortfolioValue ? ((totalGains / (totalPortfolioValue - totalGains)) * 100) : 0)).toFixed(1)}% {t('ipAppreciationLower')}
            </p>
          </div>

          {/* DLD Rental Intelligence */}
          <div style={{
            background: portfolio?.dldTotalAnnualRent
              ? 'linear-gradient(135deg, #faf5ff, #f3e8ff)'
              : 'linear-gradient(135deg, #faf5ff, #ede9fe)',
            borderRadius: '1rem', padding: '1.25rem',
            border: portfolio?.dldTotalAnnualRent ? '1px solid #c4b5fd' : '1px solid #ddd6fe'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Building2 size={18} color="#7c3aed" />
              <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: '600' }}>
                {portfolio?.dldTotalAnnualRent ? t('ipDldRentalEstimate') : t('ipAnnualRental')}
              </span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: '800', color: '#4c1d95', margin: 0 }}>
              AED {(portfolio?.dldTotalAnnualRent || (totalMonthlyRental || 0) * 12).toLocaleString('en-AE')}
              <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#7c3aed' }}>{t('ipPerYear')}</span>
            </p>
            {portfolio?.portfolioRentalYield > 0 && (
              <p style={{ fontSize: '0.8rem', color: '#7c3aed', margin: '0.25rem 0 0', fontWeight: '600' }}>
                {portfolio.portfolioRentalYield.toFixed(1)}% {t('ipGrossYield')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* PER-UNIT DLD INTELLIGENCE */}
      {otpUnits.length > 0 && (
        <div style={{
          backgroundColor: '#fff', borderRadius: '1rem',
          border: '1px solid #e5e7eb', overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>
                {t('ipPropertyLevelAnalysis')}
              </h3>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                {t('ipEachPropertyCompared')}
              </p>
            </div>
            {hasDLD && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.35rem 0.75rem', borderRadius: '2rem',
                backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0'
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: '600' }}>{t('ipDldDataActive')}</span>
              </div>
            )}
          </div>

          <div style={{ padding: '1rem 1.5rem' }}>
            {unitAnalysis.map((unit, index) => {
              const isExpanded = expandedUnit === unit.id;
              const dld = unit.dld;
              const hasMarketValue = dld?.dldMarketValue > 0;
              const hasRentalData = dld?.dldAvgAnnualRent > 0;
              const marketValueDiff = hasMarketValue ? dld.dldMarketValue - unit.purchaseValue : 0;
              const marketValuePct = hasMarketValue && unit.purchaseValue > 0
                ? ((marketValueDiff / unit.purchaseValue) * 100) : 0;

              return (
                <div key={unit.id} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  marginBottom: '1rem',
                  overflow: 'hidden',
                  transition: 'all 0.2s'
                }}>
                  {/* Unit Header Row */}
                  <div
                    onClick={() => setExpandedUnit(isExpanded ? null : unit.id)}
                    style={{
                      padding: '1rem 1.25rem',
                      cursor: 'pointer',
                      background: index === 0 ? 'linear-gradient(135deg, #fefce8, #fef9c3)' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '1rem', flexWrap: 'wrap'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '200px' }}>
                      {index === 0 && (
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px',
                          background: 'linear-gradient(135deg, #D6B160, #ecc94b)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <Star size={16} color="#fff" />
                        </div>
                      )}
                      <div>
                        <h4 style={{ margin: 0, fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>
                          {unit.name}
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                          {unit.project} {unit.unitNumber ? `• ${t('unit')} ${unit.unitNumber}` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                      {hasMarketValue && (
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('ipDldValue')}</p>
                          <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#16a34a' }}>
                            AED {dld.dldMarketValue.toLocaleString('en-AE')}
                          </p>
                        </div>
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ROI</p>
                        <p style={{
                          margin: 0, fontSize: '1rem', fontWeight: '700',
                          color: unit.capitalROI >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {unit.capitalROI >= 0 ? '+' : ''}{unit.capitalROI.toFixed(1)}%
                        </p>
                      </div>
                      {hasRentalData && (
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('ipDldRent')}</p>
                          <p style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#7c3aed' }}>
                            AED {dld.dldAvgMonthlyRent.toLocaleString('en-AE')}{t('ipPerMonth')}
                          </p>
                        </div>
                      )}
                      <ChevronRight size={20} color="#9ca3af" style={{
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }} />
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div style={{ padding: '1.25rem', borderTop: '1px solid #f3f4f6', backgroundColor: '#fafafa' }}>

                      {/* Valuation Comparison */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: '0.75rem', marginBottom: '1.25rem'
                      }}>
                        <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>{t('purchasePrice')}</p>
                          <p style={{ margin: '0.25rem 0 0', fontSize: '1.15rem', fontWeight: '800', color: '#111827' }}>
                            AED {unit.purchaseValue.toLocaleString('en-AE')}
                          </p>
                        </div>
                        <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>{t('ipBookValue')}</p>
                          <p style={{ margin: '0.25rem 0 0', fontSize: '1.15rem', fontWeight: '800', color: '#2563eb' }}>
                            AED {(unit.effectiveValue || unit.currentValue).toLocaleString('en-AE')}
                          </p>
                        </div>
                        {hasMarketValue && (
                          <div style={{
                            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                            borderRadius: '0.75rem', padding: '1rem',
                            border: '1px solid #86efac'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <p style={{ margin: 0, fontSize: '0.7rem', color: '#16a34a', fontWeight: '600', textTransform: 'uppercase' }}>{t('ipDldMarketValue')}</p>
                              <div style={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: confidenceColor(dld.valuationConfidence)
                              }} title={confidenceLabel(dld.valuationConfidence)} />
                            </div>
                            <p style={{ margin: '0.25rem 0 0', fontSize: '1.15rem', fontWeight: '800', color: '#14532d' }}>
                              AED {dld.dldMarketValue.toLocaleString('en-AE')}
                            </p>
                            <p style={{
                              margin: '0.15rem 0 0', fontSize: '0.7rem', fontWeight: '600',
                              color: marketValuePct >= 0 ? '#16a34a' : '#dc2626'
                            }}>
                              {marketValuePct >= 0 ? '+' : ''}{marketValuePct.toFixed(1)}% {t('ipVsPurchase')}
                              ({dld.comparablesCount} {t('ipComparables')})
                            </p>
                          </div>
                        )}
                        <div style={{
                          background: unit.capitalGain >= 0
                            ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
                            : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                          borderRadius: '0.75rem', padding: '1rem',
                          border: unit.capitalGain >= 0 ? '1px solid #6ee7b7' : '1px solid #fca5a5'
                        }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: unit.capitalGain >= 0 ? '#059669' : '#dc2626', fontWeight: '600', textTransform: 'uppercase' }}>{t('capitalGain')}</p>
                          <p style={{
                            margin: '0.25rem 0 0', fontSize: '1.15rem', fontWeight: '800',
                            color: unit.capitalGain >= 0 ? '#065f46' : '#991b1b'
                          }}>
                            {unit.capitalGain >= 0 ? '+' : ''}AED {unit.capitalGain.toLocaleString('en-AE')}
                          </p>
                          <p style={{ margin: '0.15rem 0 0', fontSize: '0.7rem', fontWeight: '600',
                            color: unit.capitalROI >= 0 ? '#059669' : '#dc2626'
                          }}>
                            {unit.capitalROI >= 0 ? '+' : ''}{unit.capitalROI.toFixed(1)}% ROI
                          </p>
                        </div>
                      </div>

                      {/* DLD Rental Intelligence */}
                      {hasRentalData && (
                        <div style={{
                          background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
                          borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.25rem',
                          border: '1px solid #ddd6fe'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <Building2 size={16} color="#7c3aed" />
                            <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: '#5b21b6' }}>
                              {t('ipDldRentalMarketData')}
                            </h4>
                            <span style={{
                              fontSize: '0.65rem', padding: '0.15rem 0.5rem', borderRadius: '1rem',
                              backgroundColor: `${confidenceColor(dld.rentalConfidence)}20`,
                              color: confidenceColor(dld.rentalConfidence), fontWeight: '600'
                            }}>
                              {confidenceLabel(dld.rentalConfidence)}
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                            <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7c3aed' }}>{t('ipAvgMonthlyRent')}</p>
                              <p style={{ margin: '0.15rem 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#4c1d95' }}>
                                AED {dld.dldAvgMonthlyRent.toLocaleString('en-AE')}
                              </p>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7c3aed' }}>{t('ipAvgAnnualRent')}</p>
                              <p style={{ margin: '0.15rem 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#4c1d95' }}>
                                AED {dld.dldAvgAnnualRent.toLocaleString('en-AE')}
                              </p>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7c3aed' }}>{t('ipGrossRentalYield')}</p>
                              <p style={{ margin: '0.15rem 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#4c1d95' }}>
                                {dld.dldRentalYield?.toFixed(1) || '—'}%
                              </p>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7c3aed' }}>{t('ipRentalComparables')}</p>
                              <p style={{ margin: '0.15rem 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#4c1d95' }}>
                                {dld.rentalComparablesCount} {t('ipContracts')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* DLD Price Per Sqft & Total ROI */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem'
                      }}>
                        {dld?.dldPricePerSqft > 0 && (
                          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #e5e7eb' }}>
                            <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600' }}>{t('ipDldPricePerSqm')}</p>
                            <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: '800', color: '#111827' }}>
                              AED {(dld.dldPricePerSqm || Math.round(dld.dldPricePerSqft * 10.764)).toLocaleString('en-AE')}
                            </p>
                          </div>
                        )}
                        <div style={{
                          background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                          borderRadius: '0.75rem', padding: '1rem',
                          border: '1px solid #93c5fd'
                        }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#2563eb', fontWeight: '600' }}>{t('ipTotalRoiCapitalRental')}</p>
                          <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: '800', color: '#1e3a8a' }}>
                            {unit.totalROI.toFixed(1)}%
                          </p>
                          <p style={{ margin: '0.15rem 0 0', fontSize: '0.65rem', color: '#3b82f6' }}>
                            {t('ipCapitalShort')}: {unit.capitalROI.toFixed(1)}% + {t('ipRentalShort')}: {unit.rentalYield.toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      {/* Recent Comparable Transactions */}
                      {dld?.recentComparables?.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: '700', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Layers size={14} /> {t('ipRecentDldTransactions')}
                          </h4>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', color: '#6b7280' }}>{t('date')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>{t('amount')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>{t('ipSize')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>AED/sqft</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', color: '#6b7280' }}>{t('ipType')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dld.recentComparables.map((comp: any, i: number) => (
                                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '0.5rem', color: '#374151' }}>
                                      {comp.date ? new Date(comp.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                                      AED {comp.amount?.toLocaleString('en-AE') || '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#374151' }}>
                                      {comp.sizeSqft ? `${comp.sizeSqft.toLocaleString('en-AE')} sqft` : '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#2563eb' }}>
                                      {comp.pricePerSqft ? comp.pricePerSqft.toLocaleString('en-AE') : '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', color: '#6b7280' }}>
                                      {comp.rooms || comp.type || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Recent Rental Comparables */}
                      {dld?.recentRentals?.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: '700', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Building2 size={14} /> {t('ipRecentDldRentals')}
                          </h4>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#faf5ff' }}>
                                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', color: '#7c3aed' }}>{t('ipStartDate')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('ipAnnualRentCol')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('ipMonthlyCol')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('ipSize')}</th>
                                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', color: '#7c3aed' }}>{t('ipType')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dld.recentRentals.map((rent: any, i: number) => (
                                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '0.5rem', color: '#374151' }}>
                                      {rent.startDate ? new Date(rent.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#4c1d95' }}>
                                      AED {rent.annualRent?.toLocaleString('en-AE') || '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#374151' }}>
                                      AED {rent.monthlyRent?.toLocaleString('en-AE') || '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#374151' }}>
                                      {rent.sizeSqft ? `${rent.sizeSqft.toLocaleString('en-AE')} sqft` : '—'}
                                    </td>
                                    <td style={{ padding: '0.5rem', color: '#6b7280' }}>
                                      {rent.rooms || '—'} {rent.isRenewal ? t('ipRenewalParen') : t('ipNewParen')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* No DLD data state */}
                      {!dld && (
                        <div style={{
                          textAlign: 'center', padding: '1.5rem',
                          background: '#f9fafb', borderRadius: '0.75rem'
                        }}>
                          <Activity size={24} color="#9ca3af" style={{ margin: '0 auto 0.5rem' }} />
                          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                            {t('ipDldDataAppearsAuto')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INVESTMENT SUMMARY */}
      {otpUnits.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #0f172a, #1e293b)',
          borderRadius: '1rem', padding: '1.5rem', color: 'white'
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Zap size={18} color="#D6B160" /> {t('ipInvestmentSummary')}
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('ipTotalInvested')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700' }}>
                AED {otpUnits.reduce((sum, u) => sum + (u.purchaseValue || 0), 0).toLocaleString('en-AE')}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('ipPortfolioValueLabel')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#60a5fa' }}>
                AED {(portfolio?.dldMarketValue || totalPortfolioValue || 0).toLocaleString('en-AE')}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('ipTotalGain')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#4ade80' }}>
                +AED {(portfolio?.unrealizedGain || totalGains || 0).toLocaleString('en-AE')}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('ipAnnualRental')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#c084fc' }}>
                AED {(portfolio?.dldTotalAnnualRent || (totalMonthlyRental || 0) * 12).toLocaleString('en-AE')}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('ipRentalYield')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#c084fc' }}>
                {(portfolio?.portfolioRentalYield || (totalPortfolioValue > 0 ? (((totalMonthlyRental || 0) * 12) / totalPortfolioValue * 100) : 0)).toFixed(1)}%
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.6, textTransform: 'uppercase' }}>{t('properties')}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', fontWeight: '700' }}>
                {otpUnits.length}
              </p>
            </div>
          </div>
          <div style={{
            marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.5
          }}>
            <Shield size={14} />
            <span style={{ fontSize: '0.7rem' }}>
              {t('ipDldDataDisclaimer')}
            </span>
          </div>
        </div>
      )}

      {/* Data Sources — Transparency Card */}
      {otpUnits.length > 0 && (
        <div style={{
          backgroundColor: '#fff', borderRadius: '1rem',
          border: '1px solid #e5e7eb', overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', gap: '0.75rem'
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'linear-gradient(135deg, #0f172a, #334155)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Shield size={18} color="#D6B160" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                {t('ipHowWeGetData')}
              </h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                {t('ipAllMarketDataSourced')}
              </p>
            </div>
          </div>
          <div style={{ padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { label: t('ipDsMarketValue'), desc: t('ipDsMarketValueDesc'), color: '#16a34a', bg: '#f0fdf4' },
                { label: t('ipDsPricePerSqft'), desc: t('ipDsPricePerSqftDesc'), color: '#2563eb', bg: '#eff6ff' },
                { label: t('ipDsRentalPerMonth'), desc: t('ipDsRentalPerMonthDesc'), color: '#7c3aed', bg: '#faf5ff' },
                { label: t('ipDsRentalYield'), desc: t('ipDsRentalYieldDesc'), color: '#ca8a04', bg: '#fefce8' },
                { label: t('ipDsComparableSales'), desc: t('ipDsComparableSalesDesc'), color: '#0891b2', bg: '#ecfeff' },
                { label: t('ipDsComparableRentals'), desc: t('ipDsComparableRentalsDesc'), color: '#9333ea', bg: '#faf5ff' }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  padding: '0.75rem', borderRadius: '0.75rem',
                  backgroundColor: item.bg
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: item.color, marginTop: '0.35rem', flexShrink: 0
                  }} />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: item.color }}>
                      {item.label}
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: '#4b5563' }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{
              marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280' }}>
                {t('ipSourceLabel')} <strong>gateway.dubailand.gov.ae</strong> {t('ipDldApiNote')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {otpUnits.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('ipNoPortfolioData')}</h3>
            <p className="text-gray-600 mb-6">
              {t('ipUploadOtpToUnlock')}
            </p>
            <button
              onClick={() => setActiveTab('documents')}
              className="text-white px-6 py-3 rounded-lg font-medium transition-colors"
              style={{ backgroundColor: '#D6B160' }}
            >
              {t('ipUploadOtpDocuments')}
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

  // Payments View — now powered by InstallmentPlanView
  const PaymentsView = () => (
    <InstallmentPlanView currentInvestor={currentInvestor} units={units} />
  );


  // Events View

// Events View
const EventsView = () => {
  // An array for your past event videos from different sources
 const pastEvents = [
    {
      id: 1,
      title: 'Asset Desk Journey Highlights, March 2025',
      description: 'A look back at our exclusive investor journey in Dubai.',
      src: 'https://drive.google.com/file/d/11kjhUYwxAsmjB6EH30d5j5rOmfQvq9Le/preview'
    },
    {
      id: 2,
      title: 'Asset Desk Journey Highlights, April 2025',
      description: 'A look back at our exclusive investor journey in Dubai',
      src: 'https://drive.google.com/file/d/13Tm0DLECKrsmx7fnw4bSyTXQ7-wTcH1J/preview'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4">
      <Card className="border border-[#D6B160] bg-white">
        <CardHeader>
          <CardTitle className="text-black text-2xl">Upcoming Asset Desk Events</CardTitle>
          <CardDescription className="text-gray-700">Webinars, congresses, and exclusive investment trips.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="border border-[#D6B160] rounded-lg hover:shadow-md transition-shadow bg-white overflow-hidden">
              
              {/* TOP HEADER */}
              <div className="p-6 bg-white space-y-2">
                <h4 className="text-xl text-black font-bold">InvestMent Kongress 2025</h4>
                <p className="text-sm text-gray-700">
                  Attend the premier investment congress to network with industry leaders and gain insights into the future of investing.
                </p>
              
              </div>
              
              {/* TOP SCROLLING FEATURES CAROUSEL */}
              <div className="overflow-hidden bg-[#1a1c22]" style={{ width: '100%', padding: '14px 0' }}>
                <div className="flex animate-scroll" style={{ width: 'fit-content' }}>
                  {/* First set of items */}
                  <div className="flex items-center flex-shrink-0" style={{ gap: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Maximale Rendite</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Steuerliche Vorteile</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Diversifikation</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Vermögensschutz</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Kapitalanlagen</span>
                  </div>
                  {/* Duplicate set for seamless loop */}
                  <div className="flex items-center flex-shrink-0" style={{ gap: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Maximale Rendite</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Steuerliche Vorteile</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Diversifikation</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Vermögensschutz</span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#e5e7eb', whiteSpace: 'nowrap', padding: '6px 14px', backgroundColor: '#374151', borderRadius: '9999px' }}>Kapitalanlagen</span>
                  </div>
                </div>
              </div>
{/* SPEAKERS TITLE SECTION */}
              <div style={{ backgroundColor: '#1a1c22', color: '#ffffff', padding: '16px 24px', textAlign: 'center' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{t('ipKongressSpeakers')}</h3>
              </div>

              {/* MAIN CONTENT BLOCK - CROPPED TO FOCUS ON SPEAKERS */}
              <div className="w-full" style={{ height: '320px', position: 'relative', overflow: 'hidden', backgroundColor: '#000' }}>
                <iframe 
                  src="https://drive.google.com/file/d/1q44kXIazn2FMehHTcVW5q_ET3IPnpvFj/preview" 
                  title="Kongress Event Preview" 
                  allow="autoplay; fullscreen; picture-in-picture" 
                  allowFullScreen 
                  style={{ 
                    width: '100%', 
                    height: '600px', 
                    border: 'none',
                    marginTop: '-120px'
                  }}
                ></iframe>
                {/* Overlay to hide Google Drive controls */}
                <div style={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  height: '60px', 
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)',
                  pointerEvents: 'none'
                }}></div>
              </div>



              {/* BOTTOM LOGO CAROUSEL */}
              <div style={{ overflow: 'hidden', backgroundColor: '#000000', padding: '8px 0', width: '100%', margin: 0 }}>
                <div className="animate-logo-scroll" style={{ display: 'flex', width: 'max-content', gap: 0, margin: 0 }}>
                  {/* First set */}
                  <img 
                    src="https://cdn.prod.website-files.com/68586f875647a3878e8cc485/68a5cd6eb9950407b7e8d970_Untitled%20design.avif" 
                    alt="Media Partners and Sponsors" 
                    style={{ height: '24px', width: 'auto', minWidth: '940px', objectFit: 'contain', display: 'block', margin: 0, padding: 0 }}
                  />
                  {/* Duplicate set for seamless loop - no gap */}
                  <img 
                    src="https://cdn.prod.website-files.com/68586f875647a3878e8cc485/68a5cd6eb9950407b7e8d970_Untitled%20design.avif" 
                    alt="Media Partners and Sponsors" 
                    style={{ height: '24px', width: 'auto', minWidth: '940px', objectFit: 'contain', display: 'block', margin: 0, padding: 0 }}
                  />
                </div>
              </div>

              {/* FOOTER: Countdown Timer */}
              <div className="bg-gray-800 text-white p-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold">21</div>
                      <div className="text-xs text-gray-300">Tagen</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">2</div>
                      <div className="text-xs text-gray-300">Stunden</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">24</div>
                      <div className="text-xs text-gray-300">Minuten</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">38</div>
                      <div className="text-xs text-gray-300">Sekunden</div>
                    </div>
                  </div>
                  
                  <div className="text-center sm:text-right flex flex-col items-center sm:items-end space-y-2">
                    <p className="text-sm font-light">Fr, 24. und Sa, 25. Oktober 2025</p>
                    <a
                      href="https://www.investment-kongress.com/?referrer=INVESTMENTER"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-[#D6B160] text-black px-4 py-2 rounded font-semibold text-sm hover:bg-opacity-90 transition-colors"
                    >
                      Jetzt gratis Ticket sichern
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Past Events Section */}
      <Card className="border border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-black text-2xl">Past Events</CardTitle>
          <CardDescription className="text-gray-700">Highlights from our previous investor journeys.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pastEvents.map(event => (
              <div key={event.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-white">
                <div style={{ height: '300px', position: 'relative' }}>
                  <iframe
                    src={event.src}
                    title={event.title}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  ></iframe>
                </div>
                <div className="p-4 bg-white">
                  <h4 className="text-lg font-bold text-black mb-2">{event.title}</h4>
                  <p className="text-sm text-gray-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

  // ============================================================
  // NATIVE ROI CALCULATOR / SOA GENERATOR
  // ============================================================
  const SOACalculatorView = () => {
    // Role selection
    const [selectedRole, setSelectedRole] = useState<'buyer' | 'seller' | null>(null);
    
    // Navigation
    const [currentView, setCurrentView] = useState<'form' | 'preview' | 'roi'>('form');
    
    // Document settings
    const [documentStatus, setDocumentStatus] = useState<'draft' | 'final'>('draft');
    const [propertyType, setPropertyType] = useState<'offplan' | 'ready-cash' | 'ready-mortgage'>('offplan');
    const [buyerType, setBuyerType] = useState<'cash' | 'mortgage'>('cash');
    const [sellerType, setSellerType] = useState<'cash' | 'mortgage'>('cash');
    
    // Form data
    const [formData, setFormData] = useState({
      sellersName: '',
      buyerName: '',
      propertyAddress: '',
      estimatedTransferDate: '',
      transferLocation: '',
      documentDate: new Date().toLocaleDateString('en-GB'),
      preparedBy: 'Mackie',
      buyerBrokerPercentage: '2',
      sellerBrokerPercentage: '2',
      purchaseDate: '',
      sellingDate: '',
      expectedRentalYield: '6',
      expectedAppreciation: '5',
      holdingPeriod: '5'
    });
    
    // Units
    const [soaUnits, setSoaUnits] = useState([{
      id: 1,
      name: '',
      sellingPrice: '',
      purchasePrice: '',
      installmentPaid: '',
      buyerPaidToDeveloper: '',
      buyerPaidToDeveloperIsPaid: false,
      folAmount: ''
    }]);
    
    // Expenses
    const [buyerExpenses, setBuyerExpenses] = useState<any[]>([]);
    const [sellerExpenses, setSellerExpenses] = useState<any[]>([]);
    const [customBuyerBrokerFee, setCustomBuyerBrokerFee] = useState('');
    const [customSellerBrokerFee, setCustomSellerBrokerFee] = useState('');
    const [sellerHasUAEBankAccount, setSellerHasUAEBankAccount] = useState(true);
    
    // Auto-add Sales Progression Fee
    useEffect(() => {
      if (selectedRole === 'buyer') {
        setBuyerExpenses(prev => {
          let filtered = prev.filter(exp => exp.id !== 'sales-progression-auto');
          const salesProgressionAmount = getSalesProgressionFee();
          filtered.push({
            id: 'sales-progression-auto',
            expenseType: 'sales-progression',
            description: 'Sales Progression Fee incl. 5% VAT',
            amount: (salesProgressionAmount % 1 === 0) ? salesProgressionAmount.toString() : salesProgressionAmount.toFixed(2),
            mode: "Manager's Cheque",
            payee: "Next Level Consulting - FZCO"
          });
          return filtered;
        });
      }
    }, [buyerType, sellerType, selectedRole]);
    
    // Auto-add Mortgage Registration Fee for ready-mortgage
    useEffect(() => {
      if (selectedRole === 'buyer' && propertyType === 'ready-mortgage') {
        setBuyerExpenses(prev => {
          const filtered = prev.filter(exp => exp.id !== 'mortgage-registration-auto');
          const totalFolAmount = soaUnits.reduce((sum, unit) => sum + (parseFloat(unit.folAmount) || 0), 0);
          let mortgageAmount = totalFolAmount > 0 ? ((totalFolAmount * 0.0025) + 290).toFixed(2) : 'TO BE CONFIRMED';
          filtered.push({
            id: 'mortgage-registration-auto',
            expenseType: 'mortgage-registration',
            description: 'DLD Mortgage Registration Fee (0.25% from FOL amount + AED 290 Admin Fee)',
            amount: mortgageAmount,
            mode: "Manager's Cheque",
            payee: "Dubai Land Department"
          });
          return filtered;
        });
      } else {
        setBuyerExpenses(prev => prev.filter(exp => exp.id !== 'mortgage-registration-auto'));
      }
    }, [propertyType, soaUnits, selectedRole]);
    
    // Calculations
    const getSalesProgressionFee = () => {
      const key = `${buyerType}-${sellerType}`;
      const fees: Record<string, number> = { 'cash-cash': 3000, 'mortgage-cash': 4500, 'cash-mortgage': 4500, 'mortgage-mortgage': 6000 };
      return (fees[key] || 3000) * 1.05;
    };
    
    const getDLDAdminFee = () => propertyType === 'offplan' ? 40 : 580;
    const getDLDTrusteeFee = () => propertyType === 'offplan' ? 5250 : 4200;
    
    const getOutstandingBalanceForNOC = () => {
      return soaUnits.reduce((sum, unit) => {
        const amount = parseFloat(unit.buyerPaidToDeveloper) || 0;
        if (amount > 0) return sum + (unit.buyerPaidToDeveloperIsPaid ? -amount : amount);
        return sum;
      }, 0);
    };
    
    const hasAnyBuyerPaidToDeveloper = () => soaUnits.some(unit => (parseFloat(unit.buyerPaidToDeveloper) || 0) > 0);
    
    const getTotalSellingPrice = () => soaUnits.reduce((sum, unit) => sum + (parseFloat(unit.sellingPrice) || 0), 0);
    const getTotalPurchasePrice = () => soaUnits.reduce((sum, unit) => sum + (parseFloat(unit.purchasePrice) || 0), 0);
    const getTotalInstallmentPaid = () => soaUnits.reduce((sum, unit) => sum + (parseFloat(unit.installmentPaid) || 0), 0);
    const getTotalOutstandingBalance = () => soaUnits.reduce((sum, unit) => sum + ((parseFloat(unit.purchasePrice) || 0) - (parseFloat(unit.installmentPaid) || 0)), 0);
    const getTotalBuyerPaidToDeveloper = () => soaUnits.reduce((sum, unit) => sum + (parseFloat(unit.buyerPaidToDeveloper) || 0), 0);
    const getNetToSeller = () => getTotalSellingPrice() - getTotalOutstandingBalance();
    const getUnitOutstandingBalance = (unit: any) => (parseFloat(unit.purchasePrice) || 0) - (parseFloat(unit.installmentPaid) || 0);
    const getUnitNetToSeller = (unit: any) => (parseFloat(unit.sellingPrice) || 0) - getUnitOutstandingBalance(unit);
    
    const getBuyerBrokerFee = () => customBuyerBrokerFee ? (parseFloat(customBuyerBrokerFee) || 0) : (getTotalSellingPrice() * ((parseFloat(formData.buyerBrokerPercentage) || 2) / 100)) * 1.05;
    const getSellerBrokerFee = () => customSellerBrokerFee ? (parseFloat(customSellerBrokerFee) || 0) : (getTotalSellingPrice() * ((parseFloat(formData.sellerBrokerPercentage) || 2) / 100)) * 1.05;
    
    const getDLDFee = () => {
      let baseAmount = 0;
      if (propertyType === 'offplan') {
        soaUnits.forEach(unit => {
          const sellingPrice = parseFloat(unit.sellingPrice) || 0;
          const purchasePrice = parseFloat(unit.purchasePrice) || 0;
          baseAmount += (sellingPrice < purchasePrice ? purchasePrice : sellingPrice) * 0.04;
        });
      } else {
        baseAmount = getTotalSellingPrice() * 0.04;
      }
      return baseAmount + (getDLDAdminFee() * soaUnits.length);
    };
    
    const calculateBuyerTotal = () => {
      let total = getNetToSeller() + getOutstandingBalanceForNOC() + getDLDFee() + (getDLDTrusteeFee() * soaUnits.length) + getBuyerBrokerFee();
      buyerExpenses.forEach(exp => {
        if (exp.amount && !isNaN(parseFloat(exp.amount))) total += parseFloat(exp.amount);
      });
      return total;
    };
    
    const calculateSellerTotalExpenses = () => {
      const totalBuyerPaidDeveloper = getTotalBuyerPaidToDeveloper();
      let totalExpenses = totalBuyerPaidDeveloper > 0 ? totalBuyerPaidDeveloper : 0;
      totalExpenses += getSellerBrokerFee();
      sellerExpenses.forEach(exp => {
        if (exp.amount && !isNaN(parseFloat(exp.amount))) totalExpenses += parseFloat(exp.amount);
      });
      return totalExpenses;
    };
    
    const getSellerFinalProceeds = () => {
      return getNetToSeller() - calculateSellerTotalExpenses();
    };
    
    // Escrow calculations
    const getBuyerEscrowFeeWithConversion = () => (calculateBuyerTotal() * 0.0025) + 6300;
    const getBuyerEscrowFeeWithoutConversion = () => (calculateBuyerTotal() * 0.01) + 6300;
    const getSellerEscrowBase = () => sellerHasUAEBankAccount ? getNetToSeller() + calculateSellerTotalExpenses() : getSellerFinalProceeds();
    const getSellerEscrowFeeWithConversion = () => (getSellerEscrowBase() * 0.0025) + 6300;
    const getSellerEscrowFeeWithoutConversion = () => (getSellerEscrowBase() * 0.01) + 6300;
    
    // ROI Calculations
    const calculateSellerROI = () => {
      const totalInvestment = getTotalInstallmentPaid();
      const netProceeds = getSellerFinalProceeds();
      const profit = netProceeds - totalInvestment;
      const roi = totalInvestment > 0 ? (profit / totalInvestment) * 100 : 0;
      
      let holdingYears = 0;
      if (formData.purchaseDate && formData.sellingDate) {
        const purchase = new Date(formData.purchaseDate);
        const selling = new Date(formData.sellingDate);
        holdingYears = (selling.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 365);
      }
      
      const annualizedROI = holdingYears > 0 ? roi / holdingYears : roi;
      
      return { totalInvestment, grossProceeds: getTotalSellingPrice(), totalExpenses: calculateSellerTotalExpenses() + getTotalOutstandingBalance(), netProceeds, profit, roi, holdingYears, annualizedROI };
    };
    
    const calculateBuyerROI = () => {
      const purchasePrice = getTotalSellingPrice();
      const totalAcquisitionCost = calculateBuyerTotal();
      const holdingPeriod = parseFloat(formData.holdingPeriod) || 5;
      const annualAppreciation = parseFloat(formData.expectedAppreciation) || 5;
      const annualRentalYield = parseFloat(formData.expectedRentalYield) || 6;
      
      const dldFee = getDLDFee();
      const trusteeFee = getDLDTrusteeFee() * soaUnits.length;
      const brokerFee = getBuyerBrokerFee();
      const otherExpenses = buyerExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
      const totalAcquisitionExpenses = dldFee + trusteeFee + brokerFee + otherExpenses;
      
      const isOffplan = propertyType === 'offplan';
      const annualRentalIncome = isOffplan ? 0 : purchasePrice * (annualRentalYield / 100);
      const totalRentalIncome = annualRentalIncome * holdingPeriod;
      
      const futurePropertyValue = purchasePrice * Math.pow(1 + (annualAppreciation / 100), holdingPeriod);
      const capitalGain = futurePropertyValue - purchasePrice;
      const estimatedSellingCosts = futurePropertyValue * 0.06;
      const netCapitalGain = capitalGain - estimatedSellingCosts;
      const totalReturns = totalRentalIncome + netCapitalGain;
      const netProfit = totalReturns - totalAcquisitionExpenses;
      const totalROI = (netProfit / totalAcquisitionCost) * 100;
      const annualizedROI = totalROI / holdingPeriod;
      
      return { purchasePrice, totalAcquisitionCost, dldFee, trusteeFee, brokerFee, otherExpenses, totalAcquisitionExpenses, isOffplan, holdingPeriod, annualRentalIncome, totalRentalIncome, annualAppreciation, futurePropertyValue, capitalGain, estimatedSellingCosts, netCapitalGain, totalReturns, netProfit, totalROI, annualizedROI };
    };
    
    // Formatting
    const formatCurrency = (amount: number) => {
      if (!amount && amount !== 0) return '';
      return new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    };
    
    const formatNOCBalance = (amount: number) => {
      if (!amount && amount !== 0) return '';
      const absAmount = Math.abs(amount);
      const formatted = formatCurrency(absAmount);
      return amount < 0 ? `-${formatted}` : formatted;
    };
    
    // Unit management
    const addSoaUnit = () => setSoaUnits([...soaUnits, {
      id: Date.now(),
      name: '',
      sellingPrice: '',
      purchasePrice: '',
      installmentPaid: '',
      buyerPaidToDeveloper: '',
      buyerPaidToDeveloperIsPaid: false,
      folAmount: ''
    }]);
    
    const removeSoaUnit = (id: number) => {
      if (soaUnits.length > 1) setSoaUnits(soaUnits.filter(unit => unit.id !== id));
    };
    
    const updateSoaUnit = (id: number, field: string, value: any) => {
      setSoaUnits(soaUnits.map(unit => unit.id === id ? { ...unit, [field]: value } : unit));
    };
    
    // Expense management
    const addExpense = (type: 'buyer' | 'seller') => {
      const newExpense = {
        id: Date.now(),
        expenseType: 'poa-no-eid',
        description: 'POA Fee (no Emirates ID) incl. 5% VAT',
        amount: '4200',
        mode: "Manager's Cheque",
        payee: "Next Level Consulting - FZCO"
      };
      if (type === 'buyer') {
        setBuyerExpenses([...buyerExpenses, newExpense]);
      } else {
        setSellerExpenses([...sellerExpenses, newExpense]);
      }
    };
    
    const removeExpense = (type: 'buyer' | 'seller', id: any) => {
      if (id === 'sales-progression-auto' || id === 'mortgage-registration-auto') return;
      if (type === 'buyer') {
        setBuyerExpenses(buyerExpenses.filter(exp => exp.id !== id));
      } else {
        setSellerExpenses(sellerExpenses.filter(exp => exp.id !== id));
      }
    };
    
    const updateExpense = (type: 'buyer' | 'seller', id: any, field: string, value: string) => {
      const presets: Record<string, any> = {
        'poa-no-eid': { description: 'POA Fee (no Emirates ID) incl. 5% VAT', amount: '4200', payee: 'Next Level Consulting - FZCO' },
        'poa-with-eid': { description: 'POA Fee (with Emirates ID) incl. 5% VAT', amount: '3150', payee: 'Next Level Consulting - FZCO' },
        'handling-admin': { description: 'Handling Admin Fee incl. 5% VAT', amount: '4198.95', payee: 'Next Level Consulting - FZCO' },
        'managers-cheque-fee': { description: "Manager's Cheque Fee (No Escrow Service) (10% Security Deposit)", amount: '1048.95', payee: 'Investmenter Real Estate LLC' },
        'other': { description: '', amount: '' }
      };
      
      const updateFn = (expenses: any[]) => expenses.map(exp => {
        if (exp.id === id) {
          if (field === 'expenseType' && presets[value]) {
            return { ...exp, expenseType: value, ...presets[value] };
          }
          return { ...exp, [field]: value };
        }
        return exp;
      });
      
      if (type === 'buyer') {
        setBuyerExpenses(updateFn);
      } else {
        setSellerExpenses(updateFn);
      }
    };
    
    // Reset
    const handleReset = () => {
      setSelectedRole(null);
      setCurrentView('form');
      setDocumentStatus('draft');
      setPropertyType('offplan');
      setBuyerType('cash');
      setSellerType('cash');
      setFormData({
        sellersName: '', buyerName: '', propertyAddress: '', estimatedTransferDate: '', transferLocation: '',
        documentDate: new Date().toLocaleDateString('en-GB'), preparedBy: 'Mackie',
        buyerBrokerPercentage: '2', sellerBrokerPercentage: '2',
        purchaseDate: '', sellingDate: '',
        expectedRentalYield: '6', expectedAppreciation: '5', holdingPeriod: '5'
      });
      setSoaUnits([{ id: 1, name: '', sellingPrice: '', purchasePrice: '', installmentPaid: '', buyerPaidToDeveloper: '', buyerPaidToDeveloperIsPaid: false, folAmount: '' }]);
      setBuyerExpenses([]);
      setSellerExpenses([]);
      setCustomBuyerBrokerFee('');
      setCustomSellerBrokerFee('');
      setSellerHasUAEBankAccount(true);
    };
    
    // Role Selection Screen
    if (!selectedRole) {
      return (
        <div className="min-h-screen bg-gray-50">
          <MobileHeader title="ROI Calculator" />
          <div className="p-4">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#D6B160] to-[#B8963D] rounded-full flex items-center justify-center mx-auto mb-4">
                <Calculator className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('roiCalculatorTitle')}</h2>
              <p className="text-gray-600 text-sm mb-6">Select your role to generate Statement of Account</p>
              
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedRole('buyer')}
                  className="w-full p-5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <Home size={32} className="text-blue-500" />
                    <div>
                      <h3 className="font-bold text-gray-900">I am a Buyer</h3>
                      <p className="text-sm text-gray-600">Calculate acquisition costs & projected ROI</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => setSelectedRole('seller')}
                  className="w-full p-5 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border-2 border-green-200 hover:border-green-400 transition-all text-left"
                >
                  <div className="flex items-center gap-4">
                    <DollarSign size={32} className="text-green-500" />
                    <div>
                      <h3 className="font-bold text-gray-900">I am a Seller</h3>
                      <p className="text-sm text-gray-600">Calculate proceeds & realized ROI</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Features */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Features Include:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Offplan, Ready Cash & Ready Mortgage</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />DLD fees, Trustee fees & Broker commissions</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Sales Progression Fee calculator</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Escrow fee estimation</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />ROI analysis with annualized returns</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }
    
    // Render Form
    const renderForm = () => (
      <div className="space-y-4">
        {/* Document Status */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Document Status</h3>
          <div className="flex gap-3">
            <button
              onClick={() => setDocumentStatus('draft')}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${documentStatus === 'draft' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
            ><PenLine size={16} className="inline mr-1" /> DRAFT</button>
            <button
              onClick={() => setDocumentStatus('final')}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${documentStatus === 'final' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'}`}
            ><CheckCircle size={16} className="inline mr-1" /> FINAL</button>
          </div>
        </div>
        
        {/* Property Type */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Property Type</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'offplan', label: 'Offplan' },
              { value: 'ready-cash', label: 'Ready/Cash' },
              { value: 'ready-mortgage', label: 'Ready/Mortgage' }
            ].map(type => (
              <button
                key={type.value}
                onClick={() => setPropertyType(type.value as any)}
                className={`py-3 rounded-xl text-sm font-medium transition-all ${propertyType === type.value ? 'bg-[#D6B160] text-white' : 'bg-gray-100 text-gray-600'}`}
              >{type.label}</button>
            ))}
          </div>
        </div>
        
        {/* Transaction Type */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Transaction Type</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Buyer Type</label>
              <div className="flex gap-2">
                <button onClick={() => setBuyerType('cash')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${buyerType === 'cash' ? 'bg-[#D6B160] text-white' : 'bg-gray-100'}`}>Cash</button>
                <button onClick={() => setBuyerType('mortgage')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${buyerType === 'mortgage' ? 'bg-[#D6B160] text-white' : 'bg-gray-100'}`}>Mortgage</button>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-2 block">Seller Type</label>
              <div className="flex gap-2">
                <button onClick={() => setSellerType('cash')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${sellerType === 'cash' ? 'bg-[#D6B160] text-white' : 'bg-gray-100'}`}>Cash</button>
                <button onClick={() => setSellerType('mortgage')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${sellerType === 'mortgage' ? 'bg-[#D6B160] text-white' : 'bg-gray-100'}`}>Mortgage</button>
              </div>
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800"><strong>Sales Progression Fee:</strong> AED {formatCurrency(getSalesProgressionFee())}</p>
          </div>
        </div>
        
        {/* Basic Information */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Basic Information</h3>
          <div className="space-y-3">
            <input type="text" placeholder="Seller's Name" value={formData.sellersName} onChange={(e) => setFormData({...formData, sellersName: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D6B160]" />
            <input type="text" placeholder="Buyer's Name" value={formData.buyerName} onChange={(e) => setFormData({...formData, buyerName: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D6B160]" />
            <textarea placeholder="Property Address" rows={2} value={formData.propertyAddress} onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D6B160]" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Transfer Date" value={formData.estimatedTransferDate} onChange={(e) => setFormData({...formData, estimatedTransferDate: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D6B160]" />
              <input type="text" placeholder="Broker % (e.g. 2)" value={selectedRole === 'buyer' ? formData.buyerBrokerPercentage : formData.sellerBrokerPercentage} onChange={(e) => setFormData({...formData, [selectedRole === 'buyer' ? 'buyerBrokerPercentage' : 'sellerBrokerPercentage']: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D6B160]" />
            </div>
          </div>
        </div>
        
        {/* ROI Parameters */}
        {selectedRole === 'buyer' && (
          <div className="bg-white rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-3">ROI Parameters</h3>
            {propertyType === 'offplan' && (
              <div className="p-3 bg-amber-50 rounded-lg mb-3">
                <p className="text-sm text-amber-800"><Info size={14} className="inline mr-1" /> Offplan: No rental income until handover. ROI based on appreciation only.</p>
              </div>
            )}
            <div className="space-y-3">
              {propertyType !== 'offplan' && (
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Expected Rental Yield (%)</label>
                  <input type="number" step="0.5" value={formData.expectedRentalYield} onChange={(e) => setFormData({...formData, expectedRentalYield: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Annual Appreciation (%)</label>
                  <input type="number" step="0.5" value={formData.expectedAppreciation} onChange={(e) => setFormData({...formData, expectedAppreciation: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Holding Period (Years)</label>
                  <input type="number" min="1" max="30" value={formData.holdingPeriod} onChange={(e) => setFormData({...formData, holdingPeriod: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {selectedRole === 'seller' && (
          <div className="bg-white rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-3">ROI Dates (Optional)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Purchase Date</label>
                <input type="date" value={formData.purchaseDate} onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Selling Date</label>
                <input type="date" value={formData.sellingDate} onChange={(e) => setFormData({...formData, sellingDate: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl" />
              </div>
            </div>
          </div>
        )}
        
        {/* Property Units */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Property Units & Pricing</h3>
          {soaUnits.map((unit, index) => (
            <div key={unit.id} className="p-4 bg-gray-50 rounded-xl mb-3">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-gray-800">Unit {index + 1}</h4>
                {soaUnits.length > 1 && (
                  <button onClick={() => removeSoaUnit(unit.id)} className="text-red-500 text-sm font-medium">Remove</button>
                )}
              </div>
              <div className="space-y-3">
                <input type="text" placeholder="Unit Name (optional)" value={unit.name} onChange={(e) => updateSoaUnit(unit.id, 'name', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder="Purchase Price" value={unit.purchasePrice} onChange={(e) => updateSoaUnit(unit.id, 'purchasePrice', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                  <input type="number" placeholder="Installment Paid" value={unit.installmentPaid} onChange={(e) => updateSoaUnit(unit.id, 'installmentPaid', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-gray-600">Outstanding Balance</p>
                  <p className="font-bold text-amber-700">AED {formatCurrency(getUnitOutstandingBalance(unit))}</p>
                </div>
                <input type="number" placeholder="Selling Price" value={unit.sellingPrice} onChange={(e) => updateSoaUnit(unit.id, 'sellingPrice', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                <input type="number" placeholder="Buyer Paid to Developer (optional)" value={unit.buyerPaidToDeveloper} onChange={(e) => updateSoaUnit(unit.id, 'buyerPaidToDeveloper', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                {propertyType === 'ready-mortgage' && selectedRole === 'buyer' && (
                  <input type="number" placeholder="FOL Amount (Mortgage)" value={unit.folAmount} onChange={(e) => updateSoaUnit(unit.id, 'folAmount', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm" />
                )}
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-600">Net to Seller</p>
                  <p className="font-bold text-green-700">AED {formatCurrency(getUnitNetToSeller(unit))}</p>
                </div>
              </div>
            </div>
          ))}
          <button onClick={addSoaUnit} className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200">+ Add Another Unit</button>
        </div>
        
        {/* Additional Expenses */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">{selectedRole === 'buyer' ? 'Buyer' : 'Seller'} Expenses</h3>
          
          {/* Fixed expenses display */}
          <div className="space-y-2 mb-4">
            {selectedRole === 'buyer' && (
              <>
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm">Net to Seller</span>
                  <span className="font-semibold">AED {formatCurrency(getNetToSeller())}</span>
                </div>
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm">4% DLD Fee + Admin</span>
                  <span className="font-semibold">AED {formatCurrency(getDLDFee())}</span>
                </div>
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm">DLD Trustee Fee</span>
                  <span className="font-semibold">AED {formatCurrency(getDLDTrusteeFee() * soaUnits.length)}</span>
                </div>
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm">Broker Fee ({formData.buyerBrokerPercentage}% + VAT)</span>
                  <span className="font-semibold">AED {formatCurrency(getBuyerBrokerFee())}</span>
                </div>
              </>
            )}
            {selectedRole === 'seller' && (
              <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm">Broker Fee ({formData.sellerBrokerPercentage}% + VAT)</span>
                <span className="font-semibold">AED {formatCurrency(getSellerBrokerFee())}</span>
              </div>
            )}
          </div>
          
          {/* Auto expenses */}
          {(selectedRole === 'buyer' ? buyerExpenses : sellerExpenses).filter(exp => ['sales-progression-auto', 'mortgage-registration-auto'].includes(exp.id)).map(exp => (
            <div key={exp.id} className="flex justify-between p-3 bg-amber-50 rounded-lg mb-2">
              <span className="text-sm">{exp.description}</span>
              <span className="font-semibold">AED {exp.amount}</span>
            </div>
          ))}
          
          {/* Custom expenses */}
          {(selectedRole === 'buyer' ? buyerExpenses : sellerExpenses).filter(exp => !['sales-progression-auto', 'mortgage-registration-auto'].includes(exp.id)).map(exp => (
            <div key={exp.id} className="p-3 bg-gray-50 rounded-lg mb-2">
              <div className="flex justify-between items-center mb-2">
                <select value={exp.expenseType} onChange={(e) => updateExpense(selectedRole!, exp.id, 'expenseType', e.target.value)} className="flex-1 px-2 py-1 border rounded text-sm mr-2">
                  <option value="poa-no-eid">POA (no EID)</option>
                  <option value="poa-with-eid">POA (with EID)</option>
                  <option value="handling-admin">Handling Admin Fee</option>
                  <option value="managers-cheque-fee">Manager's Cheque Fee</option>
                  <option value="other">Other</option>
                </select>
                <button onClick={() => removeExpense(selectedRole!, exp.id)} className="text-red-500 text-sm">✕</button>
              </div>
              {exp.expenseType === 'other' && (
                <input type="text" placeholder="Description" value={exp.description} onChange={(e) => updateExpense(selectedRole!, exp.id, 'description', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-2" />
              )}
              <input type="number" placeholder="Amount" value={exp.amount} onChange={(e) => updateExpense(selectedRole!, exp.id, 'amount', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
            </div>
          ))}
          
          <button onClick={() => addExpense(selectedRole!)} className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200 mt-2">+ Add Expense</button>
        </div>
        
        {/* Summary */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
          <h3 className="text-[#D6B160] font-semibold mb-4">Quick Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-gray-300">Total Selling Price</span><span>AED {formatCurrency(getTotalSellingPrice())}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">Net to Seller</span><span>AED {formatCurrency(getNetToSeller())}</span></div>
            <div className="border-t border-gray-700 my-3"></div>
            {selectedRole === 'buyer' ? (
              <div className="flex justify-between text-lg"><span className="font-semibold">Total Buyer Needs</span><span className="text-[#D6B160] font-bold">AED {formatCurrency(calculateBuyerTotal())}</span></div>
            ) : (
              <div className="flex justify-between text-lg"><span className="font-semibold">Seller Final Proceeds</span><span className="text-[#D6B160] font-bold">AED {formatCurrency(getSellerFinalProceeds())}</span></div>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3">
          <button onClick={() => setCurrentView('preview')} className="flex-1 py-4 bg-[#D6B160] text-white rounded-xl font-semibold">Generate SOA</button>
          <button onClick={handleReset} className="py-4 px-6 bg-gray-200 text-gray-700 rounded-xl font-medium">Reset</button>
        </div>
      </div>
    );
    
    // Render ROI Analysis
    const renderROI = () => {
      if (selectedRole === 'seller') {
        const roi = calculateSellerROI();
        const isProfit = roi.profit >= 0;
        
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Seller ROI Analysis</h3>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className={`p-4 rounded-xl text-center ${isProfit ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-gray-600 mb-1">Profit/Loss</p>
                  <p className={`text-lg font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>{isProfit ? '+' : ''}AED {formatCurrency(roi.profit)}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${isProfit ? 'bg-green-50 border-2 border-green-300' : 'bg-red-50 border-2 border-red-300'}`}>
                  <p className="text-xs text-gray-600 mb-1">ROI</p>
                  <p className={`text-xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>{roi.roi.toFixed(1)}%</p>
                </div>
                <div className="p-4 rounded-xl text-center bg-gray-50">
                  <p className="text-xs text-gray-600 mb-1">Annualized</p>
                  <p className="text-lg font-bold text-gray-900">{roi.holdingYears > 0 ? `${roi.annualizedROI.toFixed(1)}%` : 'N/A'}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Your Investment</span><span className="font-semibold">AED {formatCurrency(roi.totalInvestment)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Gross Selling Price</span><span className="font-semibold">AED {formatCurrency(roi.grossProceeds)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Total Expenses</span><span className="font-semibold text-red-600">-AED {formatCurrency(roi.totalExpenses)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Net Proceeds</span><span className="font-semibold">AED {formatCurrency(roi.netProceeds)}</span></div>
                <div className="flex justify-between py-3 bg-gray-50 rounded-lg px-3">
                  <span className="font-semibold">Total {isProfit ? 'Profit' : 'Loss'}</span>
                  <span className={`font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>{isProfit ? '+' : ''}AED {formatCurrency(roi.profit)}</span>
                </div>
              </div>
              
              {roi.holdingYears > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800"><strong>Holding Period:</strong> {roi.holdingYears.toFixed(1)} years</p>
                  <p className="text-sm text-blue-800"><strong>Annualized Return:</strong> {roi.annualizedROI.toFixed(2)}% per year</p>
                </div>
              )}
            </div>
          </div>
        );
      } else {
        const roi = calculateBuyerROI();
        const isPositive = roi.netProfit >= 0;
        
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Buyer Projected ROI</h3>
              <p className="text-sm text-gray-500 mb-4">{roi.isOffplan ? 'Offplan (appreciation only)' : `${roi.holdingPeriod} year projection`}</p>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className={`p-4 rounded-xl text-center ${isPositive ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-gray-600 mb-1">Net Profit</p>
                  <p className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}{formatCurrency(roi.netProfit)}</p>
                </div>
                <div className={`p-4 rounded-xl text-center ${isPositive ? 'bg-green-50 border-2 border-green-300' : 'bg-red-50 border-2 border-red-300'}`}>
                  <p className="text-xs text-gray-600 mb-1">Total ROI</p>
                  <p className={`text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{roi.totalROI.toFixed(1)}%</p>
                </div>
                <div className="p-4 rounded-xl text-center bg-gray-50">
                  <p className="text-xs text-gray-600 mb-1">Annual ROI</p>
                  <p className="text-lg font-bold text-gray-900">{roi.annualizedROI.toFixed(1)}%</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Acquisition Costs</h4>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Purchase Price</span><span className="font-semibold">AED {formatCurrency(roi.purchasePrice)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">DLD + Trustee + Broker</span><span className="font-semibold">AED {formatCurrency(roi.totalAcquisitionExpenses)}</span></div>
                <div className="flex justify-between py-2 bg-gray-50 rounded-lg px-3"><span className="font-semibold">{t('totalInvestment')}</span><span className="font-bold">AED {formatCurrency(roi.totalAcquisitionCost)}</span></div>
                
                <h4 className="font-semibold text-gray-800 mt-4">Projected Returns ({roi.holdingPeriod} Years)</h4>
                {!roi.isOffplan && (
                  <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Total Rental Income</span><span className="font-semibold text-green-600">+AED {formatCurrency(roi.totalRentalIncome)}</span></div>
                )}
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Future Property Value</span><span className="font-semibold">AED {formatCurrency(roi.futurePropertyValue)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Capital Gain</span><span className="font-semibold text-green-600">+AED {formatCurrency(roi.capitalGain)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Selling Costs (6%)</span><span className="font-semibold text-red-600">-AED {formatCurrency(roi.estimatedSellingCosts)}</span></div>
                <div className="flex justify-between py-3 bg-gray-50 rounded-lg px-3">
                  <span className="font-semibold">Net Profit</span>
                  <span className={`font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>{isPositive ? '+' : ''}AED {formatCurrency(roi.netProfit)}</span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-800"><AlertCircle size={12} className="inline mr-1" /> These projections are estimates based on the parameters entered. Actual returns may vary.</p>
              </div>
            </div>
          </div>
        );
      }
    };
    
    // Render Preview
    const renderPreview = () => (
      <div className="space-y-4">
        <div className={`p-3 rounded-xl ${documentStatus === 'draft' ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
          <p className={`text-sm font-semibold ${documentStatus === 'draft' ? 'text-amber-800' : 'text-green-800'}`}>
            {documentStatus === 'draft' ? <><PenLine size={14} className="inline mr-1" /> DRAFT Statement</> : <><CheckCircle size={14} className="inline mr-1" /> FINAL Statement</>} Generated
          </p>
        </div>
        
        {/* SOA Preview */}
        <div className="bg-white rounded-xl p-5 relative overflow-hidden">
          {documentStatus === 'draft' && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-[-45deg] text-6xl font-bold text-red-200 opacity-30 pointer-events-none select-none z-0">DRAFT</div>
          )}
          
          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-6">
              <img src="https://i.imgur.com/iyarNKS.png" alt="Asset Desk" className="h-12 mx-auto mb-3" />
              <h2 className="text-lg font-bold uppercase tracking-wide">{selectedRole === 'buyer' ? 'Buyer' : 'Seller'} Statement of Account</h2>
            </div>
            
            {/* Info */}
            <div className="text-sm space-y-1 mb-4">
              <div className="flex"><span className="w-40 font-medium">Seller's Name:</span><span className="text-gray-600">{formData.sellersName || '-'}</span></div>
              <div className="flex"><span className="w-40 font-medium">Buyer's Name:</span><span className="text-gray-600">{formData.buyerName || '-'}</span></div>
              <div className="flex"><span className="w-40 font-medium">Property Address:</span><span className="text-gray-600">{formData.propertyAddress || '-'}</span></div>
              <div className="flex"><span className="w-40 font-medium">Transfer Date:</span><span className="text-gray-600">{formData.estimatedTransferDate || 'TO BE CONFIRMED'}</span></div>
              <div className="flex"><span className="w-40 font-medium">Selling Price:</span><span className="text-gray-600">AED {formatCurrency(getTotalSellingPrice())}</span></div>
            </div>
            
            {/* Expenses Table */}
            <div className="border rounded-lg overflow-hidden mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#D6B160] text-white">
                    <th className="py-2 px-3 text-left">Description</th>
                    <th className="py-2 px-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRole === 'buyer' ? (
                    <>
                      {soaUnits.map((unit, i) => (
                        <tr key={unit.id} className="border-b"><td className="py-2 px-3">Net to Seller{unit.name ? ` - ${unit.name}` : soaUnits.length > 1 ? ` - Unit ${i+1}` : ''}</td><td className="py-2 px-3 text-right">{formatCurrency(getUnitNetToSeller(unit))}</td></tr>
                      ))}
                      {hasAnyBuyerPaidToDeveloper() && (
                        <tr className="border-b bg-amber-50"><td className="py-2 px-3">Outstanding balance for NOC</td><td className="py-2 px-3 text-right">{formatNOCBalance(getOutstandingBalanceForNOC())}</td></tr>
                      )}
                      <tr className="border-b"><td className="py-2 px-3">4% DLD Fee + Admin</td><td className="py-2 px-3 text-right">{formatCurrency(getDLDFee())}</td></tr>
                      <tr className="border-b"><td className="py-2 px-3">DLD Trustee Fee</td><td className="py-2 px-3 text-right">{formatCurrency(getDLDTrusteeFee() * soaUnits.length)}</td></tr>
                      <tr className="border-b"><td className="py-2 px-3">Broker's Fee incl. VAT ({formData.buyerBrokerPercentage}%)</td><td className="py-2 px-3 text-right">{formatCurrency(getBuyerBrokerFee())}</td></tr>
                      {buyerExpenses.map(exp => (
                        <tr key={exp.id} className="border-b"><td className="py-2 px-3">{exp.description}</td><td className="py-2 px-3 text-right">{exp.amount}</td></tr>
                      ))}
                      <tr className="bg-yellow-200 font-bold"><td className="py-2 px-3">Total amount to complete transfer</td><td className="py-2 px-3 text-right">AED {formatCurrency(calculateBuyerTotal())}</td></tr>
                    </>
                  ) : (
                    <>
                      {soaUnits.map((unit, i) => (
                        <tr key={unit.id} className="border-b"><td className="py-2 px-3">Net to Seller{unit.name ? ` - ${unit.name}` : soaUnits.length > 1 ? ` - Unit ${i+1}` : ''}</td><td className="py-2 px-3 text-right">{formatCurrency(getUnitNetToSeller(unit))}</td></tr>
                      ))}
                      <tr className="border-b font-semibold"><td className="py-2 px-3">Total Sales Proceeds</td><td className="py-2 px-3 text-right">{formatCurrency(getNetToSeller())}</td></tr>
                      <tr className="border-b bg-gray-100"><td className="py-2 px-3 font-semibold" colSpan={2}>Less Expenses:</td></tr>
                      {getTotalBuyerPaidToDeveloper() > 0 && (
                        <tr className="border-b"><td className="py-2 px-3">Amount paid by Buyer to Developer</td><td className="py-2 px-3 text-right">{formatCurrency(getTotalBuyerPaidToDeveloper())}</td></tr>
                      )}
                      <tr className="border-b"><td className="py-2 px-3">Broker's Fee incl. VAT ({formData.sellerBrokerPercentage}%)</td><td className="py-2 px-3 text-right">{formatCurrency(getSellerBrokerFee())}</td></tr>
                      {sellerExpenses.map(exp => (
                        <tr key={exp.id} className="border-b"><td className="py-2 px-3">{exp.description}</td><td className="py-2 px-3 text-right">{exp.amount}</td></tr>
                      ))}
                      <tr className="bg-yellow-200 font-bold"><td className="py-2 px-3">Final Sales Proceeds</td><td className="py-2 px-3 text-right">AED {formatCurrency(getSellerFinalProceeds())}</td></tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Escrow Info */}
            <div className="text-xs bg-gray-50 rounded-lg p-3">
              <h4 className="font-semibold mb-2">Estimated Escrow Fees:</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>With currency conversion (0.25%)</span>
                  <span className="font-semibold">AED {formatCurrency(selectedRole === 'buyer' ? getBuyerEscrowFeeWithConversion() : getSellerEscrowFeeWithConversion())}</span>
                </div>
                <div className="flex justify-between">
                  <span>Without conversion (1%)</span>
                  <span className="font-semibold">AED {formatCurrency(selectedRole === 'buyer' ? getBuyerEscrowFeeWithoutConversion() : getSellerEscrowFeeWithoutConversion())}</span>
                </div>
              </div>
            </div>
            
            {documentStatus === 'draft' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-800 font-semibold"><AlertCircle size={12} className="inline mr-1" /> DRAFT STATEMENT - All amounts are ESTIMATES ONLY and NOT FINAL.</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Note about PDF */}
        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800"><Lightbulb size={14} className="inline mr-1" /> To download as PDF, use the external calculator or take a screenshot of this preview.</p>
        </div>
      </div>
    );
    
    // Main Calculator Layout
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <MobileHeader title="ROI Calculator" />
        
        {/* Role Badge & Nav */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{selectedRole === 'buyer' ? <Home size={20} /> : <DollarSign size={20} />}</span>
                <span className="font-semibold text-gray-900">{selectedRole === 'buyer' ? 'Buyer' : 'Seller'} Calculator</span>
              </div>
              <button onClick={handleReset} className="text-sm text-[#D6B160] font-medium">Change Role</button>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-gray-100 rounded-xl p-1">
              {(['form', 'preview', 'roi'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setCurrentView(tab)}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${currentView === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'}`}
                >
                  {tab === 'form' ? <><PenLine size={14} className="inline mr-1" /> Form</> : tab === 'preview' ? <><Eye size={14} className="inline mr-1" /> Preview</> : <><BarChart3 size={14} className="inline mr-1" /> ROI</>}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {currentView === 'form' && renderForm()}
          {currentView === 'preview' && renderPreview()}
          {currentView === 'roi' && renderROI()}
        </div>
      </div>
    );
  };

  // News View
  const NewsView = () => {
    const [articles, setArticles] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const fetchNews = async () => {
        setLoading(true);
        setError(null);
        try {
          const newsData = await makeAPIRequest('getNews'); 

          if (newsData && newsData.articles) {
            setArticles(newsData.articles.slice(0, 10));
          } else {
            throw new Error('No articles were found.');
          }

        } catch (err: any) {
          setError(t('ipFailedFetchNews'));
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchNews();
    }, []);

    const renderContent = () => {
      if (loading) {
        return <div className="text-center py-10"><p className="text-gray-600">{t('loading')}</p></div>;
      }
      if (error) {
        return <div className="text-center py-10 bg-red-50 text-red-700 rounded-lg"><p>{error}</p></div>;
      }
      return (
        <div className="space-y-6">
          {articles.length > 0 ? (
            articles.map((article, index) => (
              <article key={index} className="border-b pb-4 last:border-b-0">
                <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600">
                  <a href={article.url} target="_blank" rel="noopener noreferrer">
                    {article.title}
                  </a>
                </h3>
                <p className="text-gray-600 text-sm mb-2">
                  {new Date(article.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} • {article.source.name}
                </p>
                <p className="text-gray-700">{article.description}</p>
              </article>
            ))
          ) : (
            <div className="text-center py-10"><p className="text-gray-600">{t('ipNoRecentNews')}</p></div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900">{t('dubaiRealEstateNews')}</CardTitle>
            <CardDescription>{t('latestNewsSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Webinar View
  const WebinarView = () => {
    return (
      <div className="webinar-container">
        <iframe
          src="https://start.investmenter.de/webinar?utm_source=InvestMenterApp&utm_medium=organic&utm_campaign=LiveWebinar"
          title="Asset Desk Webinar"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

// Properties Overview View
  const SupportView = () => {
    const { t } = useLanguage();
  const [isConnecting, setIsConnecting] = useState(false);
  const [localChatInput, setLocalChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // WATI WhatsApp Business number
  const WHATSAPP_BUSINESS_NUMBER = '971800636837';
  
  // Get account manager name for routing
  const accountManager = currentInvestor?.leadOwner || currentInvestor?.executiveAssistant || t('ipSupportTeam');
  
  // Helper function to open URLs on both web and mobile
  const openExternalUrl = (url: string) => {
    if (typeof window !== 'undefined') {
      // For tel: and mailto: links
      if (url.startsWith('tel:') || url.startsWith('mailto:')) {
        window.location.href = url;
        return;
      }
      
      // For WhatsApp - use direct location change for mobile compatibility
      if (url.includes('wa.me')) {
        window.location.href = url;
        return;
      }
      
      // For other links
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Generate WhatsApp link with investor context for team routing
  const generateWhatsAppLink = () => {
    const investorName = currentInvestor?.name || 'Investor';
    const investorId = currentInvestor?.id || '';
    const investorEmail = currentInvestor?.email || '';
    
    // This message format helps WATI route to the correct advisor
    const message = `Hello Asset Desk Team,

I need assistance with my investment account.

────────────────────
Investor: ${investorName}
Account ID: ${investorEmail || investorId}
Account Manager: ${accountManager}
────────────────────

`;
    
    return `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  // Handle WhatsApp click
  const handleWhatsAppClick = () => {
    setIsConnecting(true);
    const link = generateWhatsAppLink();
    
    // Small delay to show connecting state
    setTimeout(() => {
      openExternalUrl(link);
      setIsConnecting(false);
    }, 500);
  };

  useEffect(() => {
  if (showChatbot) {
    document.body.classList.add('chatbot-fullscreen-open');
  } else {
    document.body.classList.remove('chatbot-fullscreen-open');
  }
  
  return () => {
    document.body.classList.remove('chatbot-fullscreen-open');
  };
}, [showChatbot]);

// Handle iOS keyboard resize
useEffect(() => {
  if (!showChatbot) return;

  const handleViewportResize = () => {
    const container = document.querySelector('.chatbot-fullscreen-container') as HTMLElement;
    if (container && window.visualViewport) {
      container.style.height = `${window.visualViewport.height}px`;
    }
  };

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportResize);
    handleViewportResize();
  }

  return () => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
    }
  };
}, [showChatbot]);

  // Show AI Chatbot
if (showChatbot) {
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 999999,
        height: '100dvh',
      }}
    >
      {/* HEADER - Fixed at top */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#C5A572',
        flexShrink: 0,
        minHeight: '60px',
      }}>
        <button 
          onClick={() => setShowChatbot(false)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ChevronRight size={24} style={{ transform: 'rotate(180deg)', color: '#fff' }} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Bot size={24} color="#fff" />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#fff', fontSize: '16px', fontWeight: 600 }}>{t('aiAssistantTitle')}</h3>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>{t('ipOnline')}</span>
          </div>
        </div>
        <button 
          onClick={() => setShowChatbot(false)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={24} color="#fff" />
        </button>
      </div>

      {/* MESSAGES - Scrollable middle section */}
      <div style={{ 
        flex: '1 1 auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px',
        background: '#f8f9fa',
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
      }}>
        {chatMessages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#C5A572',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Bot size={48} color="#fff" />
            </div>
            <h3 style={{ marginBottom: '8px', color: '#1f2937' }}>{t('welcomeToAI')}</h3>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              {t('aiWelcomeMessage')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>{t('ipTryAsking')}</p>
              {[
                { icon: <BarChart3 size={16} />, text: t('ipChatSuggestion1') },
                { icon: <DollarSign size={16} />, text: t('ipChatSuggestion2') },
                { icon: <FileText size={16} />, text: t('ipChatSuggestion3') },
                { icon: <Ticket size={16} />, text: t('ipChatSuggestion4') },
              ].map((item, idx) => (
                <button 
                  key={idx}
                  onClick={() => {
                    sendChatMessage(item.text);
                    setLocalChatInput('');
                  }}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {item.icon} {item.text.substring(0, 30)}...
                </button>
              ))}
            </div>
            
            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>{t('needToTalkHuman')}</p>
              <button 
                onClick={handleWhatsAppClick}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#25D366',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <MessageCircle size={18} />
                {t('ipChatOnWhatsApp')}
              </button>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {chatMessages.map((msg: any, index: number) => (
          <div 
            key={msg.id || index} 
            style={{
              display: 'flex',
              justifyContent: msg.isUser ? 'flex-end' : 'flex-start',
              marginBottom: '12px',
              gap: '8px',
            }}
          >
            {!msg.isUser && (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#C5A572',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Bot size={18} color="#fff" />
              </div>
            )}
            <div style={{
              maxWidth: '75%',
              padding: '12px 16px',
              borderRadius: msg.isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              backgroundColor: msg.isUser ? '#C5A572' : '#ffffff',
              color: msg.isUser ? '#ffffff' : '#1f2937',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '14px' }}>{msg.text}</p>
              <span style={{ 
                fontSize: '11px', 
                color: msg.isUser ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                display: 'block',
                marginTop: '4px',
              }}>
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}
        
        {/* Loading indicator */}
        {isChatLoading && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#C5A572',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Bot size={18} color="#fff" />
            </div>
            <div style={{
              padding: '12px 16px',
              borderRadius: '18px 18px 18px 4px',
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'pulse 1s infinite' }}></div>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'pulse 1s infinite 0.2s' }}></div>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'pulse 1s infinite 0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={chatEndRef} />
      </div>

      {/* INPUT - Fixed at bottom */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
        background: '#ffffff',
        borderTop: '1px solid #e5e7eb',
        flexShrink: 0,
      }}>
        <input
          type="text"
          value={localChatInput}
          onChange={(e) => setLocalChatInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && localChatInput.trim() && !isChatLoading) {
              e.preventDefault();
              sendChatMessage(localChatInput);
              setLocalChatInput('');
            }
          }}
          placeholder={t('typeMessage')}
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="send"
          style={{
            flex: 1,
            padding: '12px 16px',
            border: '2px solid #C5A572',
            borderRadius: '24px',
            fontSize: '16px',
            outline: 'none',
            background: '#f9fafb',
            WebkitAppearance: 'none',
          }}
        />
        <button 
          type="button"
          disabled={!localChatInput.trim() || isChatLoading}
          onClick={() => {
            if (localChatInput.trim() && !isChatLoading) {
              sendChatMessage(localChatInput);
              setLocalChatInput('');
            }
          }}
          style={{
            width: '48px',
            height: '48px',
            minWidth: '48px',
            borderRadius: '50%',
            background: localChatInput.trim() && !isChatLoading ? '#C5A572' : '#d1d5db',
            border: 'none',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: localChatInput.trim() && !isChatLoading ? 'pointer' : 'not-allowed',
            flexShrink: 0,
          }}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
}
  
  // Premium Support View - English
  return (
    <div className="premium-support-view">
      <MobileHeader title={t('support')} onProfileClick={() => setActiveTab('profile')} />
      
      {/* Account Manager Card */}
      <div className="account-manager-card">
        <div className="manager-badge">
          <Star size={14} />
          <span>{t('yourPersonalAdvisor')}</span>
        </div>
        <div className="manager-info">
          <div className="manager-avatar">
            {accountManager.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
          </div>
          <div className="manager-details">
            <h3>{accountManager}</h3>
            <p>{t('seniorInvestmentAdvisor')}</p>
          </div>
        </div>
        <button 
          className="contact-manager-btn"
          onClick={handleWhatsAppClick}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <>
              <div className="btn-spinner"></div>
              {t('connecting')}
            </>
          ) : (
            <>
              <MessageCircle size={18} />
              {t('messageOnWhatsApp')}
            </>
          )}
        </button>
      </div>

      {/* AI Assistant Card */}
      <div className="ai-assistant-card" onClick={() => setIsChatWidgetOpen(true)}>
        <div className="ai-icon-container">
          <Bot size={24} />
        </div>
        <div className="ai-info">
          <h4>{t('aiAssistantTitle')}</h4>
          <p>{t('getInstantAnswers')}</p>
        </div>
        <ChevronRight size={20} className="ai-arrow" />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>{t('howCanWeHelp')}</h3>
        <div className="quick-actions-grid">
          <button className="quick-action-card" onClick={handleWhatsAppClick}>
            <div className="action-icon">
              <TrendingUp size={20} />
            </div>
            <span>{t('portfolio')}</span>
          </button>
          <button className="quick-action-card" onClick={handleWhatsAppClick}>
            <div className="action-icon">
              <FileText size={20} />
            </div>
            <span>{t('documents')}</span>
          </button>
          <button className="quick-action-card" onClick={handleWhatsAppClick}>
            <div className="action-icon">
              <CreditCard size={20} />
            </div>
            <span>{t('payments')}</span>
          </button>
          <button className="quick-action-card" onClick={handleWhatsAppClick}>
            <div className="action-icon">
              <Building2 size={20} />
            </div>
            <span>{t('property')}</span>
          </button>
        </div>
      </div>

      {/* Contact Options */}
      <div className="contact-section">
        <h3>{t('otherWaysToReach')}</h3>
        <div className="contact-list">
          <button 
            className="contact-item"
            onClick={() => openExternalUrl('tel:+971526177472')}
          >
            <div className="contact-icon phone">
              <Phone size={18} />
            </div>
            <div className="contact-info">
              <span className="contact-label">{t('callUs')}</span>
              <span className="contact-value">+971 52 617 7472</span>
            </div>
            <ChevronRight size={18} className="contact-arrow" />
          </button>
          
          <button 
            className="contact-item"
            onClick={() => openExternalUrl('mailto:sales@assetdesk.ae')}
          >
            <div className="contact-icon email">
              <Mail size={18} />
            </div>
            <div className="contact-info">
              <span className="contact-label">{t('email')}</span>
              <span className="contact-value">sales@assetdesk.ae</span>
            </div>
            <ChevronRight size={18} className="contact-arrow" />
          </button>
        </div>
      </div>

      {/* Business Hours Footer */}
      <div className="business-hours">
        <Clock size={14} />
        <span>{t('ipBusinessHours')}</span>
      </div>
    </div>
  );
};

  const MenuView = () => {
     const { t } = useLanguage();
    // Payments has left this list — it is the Plans half of the Money tab now.
    // The calculators are grouped as tools rather than sitting one per line
    // among the services, and Corporate Services joins the services section it
    // always belonged to instead of being a heading with a single row under it.
    // Typed rather than inferred: the rows are not all the same shape any
    // more (Messages carries an unread count), and an inferred union of
    // object literals makes reading `badge` in the loop below an error.
    const menuSections: { title: string; items: { id: string; icon: any; label: string; badge?: number }[] }[] = [
      {
        // First, above the tools: this is the one row where somebody may be
        // waiting on the owner rather than the other way round.
        title: t('omSectionTitle'),
        items: [
          { id: 'messages', icon: MessagesSquare, label: t('omTitle'), badge: unreadMessages },
          // The whole wealth, not only Dubai (Michael's Co-Pilot concept).
          { id: 'wealth', icon: Wallet, label: language === 'de' ? 'Mein Gesamtvermögen' : 'My total wealth' },
        ]
      },
      {
        title: t('ipMoreTools'),
        items: [
          { id: 'roi-calculator', icon: Calculator, label: t('roiCalculator') },
          { id: 'cashflow', icon: Calculator, label: t('cashflow') },
          // Investment Analysis left the menu (its route still renders); To-dos
          // holds every reminder, open and done, that Home only shows the top of.
          { id: 'todos', icon: ListChecks, label: language === 'de' ? 'Aufgaben' : 'To-dos' },
        ]
      },
      {
        title: t('servicesAndProperties'),
        items: [
          { id: 'marketplace', icon: Briefcase, label: t('services') },
          { id: 'IM Properties', icon: Building2, label: t('imProperties') },
          { id: 'corporate-services', icon: Shield, label: t('corporateServicesUae') },
          { id: 'company-docs', icon: Building2, label: t('companyDocs') },
        ]
      },

      // PARTNER (Cooperation / Referral) has moved out of the owner menu and
      // onto the welcome screen as its own entry — see portals/partner.
      // Same account, same session; it is simply no longer reached from here.

      {
        title: t('eventsAndNews'),
        items: [
          // Newsfeed is a bottom-bar tab now — listing it here as well would be
          // the same destination twice, two taps apart.
          // { id: 'newsfeed', icon: Newspaper, label: 'Newsfeed' },
          // { id: 'events', icon: Calendar, label: t('events') },     // HIDDEN
          // { id: 'webinar', icon: Video, label: t('webinar') },      // HIDDEN
          // { id: 'news', icon: Newspaper, label: t('news') },   // HIDDEN
        ]
      },
      {
        title: language === 'de' ? 'Support & Hilfe' : 'Support & Help',
        items: [
          { id: 'support', icon: MessageCircle, label: language === 'de' ? 'Premium Support' : 'Premium Support' },
        ]
      },
      {
        title: t('account'),
        items: [
          { id: 'profile', icon: User, label: t('myProfile') },
          // "Ihre Daten. Ihre Kontrolle." — Michael's Trust Center.
          { id: 'trust-center', icon: ShieldCheck, label: 'Trust Center' },
          { id: 'about', icon: Info, label: t('ipAboutUs') },
        ]
      }
    ];

    const handleLogoutMenu = () => {
      markSignedOut();
      localStorage.removeItem('authToken');
      localStorage.removeItem('investorId');
      localStorage.removeItem('investorEmail');
      localStorage.removeItem('investor');
      localStorage.removeItem('lastActivity');
      setIsAuthenticated(false);
      setCurrentInvestor(null);
      setUnits([]);
      setPayments([]);
      setDocuments([]);
    };

    return (
      <div className="menu-view">
        <MobileHeader title={t('menu')} onProfileClick={() => setActiveTab('profile')} />

          <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          background: '#fff',
          borderRadius: '12px',
          margin: '16px',
          marginBottom: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <div data-walkthrough="language">
            <LanguageSelector compact />
          </div>
        </div>

        {/* User Profile Card */}
        <div className="menu-section">
          <div className="user-profile-card" onClick={() => setActiveTab('profile')}>
            <div className="user-avatar-large">
              {currentInvestor?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-info">
              <h3>{currentInvestor?.name || t('investor')}</h3>
              <p>{currentInvestor?.email || 'investor@email.com'}</p>
              {/* VIP status hidden from UI - used internally only */}
            </div>
            <ChevronRight size={24} />
          </div>
        </div>

        {/* COMMUNITY SECTION - hidden */}
{false && (() => {
  const investorCommunities = communityChannels;
  
  // Get projects that don't have a community
  const projectsWithoutCommunity = units
  .filter((unit: any) => {
    const hasMatch = COMMUNITY_CHANNELS.some(channel => 
      (unit.project && channel.location && 
       unit.project.toLowerCase() === channel.location.toLowerCase()) ||
      (unit.developer && channel.developer && 
       unit.developer.toLowerCase() === channel.developer.toLowerCase())
    );
    return !hasMatch;
  })
    .map(unit => unit.project || unit.name)
    .filter((value, index, self) => self.indexOf(value) === index); // unique

  const handleRequestCommunity = (projectName: string) => {
    const message = encodeURIComponent(
      `Hallo Asset Desk Team,\n\n` +
      `Ich möchte eine Community-Gruppe für mein Projekt beantragen:\n\n` +
      `Projekt: ${projectName}\n` +
      `Investor: ${currentInvestor?.name || 'N/A'}\n` +
      `Email: ${currentInvestor?.email || 'N/A'}\n\n` +
      `Bitte erstellen Sie eine Construction Update Gruppe für dieses Projekt.\n\n` +
      `Vielen Dank!`
    );
    window.open(`https://wa.me/971526177472?text=${message}`, '_blank');
  };

  return (
    <div className="menu-section">
    <h2 className="menu-section-title">{t('community')}</h2>
      <div className="menu-card">
        {/* Show matched communities */}
        {investorCommunities.length > 0 && investorCommunities.map((community) => (
          <a
            key={community.id}
            href={community.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="menu-item"
            style={{ textDecoration: 'none' }}
          >
            <div className="menu-item-icon" style={{ backgroundColor: '#25D366' }}>
              <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div className="menu-item-content">
              <span className="menu-item-label">{community.name}</span>
            </div>
            <ChevronRight size={20} className="menu-item-arrow" />
          </a>
        ))}

        {/* Show projects without community */}
        {projectsWithoutCommunity.length > 0 && projectsWithoutCommunity.map((project) => (
          <div key={project} className="p-4 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{project}</p>
                <p className="text-xs text-gray-500">No community channel available</p>
              </div>
              <button
                onClick={() => handleRequestCommunity(project)}
                className="px-3 py-1.5 bg-[#25D366] text-white text-xs font-medium rounded-lg flex items-center gap-1"
              >
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Request
              </button>
            </div>
          </div>
        ))}

        {/* If no units at all */}
        {units.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            <p className="text-sm">Add a property to see available communities.</p>
          </div>
        )}
      </div>
    </div>
  );
})()}

        {/* Menu Sections */}
        {menuSections.map((section, sectionIndex) => (
          <div className="menu-section" key={sectionIndex}>
            <h2 className="menu-section-title">{section.title}</h2>
            <div className="menu-card">
              {section.items.map((item) => (
                <button key={item.id} className="menu-item" onClick={() => setActiveTab(item.id)}>
                  <div className="menu-item-icon">
                    <item.icon size={20} />
                  </div>
                  <div className="menu-item-content">
                    <span className="menu-item-label">{item.label}</span>
                  </div>
                  {!!item.badge && item.badge > 0 && (
                    <span className="pm-msg-dot" style={{ marginRight: 8 }}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  <ChevronRight size={20} className="menu-item-arrow" />
                </button>
              ))}
            </div>
          </div>
        ))}
{/* Admin Panel Button - Only visible to j.reyes@investmenter.de */}
    {isAdmin && (
      <div className="menu-section">
        <button 
          className="menu-item"
          onClick={() => setShowAdminPanel(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            color: '#D6B160',
            border: '1px solid #D6B160',
            borderRadius: 12,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            width: '100%',
          }}
        >
          <Bell size={20} />
          <span><Bell size={16} className="inline mr-1" /> Notification Admin</span>
          <ChevronRight size={20} style={{ marginLeft: 'auto' }} />
        </button>
      </div>
    )}

        {/* Change Role */}
        {onChangeRole && (
          <div className="menu-section">
            <button
              className="menu-item"
              onClick={onChangeRole}
              style={{ width: '100%' }}
            >
              <div className="menu-item-icon">
                <RefreshCw size={20} />
              </div>
              <div className="menu-item-content">
                <span className="menu-item-label">{t('ipChangeRole')}</span>
              </div>
              <ChevronRight size={20} className="menu-item-arrow" />
            </button>
          </div>
        )}

        {/* Logout Button */}
        <div className="menu-section">
          <button className="logout-btn" onClick={handleLogoutMenu}>
            <LogOut size={20} />
            <span>{t('logout')}</span>
          </button>
        </div>

        {/* Footer */}
        <div className="menu-footer">
          <p>Asset Desk v1.3</p>
          <p>© 2026 Asset Desk Real Estate LLC</p>
        </div>
      </div>
    );
  };

  // Marketplace View
// Final version with updated tab style and new gold card design
// Correctly scoped MarketplaceView component
// Final version with updated tab style and new gold card design
// Correctly scoped MarketplaceView component
const MarketplaceView = () => {
  const [activeTab, setActiveTab] = useState<'Corporate' | 'Conveyance' | 'Booking'>('Corporate');
  
  const [selectedServiceType, setSelectedServiceType] = useState('Corporate');
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [myAppointments, setMyAppointments] = useState<Array<{
  id: string;
  service: string;
  serviceType: string;
  date: string;
  time: string;  // Add this line
  status: string;
  meetLink: string | null;
}>>([]);

  const { Check, Clock } = require('lucide-react');

  // These variables are correctly defined within the component's scope
  const corporateServices = AVAILABLE_SERVICES.filter(s => s.category === 'other');
  const conveyanceServices = AVAILABLE_SERVICES.filter(s => s.category !== 'other');
  const servicesToDisplay = activeTab === 'Corporate' ? corporateServices : conveyanceServices;
  const servicesForSelectedType = selectedServiceType === 'Corporate' ? corporateServices : conveyanceServices;

  const availableTimes = ['10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];
  

  const Label = ({ children, ...props }: any) => <label className="block text-sm font-medium text-gray-700 mb-1" {...props}>{children}</label>;
  const Select = ({ children, ...props }: any) => <select className="w-full mt-1 p-2 border border-gray-300 rounded-md" {...props}>{children}</select>;
  const SelectItem = ({ children, ...props }: any) => <option {...props}>{children}</option>;

  const handleBookingSubmit = async () => {
  if (!selectedService || !selectedDate || !selectedTime) {
    alert(t('ipCompleteAllSteps'));
    return;
  }
  
  setIsBooking(true);
  
  const selectedServiceData = AVAILABLE_SERVICES.find(s => s.name === selectedService);

  // Create appointment object FIRST
  const newAppointment = {
  id: Date.now().toString(),
  service: `${selectedTime} - ${selectedService} (${currentInvestor!.name})`,
  serviceType: selectedServiceType,
  date: format(selectedDate, 'yyyy-MM-dd'), // Just the date
  time: selectedTime, // Just the time
  status: 'Scheduled',
  meetLink: null
};
  
  // Add to state IMMEDIATELY (before any API calls)
  setMyAppointments(prev => {
    const updated = [newAppointment, ...prev];
    return updated;
  });
  
  // Try to save to backend (but don't block the UI if it fails)
  try {
    const appointmentData = {
      investorId: currentInvestor!.id,
      investorName: currentInvestor!.name,
      investorEmail: currentInvestor!.email,
      serviceType: selectedServiceType,
      serviceName: selectedService,
      serviceDescription: selectedServiceData?.description,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: selectedTime,
    };
    
    await makeAPIRequest('createAppointment', { data: appointmentData });
  } catch (error) {
    console.warn('⚠️ Backend save failed, but appointment is still in local state:', error);
  }
  
  alert(`${t('ipAppointmentBookedPre')}${selectedService}${t('ipAppointmentBookedOn')} ${format(selectedDate, 'PPP', { locale: dateFnsLocale() })} ${t('ipAppointmentBookedAt')} ${selectedTime}.`);
  
  // Clear form
  setSelectedService('');
  setSelectedDate(undefined);
  setSelectedTime('');
  setIsBooking(false);
};
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedServiceType(e.target.value);
    setSelectedService('');
    setSelectedDate(undefined);
    setSelectedTime('');
  };

  useEffect(() => {
  const loadAppointments = async () => {
    
    if (!currentInvestor) {
      return;
    }
    
    
    try {
      const result = await makeAPIRequest('getAppointments', { 
        investorEmail: currentInvestor.email 
      });
      
      
      const appointments = Array.isArray(result) ? result : [];
      
      setMyAppointments(appointments);
      
    } catch (error) {
      console.error('❌ Failed to load appointments:', error);
      setMyAppointments([]);
    }
  };
  
  loadAppointments();
}, [currentInvestor]);

  // NEW: Handler for the "Request Meeting Link" button
  const handleRequestMeetingLink = async (appointment: any) => {
    try {
      await makeAPIRequest('requestMeetingLink', {
        appointmentId: appointment.id,
        investorName: currentInvestor!.name,
        investorEmail: currentInvestor!.email,
        serviceName: appointment.service,
        serviceType: appointment.serviceType,
        appointmentDate: appointment.date,
      });
      
      alert(t('ipMeetingLinkRequested'));
    } catch (error) {
      console.error('Failed to request meeting link:', error);
      alert(t('ipFailedSendRequest'));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <div className="flex p-1 bg-gray-100 rounded-lg">
          <button onClick={() => setActiveTab('Corporate')} className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${activeTab === 'Corporate' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}>
            {t('corporateServices')}
          </button>
          <button onClick={() => setActiveTab('Conveyance')} className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${activeTab === 'Conveyance' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}>
            {t('conveyanceServices')}
          </button>
          <button onClick={() => setActiveTab('Booking')} className={`px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors ${activeTab === 'Booking' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}>
            <Clock size={16} /> {t('bookAnAppointment')}
          </button>
        </div>
      </div>

      <div>
        {activeTab === 'Booking' ? (
          <>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('bookConsultation')}</h2>
              <p className="text-gray-600 mb-8">{t('followStepsToSchedule')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="service-type">{t('selectServiceType')}</Label>
                    <Select id="service-type" value={selectedServiceType} onChange={handleServiceTypeChange}>
                      <SelectItem value="Corporate">{t('corporateServices')}</SelectItem>
                      <SelectItem value="Conveyance">{t('conveyanceServices')}</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="service-name">{t('selectService')}</Label>
                    <Select id="service-name" value={selectedService} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedService(e.target.value)} disabled={!selectedServiceType}>
                      <SelectItem value="">{t('chooseService')}</SelectItem>
                      {servicesForSelectedType.map((service: Service) => (
                        <SelectItem key={service.id} value={service.name}>{appLanguage() === 'de' && (service as any).nameDe ? (service as any).nameDe : service.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                  {selectedDate && (
                    <div>
                      <Label>{t('selectAvailableTime')}</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {availableTimes.map(time => (
                          <button key={time} onClick={() => setSelectedTime(time)} className={`p-3 rounded-lg text-sm font-semibold transition-colors ${selectedTime === time ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>{time}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <button onClick={handleBookingSubmit} disabled={!selectedTime || isBooking} className="w-full text-black font-bold py-3 rounded-lg disabled:opacity-50 mt-4 bg-[#D6B160] hover:bg-opacity-90 transition-colors">
                    {isBooking ? t('ipBooking') : t('ipConfirmAppointment')}
                  </button>
                </div>
                <div className={`transition-opacity duration-500 ${selectedService ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                   <Label>{t('ipSelectDateStep')} {selectedDate && <span className="text-[#D6B160] font-semibold">- {format(selectedDate, 'PPP', { locale: dateFnsLocale() })}</span>}</Label>
                   <div className="flex justify-center mt-2 border rounded-lg p-2">
                     <DayPicker locale={dateFnsLocale()} mode="single" selected={selectedDate} onSelect={setSelectedDate} fromDate={new Date()} classNames={{ day_selected: 'bg-black text-white', day_today: 'font-bold text-[#D6B160]' }} />
                   </div>
                </div>
              </div>
            </div>

            {/* NEW: Section to display booked appointments */}
            {myAppointments.length > 0 && (
  <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6">
    <h3 className="text-xl font-bold text-gray-900 mb-4">{t('yourBookedAppointments')}</h3>
    <div className="space-y-4">
      {myAppointments.map(apt => (
        <div key={apt.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-bold text-lg text-gray-900">{apt.service.split(' - ')[1] || apt.service}</h4>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  apt.status === 'Scheduled' ? 'bg-green-100 text-green-700' : 
                  apt.status === 'Completed' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {trData(apt.status)}
                </span>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <p className="flex items-center gap-2">
  <Clock className="h-4 w-4" />
  {new Date(apt.date + 'T12:00:00').toLocaleDateString(appLocale(), { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })} {t('ipAtTime')} {apt.time} {t('ipDubaiTimeParen')}
</p>
                <p className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${
                    apt.serviceType === 'Corporate Services' ? 'bg-blue-500' : 'bg-green-500'
                  }`}></span>
                  {trData(apt.serviceType)}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 ml-4">
              {apt.meetLink ? (
                <a 
                  href={apt.meetLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {t('joinMeeting')}
                </a>
              ) : (
                <button
                  onClick={() => handleRequestMeetingLink(apt)}
                  className="bg-[#D6B160] hover:bg-opacity-90 text-black px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('requestMeetingLink')}
                </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-2">
            <h2 className="text-3xl font-bold mb-1">{activeTab === 'Corporate' ? t('corporateServices') : t('conveyanceServices')}</h2>
            <p className="text-gray-600 mb-6">{activeTab === 'Corporate' ? t('ipCorporateServicesDesc') : t('ipConveyanceServicesDesc')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {servicesToDisplay.map((service: Service) => (
                <div 
                  key={service.id} 
                  className="bg-[#D6B160] rounded-lg p-5 shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
                  onClick={() => {
                    const templateMsg = `Hello, I am interested in your ${service.name} service.\n\nService: ${service.name}\nDescription: ${service.description}\n\nPlease provide more information.`;
                    setChatInitialMessage(templateMsg);
                    setIsChatWidgetOpen(true);
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-black/80 rounded-full p-1 flex-shrink-0 mt-1">
                        <Check className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-black">{appLanguage() === 'de' && (service as any).nameDe ? (service as any).nameDe : service.name}</h3>
                      <p className="text-sm text-black/80 mt-1">{appLanguage() === 'de' && (service as any).descriptionDe ? (service as any).descriptionDe : service.description}</p>
                      <p className="text-xs text-black/60 mt-2 flex items-center gap-1">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {t('tapToInquireChat') || 'Tap to inquire via Chat'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
  if (!isAuthenticated) {
    return <AuthForm />;
  }

  // DEWA-Style Mobile Layout with Bottom Navigation
  return (
    <div className="mobile-app-layout">
      <AppWalkthrough
  isOpen={showWalkthrough}
  onClose={closeWalkthrough}
  onComplete={completeWalkthrough}
  onTabChange={setActiveTab}
/>
      <main className="mobile-main-content">
        {/*
          Sample-account banner. Shown on every tab, with no dismiss button, so
          a prospect exploring the app — or anyone they forward a screenshot to
          — cannot mistake the demo units for a real portfolio. The account is
          read-only server-side; this is the honest label on top of that.

          The one exception is film mode, which exists to record the product
          and runs only on a preview channel no customer can reach — see
          isFilmMode in demoMode.ts.
        */}
        {currentInvestor?.isDemo && !isFilmMode() && (
          <div style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            background: '#fffbeb', border: '1px solid #fcd34d',
            borderRadius: 10, padding: '10px 12px', marginBottom: 16,
          }}>
            <FlaskConical size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e' }}>{t('dmBannerTitle')}</div>
              <p style={{ fontSize: 12, color: '#a16207', margin: '2px 0 0', lineHeight: 1.45 }}>
                {t('dmAccountBannerText')}
              </p>
            </div>
          </div>
        )}
        {uploadingDocument && (
          <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <div className="flex items-center gap-2 text-blue-900">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              {t('ipProcessingDocumentAI')}
            </div>
          </div>
        )}

    {/* 'dashboard' is where an owner lands and where an admin lands before
        touching the rail; the admin-* ids are the rail's own destinations,
        and all of them render the console. */}
    {(activeTab === 'dashboard' || (isAdmin && !adminViewingOwn && ADMIN_TAB_VIEW[activeTab] !== undefined)) && (
  isAdmin && !adminViewingOwn ? (
    // Staff / EA account: the console IS their app. Which of its screens is
    // showing comes from the rail on the left (ADMIN_NAV), so the console is
    // in controlled mode and drops its own tab row — one navigation, not two
    // stacked on each other.
    //
    // "My view" drops them into their OWN owner dashboard, because a manager
    // can also be an investor here and used to have no way to see that.
    <EAAdminConsole
      staffName={currentInvestor?.name || 'Staff'}
      staffEmail={currentInvestor?.email}
      roleLabel={t('tpOwnerWorkspace')}
      onViewOwn={() => setAdminViewingOwn(true)}
      view={ADMIN_TAB_VIEW[activeTab] || 'assetdesk'}
      onUnreadChange={setUnreadMessages}
    />
  ) : (
    <MainDashboard
      currentInvestor={currentInvestor}
      units={units}
      apiUrl={API_URL}
      setActiveTab={setActiveTab}
      totalPortfolioValue={totalPortfolioValue}
      onOpenChat={() => setIsChatWidgetOpen(true)}
      unreadMessages={unreadMessages}
      pmUnitsOverride={demoMode ? DEMO_PM_UNITS : undefined}
      dldOverride={demoMode ? DEMO_DLD_DATA : undefined}
      onAskCopilot={(message: string) => { setChatInitialMode('brain'); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
      onOpenWealth={() => setActiveTab('wealth')}
    />
  )
)}
        {/* Showcase passes the sample DLD payload straight in. Without it the
            valuation report renders "insufficient data": its own fetch is
            blocked in demo mode, so it would have no comparables to work
            from and the headline screen of the demo would look broken. */}
        {/* Report · Management · Valuation · Plans, behind the Portfolio tab.
            Each id stays its own route so deep links and the walkthrough land
            on the right sub-tab rather than always on the first one. */}
        {['portfolio', 'pm-dashboard', 'pm-maintenance', 'corporate', 'valuation', 'payments', 'brain', 'property-management'].includes(activeTab) && (
          <PortfolioTabs
            screen={activeTab === 'property-management' ? 'pm-dashboard' : activeTab}
            setScreen={setActiveTab}
            currentInvestor={currentInvestor}
            units={units}
            demoMode={demoMode}
            onAsk={(message: string) => { setChatInitialMode(undefined); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
            plansNode={<PaymentsView />}
            brainNode={
              <InvestMenterBrain
                currentInvestor={currentInvestor}
                units={units}
                apiUrl={demoMode ? undefined : API_URL}
                onAsk={(message: string) => { setChatInitialMode('brain'); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
                dldOverride={demoMode ? DEMO_DLD_DATA : undefined}
                historyOverride={demoMode ? DEMO_DEAL_HISTORY : undefined}
                pmUnitsOverride={demoMode ? DEMO_PM_UNITS : undefined}
              />
            }
            corporateNode={<ClientServicesSection apiUrl={demoMode ? undefined : API_URL} language={language} variant="tab" />}
            renderPortfolio={(initialTab) => (
              <PortfolioView
                properties={units}
                investorId={currentInvestor?.id}
                apiUrl={demoMode ? undefined : API_URL}
                investorName={currentInvestor?.name}
                initialTab={initialTab}
                /* One tab each: the Portfolio bar above already names them, and
                   PortfolioView hides its own row when only one is shown. The
                   Calculator lives in More with the other calculators; the
                   product tour still mounts this with all three. */
                tabsShown={[initialTab]}
                dldDataOverride={demoMode ? DEMO_DLD_DATA : undefined}
                leasesOverride={demoMode ? DEMO_LEASES : undefined}
                plansOverride={demoMode ? DEMO_PLANS : undefined}
                benchmarkOverride={demoMode ? DEMO_BENCHMARKS : undefined}
                timelineOverride={demoMode ? DEMO_TIMELINES : undefined}
                listingsOverride={demoMode ? DEMO_SELL_LISTINGS : undefined}
              />
            )}
          />
        )}
        {activeTab === 'analysis' && <AnalysisView />}
        {/*
          The Properties tab.

          This screen — not the Property Management list — is what an owner
          means by "my properties". Its cards carry the photo, the unit specs,
          the current value with its EUR equivalent, construction progress, and
          the status-specific variants: a For Sale card with days/views/
          inquiries, and a Sold card that derives the ROI line by line. The PM
          list shows tenancy and occupancy, which is a narrower question.

          Its own Properties / Cashflow / Personal / Company bar is hidden:
          Cashflow is a tool in More, Personal documents moved to the profile,
          and Company documents has its own entry. The four status buckets
          (Active / Rented / Selling / Sold) stay, because those ARE this
          screen.
        */}
        {(activeTab === 'properties' || activeTab === 'company-docs') && <PropertyView
          hideMainTabs
          initialMainTab={activeTab === 'company-docs' ? 'company' : 'properties'}
          /* The portfolio in one line: what it is worth, and what it pays.
             The report and the valuations are not repeated here — Portfolio and
             Valuations are their own entries in the navigation. */
          headerActions={activeTab === 'properties' ? (
            <PortfolioHero
              units={units}
              totalValue={totalPortfolioValue}
              /* The same annual figure the dashboard and the analysis screen
                 show: every rented unit's monthly rent, over twelve months. */
              annualRental={(totalMonthlyRental || 0) * 12}
              rentUnits={otpUnits}
              dld={heroDld}
              investorId={currentInvestor?.id}
            />
          ) : undefined}
          /* Opening a property shows its management data — lifecycle, tenancy,
             inspection, maintenance, inventory and its documents — rather than
             this file's own summary/income/documents view. */
          renderUnitDetail={(unitId, onBack) => (
            <UnitDetail
              unitId={unitId}
              onBack={onBack}
              /* So the screen has the property even when the PM call cannot
                 be reached — see UnitDetail.fallbackUnit. */
              fallbackUnit={units.find((u: any) => u.id === unitId)}
              fallbackDocuments={documents.filter((d: any) => d.unitId === unitId)}
              demoMode={demoMode}
              ownerName={currentInvestor?.name}
              investor={currentInvestor}
              onAsk={(message: string) => { setChatInitialMode(undefined); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
              onSmartUpload={handleSmartUpload}
              smartUploading={smartUploading}
            />
          )}
          units={units}
          documents={documents}
          currentInvestor={currentInvestor}
          apiUrl={API_URL}
          handleFileUpload={handleFileUpload}
          downloadDocument={downloadDocument}
          documentCategory={documentCategory}
          setDocumentCategory={setDocumentCategory}
          selectedUnitForDoc={selectedUnitForDoc}
          setSelectedUnitForDoc={setSelectedUnitForDoc}
          documentType={documentType}
          setDocumentType={setDocumentType}
          uploadingDocument={uploadingDocument}
          expandedFolders={expandedFolders}
          toggleFolder={toggleFolder}
          isLoadingData={isLoadingData}
          paginationInfo={paginationInfo}
          currentPage={currentPage}
          documentSummary={documentSummary}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedDocType={selectedDocType}
          setSelectedDocType={setSelectedDocType}
          isSearching={isSearching}
          searchResults={searchResults}
          loadMoreDocuments={loadMoreDocuments}
          searchDocuments={searchDocuments}
          clearSearch={clearSearch}
          onUnitCreated={handleUnitCreated}
          onUnitUpdated={handleUnitUpdated}
          reloadDataFromFirebase={reloadDataFromFirebase}
          handleSmartUpload={handleSmartUpload}
          smartUploading={smartUploading}
          smartUploadProgress={smartUploadProgress}
        />}
        {activeTab === 'support' && <SupportView />}
        {/* The written line to the management team. Not the AI assistant
            (ChatWidget) and not a support ticket — the same people who send
            the inspection reports, answering in the app. */}
        {activeTab === 'messages' && (
          <CustomerMessages
            customerName={currentInvestor?.name}
            demoMode={demoMode}
            onBack={() => setActiveTab('menu')}
            onUnreadChange={setUnreadMessages}
          />
        )}
        {activeTab === 'menu' && <MenuView />}
        {activeTab === 'marketplace' && <MarketplaceView />}
        {activeTab === 'about' && <AboutUsView />}
        {activeTab === 'wealth' && (
          <WealthOverview
            investorId={currentInvestor?.id}
            units={units}
            apiUrl={API_URL}
            demoMode={demoMode}
            pmUnitsOverride={demoMode ? DEMO_PM_UNITS : undefined}
            onAsk={(message: string) => { setChatInitialMode('brain'); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
          />
        )}
        {activeTab === 'trust-center' && (
          <TrustCenter
            investorId={currentInvestor?.id}
            apiUrl={API_URL}
            demoMode={demoMode}
            onAsk={(message: string) => { setChatInitialMode('brain'); setChatInitialMessage(message); setIsChatWidgetOpen(true); }}
          />
        )}
        {activeTab === 'roi-calculator' && (
  <ROICalculatorView 
    currentInvestor={currentInvestor}
    units={units}
  />
)}
        {activeTab === 'chatbot' && <ChatbotView 
          chatMessages={chatMessages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          isChatLoading={isChatLoading}
          sendChatMessage={sendChatMessage}
          handleChatSubmit={handleChatSubmit}
        />}
        {activeTab === 'profile' && <ProfileView />}
        {/* {activeTab === 'events' && <EventsView />} */}
        {/* {activeTab === 'webinar' && <WebinarView />} */}
        {activeTab === 'news' && <NewsView />}
        {activeTab === 'newsfeed' && currentInvestor && (
  <NewsfeedView
    investorId={currentInvestor.id}
    investorName={currentInvestor.name || 'Investor'}
    isDE={language === 'de'}
    apiUrl={API_URL.replace('/api', '')}
    demoMode={demoMode}
  />
)}
        {/* Partner (cooperation / referral) now lives in its own portal, reached
            from the welcome screen — see portals/partner/PartnerPortal. */}
        {activeTab === 'IM Properties' && (
          <DubaiSellListingsView
            language={language}
            demoMode={demoMode}
            investorName={currentInvestor?.name}
            investorEmail={currentInvestor?.email}
            investorPhone={currentInvestor?.phone || ''}
          />
        )}
        {activeTab === 'todos' && (
          <TodosView apiUrl={API_URL} investorId={currentInvestor?.id} language={language} demoMode={demoMode || isFilmMode()} />
        )}
        {activeTab === 'corporate-services' && (
  <CorporateServicesView 
    currentInvestor={currentInvestor}
    apiUrl={API_URL}
  />
)}
        {activeTab === 'cashflow' && currentInvestor?.id && (
  <CashflowCalculator
    investorId={currentInvestor.id}
    units={units as any[]}
    currentInvestor={currentInvestor}
    backendUrl={API_URL}
  />
)}
        {activeTab === 'survey' && currentInvestor && (
  <SurveyView
    currentInvestor={currentInvestor}
    apiUrl={API_URL}
  />
)}
      </main>

      {/* Custom Chat Widget - PandaDoc Style */}
      {isAuthenticated && currentInvestor && (
        <ChatWidget
          investorName={currentInvestor?.name || ''}
          investorEmail={currentInvestor?.email || ''}
          investorPhone={currentInvestor?.phone || ''}
          language={language as 'en' | 'de'}
          backendUrl={API_URL}
          isOpen={isChatWidgetOpen}
          onToggle={() => { setIsChatWidgetOpen(!isChatWidgetOpen); setChatInitialMessage(undefined); }}
          initialMessage={chatInitialMessage}
          initialMode={chatInitialMode}
          onInitialMessageConsumed={() => { setChatInitialMessage(undefined); setChatInitialMode(undefined); }}
        />
      )}

      {/* Bottom Navigation - DEWA Style */}
      {/* A manager who stepped into their own portfolio needs the door back.
          Sits above the bottom bar, clear of it, the same way the console's
          own view-as control does. */}
      {isAdmin && adminViewingOwn && (
        <button
          onClick={() => { setAdminViewingOwn(false); setActiveTab('dashboard'); }}
          style={{
            position: 'fixed', bottom: 76, left: 12, zIndex: 1001,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#1a1a2e', color: '#D6B160', border: '1px solid #D6B160',
            borderRadius: 99, padding: '9px 15px', fontSize: 12.5, fontWeight: 700,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)', cursor: 'pointer', minHeight: 0,
          }}
        >
          <Shield size={15} /> {t('eaBackToConsole')}
        </button>
      )}
      <BottomNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        unreadMessages={unreadMessages}
        adminMode={isAdmin && !adminViewingOwn}
      />
      {/* Admin Panel Overlay - Only for j.reyes@investmenter.de */}
{showAdminPanel && (
  <AdminWrapper
    isAdmin={isAdmin}
    apiUrl={API_URL}
    investors={[]}
    onClose={() => setShowAdminPanel(false)}
  />
)}
{currentInvestor && (
        <PushNotificationHandler
          investorId={currentInvestor.id}
          apiUrl={API_URL}
          onNotificationReceived={(payload) => {
          }}
        />
      )}

    </div>
  );
}

export default InvestorPortal;